import React, { useEffect, useMemo, useRef, useState } from 'react';

import { animated, useSpring } from '@react-spring/web';
import moment from 'moment-timezone';
import { motion, useInView, useMotionValue, useMotionValueEvent, useScroll, useSpring as useMotionSpring, useTransform, transform } from 'motion/react';
import move from "lodash-move";
import update from 'immutability-helper';
import { ReactTyped } from 'react-typed';
import { useDataStore } from 'files/StateManagement';
import { useDrag } from '@use-gesture/react';
import { useDropzone } from 'react-dropzone';

import Abstract from 'classes/Abstract.js';
import Account from 'classes/Account.js';
import { AddEditAccount, AddEditInstruction, AddEditTrustedContact, AddEditUser, ConfigureTriggers } from 'managers/Users.js';
import { AddEditDataVaultItem } from 'managers/DataVault.js';
import Appearance from 'styles/Appearance.js';
import { AltBadge } from 'views/Main.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper';
import AnimatedElement from 'views/AnimatedElement.js';
import { BorderBeam } from 'views/MagicUI.js';
import Button from 'views/Button.js';
import DataVaultItem from 'classes/DataVaultItem.js';
import Instruction from 'classes/Instruction.js';
import Layer, { LayerItem, StartIndex as LayerStartIndex, LayerItemSpacing } from 'structure/Layer.js';
import LottieView from 'views/Lottie.js';
import { NewPartnershipApplication } from 'managers/Partnerships.js';
import { NewSubscription, SalesOpsOrderForm, SubscriptionOptions } from 'managers/Payments.js';
import PageControl from 'views/PageControl.js';
import { PanelContentContainer } from 'structure/Panel.js';
import Request from 'files/Request.js';
import Subscription from 'classes/Subscription.js';
import TextField from 'views/TextField.js';
import TrustedContact from 'classes/TrustedContact.js';
import User from 'classes/User.js';
import Utils from 'files/Utils.js';

import ConsentAndDisclosureAcknowledgment from 'files/legal/consent-and-disclosure-acknowledgment.json';
import DataProcessingAndStoragePolicy from 'files/legal/data-processing-and-storage-policy.json';
import DigitalEstateConsentAndDeviceControl from 'files/legal/digital-estate-consent-and-device-control.json';
import EmergencyTriggerAuthorizationAgreement from 'files/legal/emergency-trigger-authorization-agreement.json';
import IndemnityAndWaiverAgreement from 'files/legal/indemnity-and-waiver-agreement.json';
import PartnerCodeOfConductAgreement from 'files/legal/partner-code-of-conduct-agreement.json';
import PrivacyPolicyContent from 'files/legal/privacy-policy.json';
import TermsOfServiceContent from 'files/legal/terms-of-service.json';

export const About = ({ utils }) => {

    const getContent = () => {

        // prepare list of section entries
        let sections = [{
            body: `Major Allen Graham Jr. serves in the U.S. Army Reserve as a Military Intelligence Officer and is the founder of LifeWipe Inc. A former company commander for HHC, 38th Regional Support Group, he has held roles across the 82nd Airborne Division, XVIII Airborne Corps, and 1st Information Operations Command. He previously worked as a psychometrician for the Defense Intelligence Agency under Dark Star Intelligence and now serves as the Army’s Deputy Division Chief for Strategic Communications at AARO. Major Graham holds a M.A. in Industrial-Organizational Psychology.`,
            image: 'images/allen-graham.jpg',
            imageStyle: 'cover',
            title: 'Allen Graham',
            subTitle: 'Co-Founder'
        },{
            body: `Kelcey Barnette is the CEO of LifeWipe and leads a team of dedicated digital security experts and developers. He is a career counterintelligence professional with over 25 years of experience leading large-scale operations within the U.S. Army, national level intelligence agencies, the Pentagon, and Amazon.com. Kelcey is a leader in protecting critical enterprise assets and conducting global operations where information protection was the key to success. He holds an MBA from the University of Michigan and a Professional Certificate in International Business from Georgetown University. Kelcey continues to serve as a U.S. Army Reserve Officer while delivering LifeWipe’s digital management tools that ensure your information is safe and private.`,
            image: 'images/kelcey-barnette.jpg',
            imageStyle: 'cover',
            title: 'Kelcey Barnette',
            subTitle: 'CEO'
        },{
            body: `Mike Carpenter is the CTO and co-founder of LifeWipe, where he leads the technical vision behind one of the most secure and human-centric digital legacy platforms on the market. With over a decade of experience building B2B and B2C software across startups and established companies, Mike has architected solutions that power everything from smart homes to emergency detection networks. His engineering background spans cross-platform mobile apps, customer management tools, digital asset systems, EV charging, rideshare tech, and high-performance integrations with external services. Whether it’s early-stage prototyping or enterprise-grade infrastructure, Mike builds with privacy, clarity, and scale in mind. At LifeWipe, he’s focused on one thing: helping people take back control of their digital lives — securely, simply, and on their own terms.`,
            image: 'images/mike-carpenter.jpg',
            imageStyle: 'cover',
            title: 'Mike Carpenter',
            subTitle: 'CTO & Co-Founder'
        },{
            body: `Chris Stevens is a cybersecurity and risk governance expert with over 18 years of experience advancing digital security across government and private sectors. Holding multiple industry credentials—including Certified Ethical Hacker (CEH), EC-Council Certified Instructor, and Learning Tree International Certified Instructor—Chris combines technical mastery with a passion for education and workforce development. A graduate of the University of Maryland Global Campus in Information Systems Management, Chris has collaborated with leading federal agencies such as DISA, DoD, DON, USAF, DHS, DOS, and DOJ. In 2018, he founded Cyberfinders, Inc., a cybersecurity firm dedicated to bridging the talent gap in the digital security field through advanced training and job placement. `,
            image: 'images/chris-stevens.jpg',
            imageStyle: 'cover',
            title: 'Chris Stevens',
            subTitle: 'Commercial Development \n& Co-Founder'
        },{
            body: `Lieutenant Colonel Quinn Jones was commissioned in 2005 through the University of Arkansas Pine Bluff ROTC program. She deployed to Iraq as a Convoy Escort Company XO with the 39th Infantry Brigade Combat Team and later to Afghanistan with 1-12 Infantry, 4th Infantry Division. Her assignments include Chief, Military Personnel Division at Fort Hood; Deputy G1, 377th Theater Sustainment Command; and Brigade S1, Army Reserve Cyber Protection Brigade. She holds master’s degrees in Human Resource Management and Criminal Justice and numerous decorations including the Bronze Star Medal (OLC) and Meritorious Service Medal (OLC). LTC Jones resides in Hanover, Maryland with her four children.`,
            image: 'images/quinn-jones.jpg',
            imageStyle: 'cover',
            title: 'Quinn Jones',
            subTitle: 'Human Resources'
        }];

        return sections.filter(section => {
            return section.visible !== false;
        }).map((section, index) => {

            let position = index % 2 === 0 ? 'left' : 'right';
            if(Utils.isMobile() === true) {
                return (
                    <AnimatedElement
                    {...utils.animations.get('panel')}
                    animateWithPage={true}
                    key={index}
                    useContentDivider={true}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        textAlign: 'left',
                        width: '100%'
                    }}>
                        <div
                        className={'row mx-0'} 
                        style={{
                            borderRadius: 10,
                            backgroundColor: Appearance.colors.panelBackground(),
                            minHeight: 500,
                            maxWidth: 1250,
                            overflow: 'hidden',
                            width: '100%'
                        }}>
                            <div 
                            className={`col-12 ${section.imageStyle === 'cover' ? 'p-0' : 'p-4'}`}
                            style={{
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.divider(),
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%'
                            }}>
                                <img
                                className={section.imageStyle === 'cover' ? '' : 'float'}
                                src={section.image}
                                style={{
                                    height: 150,
                                    objectFit: 'contain',
                                    width: 150,
                                    ...section.imageStyle === 'cover' && {
                                        height: '100%',
                                        objectFit: 'cover',
                                        width: '100%'
                                    }
                                }} />
                            </div>
                            <div 
                            className={'col-12 m-0'}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                padding: Appearance.styles.panelPadding(),
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.panelHeaderText(),
                                    fontFamily: 'Zen Dots',
                                }}>{section.title.toUpperCase()}</span>
                                {typeof(section.subTitle) === 'string' && (
                                    <pre style={{
                                        ...Appearance.textStyles.panelSubHeaderText(),
                                        color: Appearance.colors.subText(),
                                        fontFamily: 'Zen Dots',
                                        lineHeight: 1.25,
                                        marginBottom: 0
                                    }}>{section.subTitle.toUpperCase()}</pre>
                                )}
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    marginTop: 18
                                }}>{section.body}</span>
                            </div>
                            {section.components}
                        </div>
                    </AnimatedElement>
                )
            }
            return (
                <AnimatedElement
                {...utils.animations.get('panel')}
                animateWithPage={true}
                key={index}
                useContentDivider={true}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <div
                    className={'row mx-0'} 
                    style={{
                        borderRadius: 10,
                        backgroundColor: Appearance.colors.panelBackground(),
                        minHeight: 500,
                        maxWidth: 1250,
                        overflow: 'hidden',
                        width: '100%'
                    }}>
                        {position === 'left' && (
                            <div 
                            className={`col-12 col-xl-6 m-0 ${section.imageStyle === 'cover' ? 'p-0' : 'p-4 p-xl-2'}`}
                            style={{
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.divider(),
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <img
                                className={section.imageStyle === 'cover' ? '' : 'float'}
                                src={section.image}
                                style={{
                                    height: 150,
                                    objectFit: 'contain',
                                    width: 150,
                                    ...section.imageStyle === 'cover' && {
                                        height: '100%',
                                        objectFit: 'cover',
                                        width: '100%'
                                    }
                                }} />
                            </div>
                        )}
                        <div 
                        className={'col-12 col-xl-6 m-0'}
                        style={{
                            borderBottom: `1px solid ${Appearance.colors.divider()}`,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: Appearance.styles.panelPadding(),
                            width: '100%'
                        }}>
                            <span style={{
                                color: Appearance.colors.text(),
                                fontSize: Appearance.textStyles.headerFontSize(),
                                fontFamily: 'Zen Dots',
                                lineHeight: 1.25
                            }}>{section.title.toUpperCase()}</span>
                            {typeof(section.subTitle) === 'string' && (
                                <pre style={{
                                    ...Appearance.textStyles.panelSubHeaderText(),
                                    color: Appearance.colors.subText(),
                                    fontFamily: 'Zen Dots',
                                    lineHeight: 1.25,
                                    marginBottom: 0
                                }}>{section.subTitle.toUpperCase()}</pre>
                            )}
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                marginTop: 18
                            }}>{section.body}</span>
                        </div>
                        {position === 'right' && (
                            <div 
                            className={`col-12 col-xl-6 m-0 ${section.imageStyle === 'cover' ? 'p-0' : 'p-4 p-xl-2'}`}
                            style={{
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.divider(),
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <img
                                className={section.imageStyle === 'cover' ? '' : 'float'}
                                src={section.image}
                                style={{
                                    height: 150,
                                    objectFit: 'contain',
                                    width: 150,
                                    ...section.imageStyle === 'cover' && {
                                        height: '100%',
                                        objectFit: 'cover',
                                        width: '100%'
                                    }
                                }} />
                            </div>
                        )}
                        {section.components && (
                            <div className={'row m-0 p-3'}>
                                {section.components}
                            </div>
                        )}
                        
                    </div>
                </AnimatedElement>
            )
        });
    }

    const getResponsiveComponents = items => {
        if(Utils.isMobile() === true) {
            return items.map((item, index) => {
                return (
                    <div
                    className={'col-12'}
                    key={index}
                    style={{
                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: Appearance.styles.panelPadding(),
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots',
                            marginBottom: 4,
                            whiteSpace: 'normal'
                        }}>{item.title.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            whiteSpace: 'normal'
                        }}>{item.body}</span>
                    </div>
                )
            });
        }

        return items.map((item, index) => {
            return (
                <div
                className={'col-12 col-xl-6 p-3'}
                key={index}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        display: 'flex',
                        flexDirection: 'row',
                        minWidth: 0,
                        height: '100%',
                        padding: 24,
                        width: '100%',
                    }}>
                        <img 
                        src={window.theme === 'dark' ? 'images/logo-badge-dark.png' : 'images/logo-badge-light.png'}
                        style={{
                            height: 45,
                            marginRight: 18,
                            objectFit: 'contain',
                            width: 45
                        }} />
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            overflow: 'hidden'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.header(),
                                fontFamily: 'Zen Dots',
                                marginBottom: 4
                            }}>{item.title.toUpperCase()}</span>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                whiteSpace: 'normal'
                            }}>{item.body}</span>
                        </div>
                    </div>
                </div>
            )
        });
    }

    const getTeamComponents = () => {
        return getResponsiveComponents([{
            body: 'Former officers from intelligence agencies with expertise in secure data handling and elimination.',
            title: 'Intelligence Service'
        },{
            body: 'Veterans of military cyber operations with training in secure data destruction protocols.',
            title: 'Military Cybersecurity' 
        },{
            body: 'Specialists who understand how to permanently remove data beyond recovery.',
            title: 'Digital Forensics' 
        },{
            body: 'Experts in digital privacy legislation who ensure our processes are legally sound.',
            title: 'Privacy Law' 
        }]);
    }

    const getValuesComponents = () => {
        return getResponsiveComponents([{
            body: `Respecting and enforcing data privacy is at the forefront of what we do; it's in our DNA.`,
            title: 'Privacy Above All'
        },{
            body: 'We employ military-grade security protocols in everything we do, from our systems to our field operations.',
            title: 'Security Without Compromise' 
        },{
            body: 'While we use advanced technology, we believe that sensitive matters require human judgment and care.',
            title: 'Human Touch' 
        },{
            body: 'We believe everyone deserves to have their final digital wishes respected with dignity and care.',
            title: 'Dignity in Digital Death' 
        }]);
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`We’re a fully remote team spread across the U.S. — from Texas to D.C. — connected by a shared purpose: helping people take control of their digital lives. Our backgrounds span cybersecurity, teaching, government, sales, the military, and tech. We're as diverse in perspective as we are in experience — across race, gender, and geography — and we believe that’s exactly what it takes to build a future-proof privacy platform`}
                title={'Meet The Team'} />
            </AnimatedElement>
            {getContent()}
            <StartConversation utils={utils} />
        </div>
    )
}

export const Affiliates = ({ utils }) => {

    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(false);
    const { system } = useDataStore();

    const getAffiliates = () => {
        return affiliates.map((affiliate, index) => {
            return (
                <AnimatedElement
                {...utils.animations.get('panel')}
                animateWithPage={true}
                key={index}
                useContentDivider={true}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <div
                    className={'row m-0'}
                    style={{
                        ...Appearance.styles.unstyledPanel(),
                        height: '100%',
                        maxWidth: 1250,
                        width: '100%',
                        ...system.window.isMobile && {
                            border: 'none',
                            borderRadius: 0
                        }
                    }}>
                        <div className={'col-12 col-xl-5 p-0'}>
                            <img 
                            src={affiliate.cover_image_url}
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }} />
                        </div>
                        <div className={'col-12 col-xl-7 p-4 p-xl-5'}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots',
                                    marginBottom: 4
                                }}>{affiliate.name.toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    marginBottom: 24,
                                    whiteSpace: 'normal'
                                }}>{affiliate.description}</span>
                                <Button
                                color={'dark'}
                                label={'Learn More'}
                                onClick={window.open.bind(this, affiliate.url)}
                                type={'large'} />
                            </div>
                        </div>
                    </div>
                </AnimatedElement>
            )
        });
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'contact',
            onClick: utils.navigation.set.bind(this, { view: 'contact' }),
            text: 'Contact Us'
        }];
    }

    const fetchAffiliates = async () => {
        try {

            setLoading(true);
            let { affiliates } = await Request.get(utils, '/utils/', {
                type: 'public_affiliates_list'
            });

            setLoading(false);
            setAffiliates(affiliates);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving the affiliates list. ${e.message || 'An unknown error occurred'}`,
                origin: 'affiliates.fetch_affiliates',
                title: 'Oops!'
            });
        }
    }

    useEffect(() => {
        fetchAffiliates();
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            useContentDivider={true}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Below, you'll find a curated list of trusted affiliate companies that LifeWipe partners with to enhance your digital legacy experience. These include estate planning services, cybersecurity tools, digital infrastructure development, insurance providers, and legal support platforms — all vetted to align with LifeWipe's mission of security, privacy, and peace of mind. Whether you're looking for professional guidance or added protection, these partners can help extend the value and reach of your LifeWipe plan.`}
                title={'Affiliates'} />
            </AnimatedElement>
            {getAffiliates()}
            <PanelContentContainer
            body={`If you're aligned with our mission and see the value in what we're building, we'd love to connect. Click the button below to reach out — and let's explore how we can grow together.`}
            buttons={getContainerButtons()}
            title={'Ready to Join the Team of Affiliates?'} />
        </div>
    )
}

const AppIcons = () => {

    const images = [
        'images/accounts-apple.png',
        'images/accounts-google.png',
        'images/accounts-youtube.png',
        'images/accounts-amazon.png', 
        'images/accounts-google-chrome.png', 
        'images/accounts-gmail.png', 
        'images/accounts-google-drive.png', 
        'images/accounts-microsoft.png',
        'images/accounts-threads.png',
        'images/accounts-cash-app.png',
        'images/accounts-paypal.png',
        'images/accounts-x.png',
        'images/accounts-facebook.png',
        'images/accounts-instagram.png',
        'images/accounts-wells-fargo.png',
        'images/accounts-chase.png',
        'images/accounts-linkedin.png',
        'images/accounts-tiktok.png',
        'images/accounts-slack.png',
        'images/accounts-discord.png',
        'images/accounts-robinhood.png',
        'images/accounts-coinbase.png',
        'images/accounts-venmo.png',
        'images/accounts-spotify.png',
        'images/accounts-icloud.png',
        'images/accounts-dropbox.png',
        'images/accounts-acorns.png',
        'images/accounts-etrade.png',
        'images/accounts-netflix.png',
        'images/accounts-hulu.png',
        'images/accounts-whats-app.png',
        'images/accounts-telegram.png',
        'images/accounts-binance.png',
        'images/accounts-snapchat.png',
        'images/accounts-citi.png'
    ];

    const containerRef = useRef(null);
    const { system } = useDataStore();
    const size = system.window.isMobile ? system.window.size.width - 32 : 650;
    const numRows = 8;

    const icon = {
        margin: 20,
        size: system.window.isMobile ? 50 : 100
    };
 
    const [grid, setGrid] = useState([]);
    const itemsPerRow = Math.ceil(images.length / numRows);
    const rowWidth = itemsPerRow * (icon.size + icon.margin);
    
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "center center"]
    });

    const opacity = useTransform(scrollYProgress, [0.25, 0.75], [0, 1]);
    const xRaw = useTransform(scrollYProgress, [0, 1], [0, -rowWidth]);
    const yRaw = useTransform(scrollYProgress, [0, 1], [-250, 0]);
    
    const x = useMotionSpring(xRaw, { stiffness: 100, damping: 45, restDelta: 0.001 });
    const y = useMotionSpring(yRaw, { stiffness: 100, damping: 45, restDelta: 0.001 });

    const createGridItems = () => {

        let duplicatesNeeded = Math.ceil(size / rowWidth) + 2;

        let grid = [];
        for (let row = 0; row < numRows; row++) {

            let rowItems = [];
            let startIndex = row * itemsPerRow;
            
            // create enough duplicates to ensure no gaps
            for (let duplicate = 0; duplicate < duplicatesNeeded; duplicate++) {
                for (let col = 0; col < itemsPerRow; col++) {
                    let imageIndex = (startIndex + col) % images.length;
                    let actualCol = duplicate * itemsPerRow + col;
                    if (imageIndex < images.length) {
                    rowItems.push({ 
                        col: actualCol, 
                        image: images[imageIndex],
                        key: `${row}-${duplicate}-${col}`
                    });
                    }
                }
            }
            grid.push(rowItems);
        }
        setGrid(grid);
    }

    useEffect(() => {
        createGridItems();
    }, []);

    return (
        <motion.div 
        ref={containerRef}
        style={{
            background: `linear-gradient(0deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.15) 100%)`,
            border: `5px solid ${Appearance.colors.text()}`,
            borderRadius: size / 2,
            height: size,
            opacity: opacity,
            overflow: 'hidden',
            width: size,
        }}>
            <motion.div style={{
                height: size,
                position: 'relative',
                width: size,
                x: x,
                y: y
            }}>
                {grid.map((rowItems, rowIndex) =>
                    rowItems.map(({ col, image, key }) => (
                        <AppIconItem 
                        col={col} 
                        deviceHeight={size}
                        deviceWidth={size}
                        icon={icon}
                        image={image}
                        key={key}
                        planeX={x} 
                        planeY={y}
                        row={rowIndex} />
                    ))
                )}
            </motion.div>
        </motion.div>
    );
}

const AppIconItem = ({ row, col, image, planeX, planeY, deviceWidth, deviceHeight, icon }) => {

    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const scale = useMotionValue(1);

    const xOffset = col * (icon.size + icon.margin) + (row % 2) * ((icon.size + icon.margin) / 2);
    const yOffset = row * (icon.size + icon.margin);

    useIconTransform({ 
        x, 
        y, 
        scale, 
        planeX, 
        planeY, 
        xOffset, 
        yOffset,
        deviceWidth,
        deviceHeight,
        icon
    });

    return (
        <motion.div style={{
            position: "absolute",
            left: xOffset,
            top: yOffset,
            x,
            y,
            scale,
            width: icon.size,
            height: icon.size,
            borderRadius: "50%",
            contain: "strict",
            backgroundImage: image ? `url(${image})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: image ? 'transparent' : `hsla(${Math.random() * 360}, 95%, 55%, 1)`
        }} />
    );
}

export const AsyncInteractionItem = ({ description, image, imageStyle, lastItem = false, onAsyncClick, onClick, required, rightContent, style, title, utils, value }) => {
        
    const [loading, setLoading] = useState(false);

    const onInternalClick = async () => {
        try {

            // trigger regular click if a standard click function was provided
            if(typeof(onClick) === 'function') {
                onClick();
                return;
            }

            // set loading flag and submit request to run default async action
            setLoading(true);
            let response = await onAsyncClick();

            // end loading and show confirmation alert
            setLoading(false);
            if(response?.message) {
                let props = { title: 'All Done!', ...response };
                response.banner === true ? utils.banner.show(props) : utils.alert.show(props);
            }

        } catch(e) {
            setTimeout(() => {
                setLoading(false);
                if(e.message !== 'user_cancelled') {
                    utils.alert.error({
                        error: e,
                        message: e.message,
                        origin: 'async_interaction_item.on_internal_click',
                        ...e.props
                    });
                }
            }, 500);
        }
    }

    const getComponents = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 100,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(Utils.isMobile() === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    padding: 16,
                    width: '100%'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            fontFamily: 'Zen Dots',
                            marginBottom: 2,
                            whiteSpace: 'normal'
                        }}>{title.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            lineHeight: 1.5,
                            whiteSpace: 'normal'
                        }}>{getDescription()}</span>
                    </div>
                    {getImage()}
                </div>
            )
        }
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                padding: Utils.isMobile() === true ? '10px 12px 10px 12px' : '14px 16px 14px 16px',
                width: '100%'
            }}>
                {getImage()}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1
                }}>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: 2,
                        minWidth: 0
                    }}>
                        {required === true && !value && (
                            <div style={{
                                backgroundColor: Appearance.colors.red,
                                borderRadius: 2.5,
                                height: 5,
                                marginRight: 8,
                                minWidth: 5,
                                overflow: 'hidden',
                                width: 5
                            }} />
                        )}
                        <span style={{
                            ...Appearance.textStyles.title(),
                            fontFamily: 'Zen Dots',
                            whiteSpace: 'normal'
                        }}>{title.toUpperCase()}</span>
                    </div>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        lineHeight: 1.5,
                        whiteSpace: 'normal'
                    }}>{description}</span>
                </div>
                {rightContent}
            </div>
        )
    }

    const getDescription = () => {
        if(Utils.isMobile() === false) {
            return description;
        }
        return description.replace('click', 'tap').replace('Click', 'Tap');
    }

    const getImage = () => {

        // prepare desktop styles by default
        let styles = {
            borderRadius: 25,
            flex: 'none',
            height: 40,
            marginRight: 16,
            objectFit: 'cover',
            width: 40,
            ...imageStyle
        };

        // use mobile stlyes instead if environment is mobile
        if(Utils.isMobile() === true) {
            styles = {
                borderRadius: 25,
                flex: 'none',
                height: 35,
                marginLeft: 12,
                objectFit: 'cover',
                width: 35,
                ...imageStyle
            }
        }

        // return component rendered from parent if applciable
        if(typeof(image) === 'function') {
            return image(styles);
        }

        return typeof(image) === 'string' && (
            <img
            src={image}
            style={styles} />
        )
    }

    return (
        <div 
        className={`view-entry ${window.theme}`}
        onClick={onInternalClick}
        style={{
            backgroundColor: null,
            borderBottom: lastItem === false && `1.5px solid ${Appearance.colors.divider()}`,
            width: '100%',
            ...style
        }}>
            {getComponents()}
        </div>
    )
}

const CardStack = ({ items, onRenderIcon }) => {

    const autoAdvanceTimeout = useRef(null);
    const cardDuration = 10000;
    const cardRefs = useRef([]);
    const containerRef = useRef(null);
    const offset = 10;
    const progressInterval = useRef(null);
    const scaleFactor = 0.06;

    const [cards, setCards] = useState(items);
    const [containerHeight, setContainerHeight] = useState('auto');
    const [currentIndex, setCurrentIndex] = useState(0);
    const isInView = useInView(containerRef, { amount: 0.5 });
    const [progress, setProgress] = useState(0);
    const { system } = useDataStore();

    const onBackClick = () => {
        setCards(prev => move(prev, prev.length - 1, 0));
        setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
        setProgress(0);
    }

    const onDragEnd = index => {
        if(index === 0) {
            moveToEnd();
            setCurrentIndex(prev => (prev + 1) % items.length);
            setProgress(0);
        }
    }

    const onIndexChange = () => {

        // clear existing intervals/timeouts
        if(progressInterval.current) {
            clearInterval(progressInterval.current);
        }
        if(autoAdvanceTimeout.current) {
            clearTimeout(autoAdvanceTimeout.current);
        }

        // only start auto-advance if component is in view
        if(!isInView) {
            setProgress(0);
            return;
        }

        // reset progress to 0 for current card
        setProgress(0);

        // update progress every 50ms
        let startTime = Date.now();
        progressInterval.current = setInterval(() => {
            let elapsed = Date.now() - startTime;
            let newProgress = Math.min((elapsed / cardDuration) * 100, 100);
            setProgress(newProgress);
        }, 50);

        // set auto-advance after after waiting for card duration
        autoAdvanceTimeout.current = setTimeout(() => {
            moveToEnd();
            setProgress(0);
            setCurrentIndex(prev => (prev + 1) % items.length);
        }, cardDuration);
    }

    const onMeasureCardHeight = () => {

        if(cardRefs.current.length === 0) {
            return;
        }
            
        // find the tallest card
        let maxHeight = 0;
        cardRefs.current.forEach(ref => {
            if(ref && ref.offsetHeight > maxHeight) {
                maxHeight = ref.offsetHeight;
            }
        });
        if(maxHeight > 0) {
            setContainerHeight(maxHeight);
        }
    }

    const onNextClick = () => {
        moveToEnd();
        setCurrentIndex(prev => (prev + 1) % items.length);
        setProgress(0);
    }

    const moveToEnd = () => {
        setCards(prev => move(prev, 0, prev.length - 1));
    }

    const getColor = index => {
        
        // create dark mode colors for interpolation
        if(window.theme === 'dark') {
            let colors = Array.from({ length: items.length }, (_, i) => {
                let progress = i / (items.length - 1);
                let r = Math.round(65 - 30 * progress);
                let g = Math.round(65 - 30 * progress);
                let b = Math.round(65 - 30 * progress);
                return `linear-gradient(0deg, rgba(${r},${g},${b},1), rgba(${r - 15},${g - 15},${b - 15},1))`;
            });
            return colors[index];
        }
        
        // create light mode colors for interpolation
        let colors = Array.from({ length: items.length }, (_, i) => {
            let progress = i / (items.length - 1);
            let r = Math.round(252 - 40 * progress);
            let g = Math.round(252 - 41 * progress);
            let b = Math.round(252 - 35 * progress);
            return `linear-gradient(0deg, rgba(${r},${g},${b},1), rgba(${r - 15},${g - 15},${b - 15},1))`;
        });
        return colors[index];
    }

    const getIcon = item => {
        if(typeof(onRenderIcon) === 'function') {
            return onRenderIcon(item);
        }
        return (
            <img
            src={item.icon}
            style={{
                border: `2px solid white`,
                borderRadius: '50%',
                height: system.window.isMobile ? 75 : 100,
                objectFit: 'contain',
                width: system.window.isMobile ? 75 : 100
            }} />
        )
    }
    
    const getNavigationControls = () => {
        return (
            <div 
            className={'mt-4'}
            style={{
                alignItems: 'center',
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
                width: '100%'
            }}>
                <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onBackClick}
                style={{
                    alignItems: 'center',
                    background: Appearance.colors.primary(),
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    height: 50,
                    justifyContent: 'center',
                    width: 50
                }}>
                    <img 
                    src={window.theme === 'dark' ? 'images/back-arrow-grey-large.png' : 'images/back-arrow-white-large.png'}
                    style={{
                        height: 40,
                        width: 40
                    }} />
                </motion.div>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 8
                }}>
                    {items.map((_, index) => {
                        if(index === currentIndex) {
                            return (
                                <div 
                                key={index}
                                style={{
                                    backgroundColor: Appearance.colors.softBorder(),
                                    borderRadius: 4,
                                    height: 8,
                                    overflow: 'hidden',
                                    width: 48
                                }}>
                                    <motion.div
                                    animate={{
                                        width: `${progress}%`
                                    }}
                                    style={{
                                        backgroundColor: Appearance.colors.primary(),
                                        borderRadius: 4,
                                        height: '100%'
                                    }} />
                                </div>
                            )
                        }
                        return (
                            <div
                            key={index}
                            style={{
                                background: Appearance.colors.softBorder(),
                                borderRadius: 4,
                                height: 8,
                                width: 8
                            }} />
                        )
                    })}
                </div>
                <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onNextClick}
                style={{
                    alignItems: 'center',
                    backgroundColor: Appearance.colors.primary(),
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    height: 50,
                    justifyContent: 'center',
                    width: 50
                }}>
                    <img 
                    src={window.theme === 'dark' ? 'images/next-arrow-grey-large.png' : 'images/next-arrow-white-large.png'}
                    style={{
                        height: 40,
                        width: 40
                    }} />
                </motion.div>
            </div>
        )
    }

    useEffect(() => {
        onIndexChange();
    }, [currentIndex, items.length, isInView]);

    useEffect(() => {

        // measure height immediately and again after short delay for content loading
        onMeasureCardHeight();
        let timeoutId = setTimeout(onMeasureCardHeight, 100);
        return clearTimeout.bind(this, timeoutId);
        
    }, [cards, system.window.isMobile]);

    useEffect(() => {
        return () => {
            if(progressInterval.current) {
                clearInterval(progressInterval.current);
            }
            if(autoAdvanceTimeout.current) {
                clearTimeout(autoAdvanceTimeout.current);
            }
        };
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative'
        }}>
            <div 
            ref={containerRef} 
            style={{
                minHeight: containerHeight === 'auto' ? 'auto' : containerHeight,
                position: 'relative',
                width: '100%'
            }}>
                {cards.map((item, index) => {
                    const canDrag = index === 0;
                    return (
                        <motion.div
                        ref={el => cardRefs.current[index] = el}
                        animate={{
                            background: getColor(index),
                            scale: 1 - index * scaleFactor,
                            top: index * -offset,
                            zIndex: items.length - index
                        }}
                        className={'p-4 p-lg-5'}
                        drag={canDrag ? 'y' : false}
                        dragConstraints={{
                            top: 0,
                            bottom: 0
                        }}
                        onDragEnd={() => onDragEnd(index)}
                        key={item.id}
                        style={{
                            borderRadius: 15,
                            cursor: canDrag ? 'grab' : 'auto',
                            height: 'auto',
                            minHeight: containerHeight,
                            position: 'absolute',
                            transformOrigin: 'top center',
                            width: '100%'
                        }}
                        transition={{
                            duration: 0.4,
                            ease: "easeInOut"
                        }}>
                            <div style={{
                                alignItems: 'center',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                position: 'relative',
                                width: '100%'
                            }}>
                                {getIcon(item)}
                                <div 
                                className={'mt-4 text-center'}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.header(),
                                        color: Appearance.colors.text(),
                                        fontSize: system.window.isMobile ? 20 : 24,
                                        fontFamily: 'Zen Dots',
                                        whiteSpace: 'pre'
                                    }}>{item.title.toUpperCase()}</span>
                                    <span 
                                    className={'mt-3'}
                                    style={{
                                        color: Appearance.colors.text(),
                                        display: 'block',
                                        fontSize: 16,
                                        fontWeight: 400,
                                        lineHeight: 1.5,
                                        whiteSpace: 'normal'
                                    }}>{item.description}</span>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
            {getNavigationControls()}
        </div>
    );
}

export const Contact = ({ utils }) => {

    const [edits, setEdits] = useState({});
    const [loading, setLoading] = useState(false);

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const onSubmitClick = async() => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/utils/', {
                ...edits,
                type: 'new_website_contact_form'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: 'We have received your contact request and will reach out to you shortly.',
                onClick: setEdits.bind(this, {})
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your contact request. ${e.message || 'An unknown error occurred'}`,
                origin: 'contact.submit_click',
                title: 'Oops!'
            });
        }
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'details',
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number
            },{
                component: 'textview',
                key: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'How can we help?',
                value: edits.description
            }]
        }];
    }

    useEffect(() => {
        let props = utils.navigation.get()?.props || {};
        setEdits(props);
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            useContentDivider={true}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div 
                className={'text-left text-xl-center'}
                style={{
                    borderRadius: 10,
                    backgroundColor: Appearance.colors.panelBackground(),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    padding: Appearance.styles.panelPadding(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.panelHeaderText(),
                        marginBottom: 12
                    }}>{'Contact Us'.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{'We appreciate your interest in our products and services. To ensure we connect you with the right member of our sales team, please take a moment to fill out our contact form. This will enable our sales team to understand your requirements and respond promptly with tailored solutions. We appreciate your time and look forward to assisting you.'}</span>
                </div>
            </AnimatedElement>
            <AnimatedElement
            {...utils.animations.get('panel')}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div 
                className={'text-left text-xl-center'}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1000,
                    width: '100%'
                }}>
                    <AltFieldMapper
                    fields={getFields()}
                    utils={utils} />
                </div>
                <div 
                className={'text-left text-xl-center'}
                style={{
                    borderRadius: 10,
                    backgroundColor: Appearance.colors.panelBackground(),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginTop: 24,
                    maxWidth: 1000,
                    padding: Appearance.styles.panelPadding(),
                    width: '100%'
                }}>
                    <span style={{
                        color: Appearance.colors.subText(),
                        display: 'flex',
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.75,
                        marginBottom: 24,
                        textAlign: 'left',
                        width: '100%'
                    }}>{`By submitting your information to our service, you acknowledge that your data will be used to provide you with updates and promotional materials related to our products and services. We are committed to protecting your privacy and ensuring your information is handled securely. Please review our privacy policy for more details on how we collect, use, and protect your information.`}</span>
                    <Button 
                    color={'primary'} 
                    label={'Submit'}
                    loading={loading === 'submit'}
                    onClick={onSubmitClick}
                    type={'large'} />
                </div>
            </AnimatedElement>
        </div>
    )
}

export const DocumentViewer = ({ document, onActionClick }, { index, options, utils }) => {

    const layerID = 'document_viewer';
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [layerState, setLayerState] = useState(null);

    const onActionButtonClick = val => {

        // prevent moving forward if the user has not scrolled to the bottom of the agreement before accepting
        if(val === true && hasScrolledToBottom === false) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Please review the entire agreement before accepting the terms.',
            });
            return;
        }

        // close layer and notify subscribers
        setLayerState('close');
        onActionClick(val);
    }

    const getButtons = () => {
        if(typeof(onActionClick) === 'function') {
            return [{
                color: 'dark',
                id: 'decline',
                onClick: onActionButtonClick.bind(this, false),
                text: 'Decline'
            },{
                color: 'primary',
                id: 'agree',
                onClick: onActionButtonClick.bind(this, true),
                text: 'Agree'
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'contact_us',
            onClick: () => {
                setLayerState('close');
                utils.navigation.set({ view: 'contact' });
            },
            text: 'Contact Us'
        }];
    }

    const getContent = () => {

        let items = [];
        switch(document.id) {

            case 'consent_and_disclosure_acknowledgment':
            items = ConsentAndDisclosureAcknowledgment;
            break;

            case 'data_processing_and_storage_policy':
            items = DataProcessingAndStoragePolicy;
            break;

            case 'digital_estate_consent_and_device_control':
            items = DigitalEstateConsentAndDeviceControl;
            break;

            case 'emergency_trigger_authorization_agreement':
            items = EmergencyTriggerAuthorizationAgreement;
            break;

            case 'indemnity_and_waiver_agreement':
            items = IndemnityAndWaiverAgreement;
            break;

            case 'partner_code_of_conduct_agreement':
            items = PartnerCodeOfConductAgreement;
            break;

            case 'privacy_policy':
            items = PrivacyPolicyContent;
            break;

            case 'terms_of_service':
            items = TermsOfServiceContent;
            break;
        }

        return items.map((section, index) => {
            return (
                <LayerItem
                collapsed={null}
                key={index}
                lastItem={index === items.length - 1}
                title={section.title}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'   
                    }}>
                        {section.body.map((text, index) => {
                            return (
                                <span 
                                key={index}
                                style={{
                                    ...Appearance.textStyles.defaultText(),
                                    marginBottom: index !== section.body.length - 1 ? 12 : 0,
                                    whiteSpace: 'normal'
                                }}>{text}</span>
                            )
                        })}
                    </div>
                </LayerItem>
            )
        });
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            onScrollToBottom: setHasScrolledToBottom.bind(this, true),
            sizing: 'large'
        }}>
            {getContent()}
        </Layer>
    )
}

export const SalesOps = ({ utils }) => {

    const { request, user, setRequest } = useDataStore();

    const onApplyClick = () => {
        user ? onNewSalesOpsApplication() : utils.user.request.login({}, onNewSalesOpsApplication);
    }

    const onCyberFindersApplyClick = () => {
        user ? onNewCyberFindersApplication() : utils.user.request.login({}, onNewCyberFindersApplication);
    }

    const onNewCyberFindersApplication = () => {
        utils.layer.open({
            Component: NewCyberFindersApplication,
            id: 'new_cyber_finders_application'
        });
    }

    const onNewSalesOpsApplication = () => {
        utils.layer.open({
            Component: NewSalesOpsApplication,
            id: 'new_sales_ops_application'
        });
    }

    const getResponsiveItems = items => {

        let isMobile = Utils.isMobile();
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                ...isMobile === true && Appearance.styles.unstyledPanel()
            }}>
                {items.map((text, index) => {
                    return (
                        <div 
                        key={index}
                        style={{
                            width: '100%',
                            ...isMobile === true && {
                                borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                                padding: 12
                            },
                            ...isMobile === false && {
                                marginTop: index === 0 ? 0 : 12,
                            }
                        }}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                whiteSpace: 'normal'
                            }}>{isMobile ? text : `• ${text}`}</span>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getBaseSalarySystem = () => {

		let items = [
			['Active Subscribers', 'Monthly Salary', 'Notes'],
			['0-499','$0','Commission only'],
			['500-999','$1,000','Must maintain 500+ active subs'],
			['1,000-1,499','$2,000','Bonus increases in $1k steps'],
			['1,500-1,999','$3,000',''],
			['2,000-2,499','$4,000',''],
			['2,500-2,999','$5,000',''],
			['3,000-3,499','$6,000',''],
			['3,500-3,999','$7,000',''],
			['4,000-4,499','$8,000',''],
			['4,500-4,999','$9,000',''],
			['5,000','$10,000 (No Cap)','']
		];

        let rules = [
            'Evaluated monthly',
            'Salary adjusts automatically based on active subscriber count',
            'Subscribers must remain active and paying to count toward salary thresholds'
        ];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					maxWidth: 1250,
					padding: Appearance.styles.panelPadding(),
					textAlign: 'left',
					width: '100%'
				}}>
					<span style={{
						...Appearance.textStyles.panelHeaderText(),
						marginBottom: 12
					}}>{`LifeWipe Base Salary Threshold System`.toUpperCase()}</span>
					<span style={{
						...Appearance.textStyles.defaultText(),
						marginBottom: LayerItemSpacing
					}}>{`As a Sales Operative, you start by earning 10% commission on every subscriber you refer. But once you hit 500 active, paying subscribers, you graduate from commission-only to a guaranteed $1,000 monthly salary. From there, the salary increases in $1,000 steps for every additional 500 subscribers you maintain. These salaries are evaluated monthly and adjust automatically based on your live subscriber count. It's a performance-driven system built to reward consistent growth and retention.`}</span>
                    <LayerItem
                    collapsed={false}
                    title={'Rules for Salary Stability'}>
                        {getResponsiveItems(rules)}
                    </LayerItem>
                    <LayerItem
                    collapsed={false}
                    title={'Why The Increase?'}>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            whiteSpace: 'normal'
                        }}>{`At 500 subscribers, LifeWipe earns $7,500/month gross. After paying 10% in commission ($750), the platform retains $6,750 — more than enough to cover a $1,000 salary and ongoing server/marketing overhead. As subscriber counts grow, profitability scales, allowing us to reward top agents with true livable income in line with major-city cost of living standards.`}</span>
                    </LayerItem>
					<ResponsiveTable 
                    items={items} 
                    style={{
                        marginTop: 24
                    }}
                    title={'Projection'} />
				</div>
			</AnimatedElement>
		);
	}
    
    const getComparison = () => {

		let items = [
			['Category', 'LifeWipe Partner', 'Traditional Insurance Agent'],
			['Commission Structure', 'Simple, recurring, and transparent', 'Front-loaded, then drops sharply'],
			['Licensing Required', 'None', 'Yes'],
			['Base Salary', 'Milestone-based, grows with you', 'Fixed, limited growth'],
			['Customer Journey', 'Fast, digital, and modern', 'Lengthy, paperwork-heavy'],
			['Earnings Ownership', 'Yours for the long haul', 'Often lost if you leave'],
			['Scalability', 'Digital-first, viral potential', 'Slow, referral-based'],
			['Overhead', 'Minimal', 'High'],
			['Simplicity', 'Share your link, get paid', 'Complex quoting and compliance'],
			['Growth Path', 'Passive income potential', 'Tied to constant hustle']
		];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					maxWidth: 1250,
					padding: Appearance.styles.panelPadding(),
					textAlign: 'left',
					width: '100%'
				}}>
					<span style={{
						...Appearance.textStyles.panelHeaderText(),
						marginBottom: 12
					}}>{`LifeWipe vs Traditional Insurance`.toUpperCase()}</span>
					<span style={{
						...Appearance.textStyles.defaultText(),
						marginBottom: 36
					}}>{'The table below outlines the key differences between LifeWipe and traditional insurance. This side-by-side comparison shows how LifeWipe complements, rather than replaces, legacy financial tools — by covering what they were never built to handle.'}</span>
                    <ResponsiveTable 
                    items={items} 
                    title={'Difference'} />
				</div>
			</AnimatedElement>
		);
	}

    const getCallToAction = () => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    backgroundColor: Appearance.colors.panelBackground(),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    padding: Appearance.styles.panelPadding(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.panelHeaderText(),
                        marginBottom: 16
                    }}>{`Ready to Turn Privacy Into Profit?`.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        maxWidth: 1000
                    }}>{`Join the LifeWipe Sales Operatives Program and start earning real income by helping others protect what matters most—their digital legacy. Whether you're tech-savvy or just mission-driven, you'll get paid weekly, earn recurring commissions, and unlock monthly salaries as you grow. With referral tools, dashboards, and full training included, all you need is the drive to start.`}</span>
                    <div style={{
                        marginTop: Appearance.styles.panelPadding(),
                        maxWidth: Utils.isMobile() ? null : '50%',
                        width: '100%'
                    }}>
                        <Button
                        color={Appearance.colors.primary()}
                        containerStyle={{
                            width: '100%'
                        }}
                        label={'Apply Now'}
                        onClick={onApplyClick}
                        type={'large'} /> 
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'apply',
            onClick: onApplyClick,
            text: 'Apply Now'
        }];
    }

    const getCyberFindersCallToAction = () => {
        
        let items = [
            'Hit 100 active subscribers: We pay for your cyber certification course, exam, and materials',
            'Maintain 500+ subscribers for 6+ months: Qualify for $10,000 job placement incentives',
            '12+ months of performance: We sponsor your security clearance for federal jobs'
        ];

        return (
            <AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					maxWidth: 1250,
					overflow: 'hidden',
					width: '100%'
				}}>
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						padding: Appearance.styles.panelPadding(),
						width: '100%'
					}}>
						<span style={{
							...Appearance.textStyles.panelHeaderText(),
						    marginBottom: 12
						}}>{'Career Growth & Free Cyber Training'.toUpperCase()}</span>
						<span style={{
							...Appearance.textStyles.defaultText(),
							marginBottom: Appearance.styles.panelPadding(),
						}}>{`Ambitious agents get more: Grow your subscriber base and unlock free cybersecurity certification through our exclusive partnership with CyberFinders Inc. Courses include Security+ (CompTIA), AWS Cloud Practitioner, Certified Ethical Hacker (CEH), Network Defense Bootcamps, and more.`}</span>
                        {items.map((item, index) => {
                            return (
                                <div
                                key={index}
                                style={{
                                    marginBottom: index !== items.length - 1 ? 12 : 0,
                                    width: '100%'
                                }}>
                                    <div style={{
                                        alignItems: 'center',
                                        borderColor: Appearance.colors.divider(),
                                        borderRadius: 10,
                                        borderStyle: 'solid',
                                        borderWidth: 1,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        minWidth: 0,
                                        height: '100%',
                                        padding: 24,
                                        width: '100%',
                                    }}>
                                        <img 
                                        className={'d-none d-xl-flex'}
                                        src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                        style={{
                                            height: 45,
                                            marginRight: 18,
                                            objectFit: 'contain',
                                            width: 45
                                        }} />
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            flexGrow: 1,
                                            whiteSpace: 'normal'
                                        }}>{item}</span>
                                    </div>
                                </div>
                            )
                        })}
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            marginTop: Appearance.styles.panelPadding(),
                            width: '100%'
                        }}>
                            <Button
                            color={Appearance.colors.primary()}
                            containerStyle={{
                                maxWidth: Utils.isMobile() ? null : '50%',
                                width: '100%'
                            }}
                            label={'Apply Now'}
                            onClick={onCyberFindersApplyClick}
                            type={'large'} /> 
                        </div>
					</div>
				</div>
			</AnimatedElement>
        )
    }

    const getEarningsBreakdown = () => {

		let items = [
			'600 subscriptions in Month 1',
            'Variable growth after with 20% annual fall-off rate assumed for retention modeling',
            'Estimated 900-1,200 active subscriptions by end of year'
		];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					maxWidth: 1250,
					overflow: 'hidden',
					width: '100%'
				}}>
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						padding: Appearance.styles.panelPadding(),
						width: '100%'
					}}>
						<span style={{
							...Appearance.textStyles.panelHeaderText(),
						    marginBottom: 12
						}}>{'Earnings Breakdown Over Time'.toUpperCase()}</span>
						<span style={{
							...Appearance.textStyles.defaultText(),
							marginBottom: LayerItemSpacing,
						}}>{`Insurance agents typically earn a 50% commission on the first year of a policy. After that, their renewal commissions usually drop to around 3%-8% per year, depending on the product. Most clients do renew their policies — around 84% stay on — but the agent's income from those policies diminishes significantly unless they continue selling new ones.`}</span>
						<LayerItem
                        collapsed={false}
                        title={'First Year Estimates'}>
                            {getResponsiveItems(items)}
                        </LayerItem>
                        <LayerItem
                        collapsed={false}
                        lastItem={true}
                        title={'Key Renewal Insight'}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                whiteSpace: 'normal'
                            }}>{`Based on our internal modeling, partners retain ~80% of their subscriber base annually. That means if you started Year 2 with 1,000 subs, you'd retain 800 of those plus any new signups. This builds long-term income predictability.`}</span>
                        </LayerItem>
					</div>
				</div>
			</AnimatedElement>
		)
	}

    const getGrowthModel = () => {

		let items = [
			['Year', 'Avg Active Subs', 'Annual Commission ($)', 'Annual Salary ($)', 'Total Annual Income ($)'],
            ['1', '648', '11,664.00', '12,000', '23,664.00'],
            ['2', '1,501', '27,018.00', '24,000', '51,018.00'],
            ['3', '3,479', '62,622.00', '60,000', '122,622.00'],
            ['4', '8,064', '145,152.00', '120,000', '265,152.00'],
            ['5', '18,682', '336,276.00', '120,000', '456,276.00'],
            ['6', '43,320', '778,320.00', '120,000', '898,320.00'],
            ['7', '100,429', '1,808,892.00', '120,000', '1,928,892.00'],
            ['8', '232,840', '4,190,940.00', '120,000', '4,310,940.00'],
            ['9', '540,013', '9,720,234.00', '120,000', '9,840,234.00'],
            ['10', '1,251,479', '22,526,622.00', '120,000', '22,646,622.00']
		];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					maxWidth: 1250,
					padding: Appearance.styles.panelPadding(),
					textAlign: 'left',
					width: '100%'
				}}>
					<span style={{
						...Appearance.textStyles.panelHeaderText(),
						marginBottom: 12
					}}>{`Full-Time Agent Growth Example`.toUpperCase()}</span>
					<span style={{
						...Appearance.textStyles.defaultText(),
						marginBottom: 36
					}}>{`Important Note: This is a growth model, not a guaranteed path. Numbers below assume strong early traction (e.g., 600 in Month 1), followed by realistic variability month-to-month. A fall-off rate of 20% is built in yearly to reflect real-world user retention.`}</span>
                    <ResponsiveTable 
                    items={items} 
                    title={'Projection'} />
				</div>
			</AnimatedElement>
		);
	}

    const getPaidAdvertisingModel = () => {

		let items = [
			['Year', 'Active Subs (with Falloff)', 'Annual Commission ($)', 'Annual Salary ($)', 'Total Annual Income ($)'],
            ['1', '192', '3,456.00', '0', '3,456.00'],
            ['2', '307', '5,526.00', '0', '5,526.00'],
            ['3', '392', '7,056.00', '0', '7,056.00'],
            ['4', '460', '8,280.00', '0', '8,280.00'],
            ['5', '516', '9,288.00', '12,000', '21,288.00'],
            ['6', '563', '10,134.00', '12,000', '22,134.00'],
            ['7', '603', '10,854.00', '12,000', '22,854.00'],
            ['8', '637', '11,466.00', '12,000', '23,466.00'],
            ['9', '667', '12,006.00', '12,000', '24,006.00'],
            ['10', '693', '12,474.00', '12,000', '24,474.00']
		];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					maxWidth: 1250,
					padding: Appearance.styles.panelPadding(),
					textAlign: 'left',
					width: '100%'
				}}>
					<span style={{
						...Appearance.textStyles.panelHeaderText(),
						marginBottom: 12
					}}>{`Paid Advertising Model Example`.toUpperCase()}</span>
					<span style={{
						...Appearance.textStyles.defaultText(),
						marginBottom: 36
					}}>{`For this example, $30/month in ad spend results in 16 new subscribers per month at a 9.6x ROI. Over time, even this modest effort can yield sustainable income with almost no additional lift.`}</span>
                    <ResponsiveTable 
                    items={items} 
                    title={'Projection'} />
				</div>
			</AnimatedElement>
		);
	}

    const getPaidPerformance = () => {

		let items = [
			'$30 ad spend = 7,802 views',
            '162 post engagements (2.07%)',
            'Assuming 10% convert = 16 new subs = $288/year passive income',
            '$1.88 acquisition cost per customer',
            '9.6x ROI — annually'
		];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					maxWidth: 1250,
					overflow: 'hidden',
					width: '100%'
				}}>
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						padding: Appearance.styles.panelPadding(),
						width: '100%'
					}}>
						<span style={{
							...Appearance.textStyles.panelHeaderText(),
						    marginBottom: 12
						}}>{'Paid Ad Performance (Real Results)'.toUpperCase()}</span>
						<span style={{
							...Appearance.textStyles.defaultText(),
							marginBottom: LayerItemSpacing,
						}}>{`A $30 ad spend bringing in 16 new LifeWipe subscribers translates to $2,880 in annual revenue—and with a 10% commission, that's $288 in passive income from a single ad. This creates a repeatable loop: invest small, earn recurring. Agents who reinvest can scale their subscriber base and grow income without chasing one-time sales. It's not just marketing—it's a compounding business model where each ad becomes an asset that pays out monthly.`}</span>
                        <LayerItem
                        collapsed={false}
                        lastItem={true}
                        title={'Example Performance Breakdown'}>
                            {getResponsiveItems(items)}
                        </LayerItem>
					</div>
				</div>
			</AnimatedElement>
		)
	}

    const getPartnerFlow = () => {

		let onboarding = [
			'Orientation Video',
            'Toolkit: digital biz cards, lead tracker, shareable memes/assets',
            '3-minute sales script + text templates',
            'Optional Zoom coaching'
		];

        let weekly = [
            'Monday: Post a meme and text 10 people',
            'Tuesday: Engage FB groups or attend local events',
            'Wednesday: Follow-ups and new post',
            'Thursday: Pitch a local business, estate attorney, or influencer',
            'Friday: Review commissions, check dashboard, share win in Partner Group'
        ];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					maxWidth: 1250,
					overflow: 'hidden',
					width: '100%'
				}}>
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						padding: Appearance.styles.panelPadding(),
						width: '100%'
					}}>
						<span style={{
							...Appearance.textStyles.panelHeaderText(),
						    marginBottom: 12
						}}>{'Daily & Weekly Partner Flow'.toUpperCase()}</span>
						<span style={{
							...Appearance.textStyles.defaultText(),
							marginBottom: LayerItemSpacing,
						}}>{`Consistency builds trust — especially when you're forging partnerships in a space as sensitive as digital legacy management. That's why we've developed a structured daily and weekly flow that outlines the exact rhythms we use to keep momentum strong.`}</span>
						<LayerItem
                        collapsed={false}
                        title={'Onboarding (Day 1-3)'}>
                            {getResponsiveItems(onboarding)}
                        </LayerItem>
                        <LayerItem
                        collapsed={false}
                        lastItem={true}
                        title={'Weekly Structure Example'}>
                            {getResponsiveItems(weekly)}
                        </LayerItem>
					</div>
				</div>
			</AnimatedElement>
		)
	}
    
    const getWhyItWorks = () => {

		let stability = [
			'In areas like Washington, DC, a comfortable middle-class lifestyle is recommended $90,000-$110,000/year after taxes (2025).',
            'To reach that level, your gross income must be in the range of $130,000-$160,000/year, depending on your tax bracket.',
            'In this example, if you want to vacation, invest, and support a family — you would aim for a gross income of $180,000+.',
		];
        
        let details = [
            'So you can see exactly what it takes to hit the targets',
            'So your daily effort translates into real, life-changing earnings',
            `And so you're not capped unless you're truly thriving`
        ];

		return (
			<AnimatedElement
			{...utils.animations.get('panel')}
			animateWithPage={true}
            useContentDivider={true}
			style={{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				width: '100%'
			}}>
				<div style={{
					borderRadius: 10,
					backgroundColor: Appearance.colors.panelBackground(),
					maxWidth: 1250,
					overflow: 'hidden',
					width: '100%'
				}}>
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						padding: Appearance.styles.panelPadding(),
						width: '100%'
					}}>
						<span style={{
							...Appearance.textStyles.panelHeaderText(),
						    marginBottom: 12
						}}>{'Why It Works'.toUpperCase()}</span>
						<span style={{
							...Appearance.textStyles.defaultText(),
							marginBottom: LayerItemSpacing,
						}}>{`LifeWipe is digital insurance for your data — fast, scalable, and subscription-based. You don't need a license. You don't need a sales background. You just need a phone and a link. This isn't just about making side money. It's about building a real pathway to financial stability in a world where the cost of living is rising fast.`}</span>
						<LayerItem
                        collapsed={false}
                        title={'Financial Stability Example'}>
                            {getResponsiveItems(stability)}
                        </LayerItem>
                        <LayerItem
                        collapsed={false}
                        lastItem={true}
                        title={'Why Our Approach Works'}>
                            {getResponsiveItems(details)}
                        </LayerItem>
					</div>
				</div>
			</AnimatedElement>
		)
	}

    const runRoutingLogic = () => {
        if(request.route === 'sales_ops' && request.action) {
            switch(request.action) {
                case 'cyber_finders_application':
                onCyberFindersApplyClick();
                break;

                case 'sales_ops_application':
                onApplyClick();
                break;
            }
            setRequest(({ ...request, action: null }));
        }
    }

    useEffect(() => {
        runRoutingLogic();
    }, [request]);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            useContentDivider={true}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Help others protect what matters—and get paid doing it. As a LifeWipe Sales Operative, you'll introduce people to emergency digital protection tools, guide them through setup, and earn commissions on every subscription you refer. The more lives you secure, the more you earn—and the more access you unlock. With training, performance tiers, and paths to Field Ops or leadership roles, this isn't just side income. It's purpose-driven work with real growth potential. Explore how to start, scale, and make a difference.`}
                buttons={getContainerButtons()}
                title={'Sales Operative Program'} />
            </AnimatedElement>

            {getComparison()}
            {getPartnerFlow()}
            {getPaidPerformance()}
            {getEarningsBreakdown()}
            {getBaseSalarySystem()}
            {getGrowthModel()}
            {getPaidAdvertisingModel()}
            {getWhyItWorks()}
            {getCallToAction()}
            {getCyberFindersCallToAction()}
        </div>
    )
}

export const FAQ = ({ utils }) => {

    const { system } = useDataStore();
    
    const getContent = () => {

        let items = [{
            body: `LifeWipe is a comprehensive digital legacy management service that ensures your digital footprint is handled according to your wishes when you're no longer able to do so yourself. We provide automated and in-person data wiping, account management, and legacy message delivery.`,
            title: `What exactly is LifeWipe?`
        },{
            body: `We use a multi-step verification process to confirm your status. This typically includes periodic check-ins, trusted contact verification, and integration with legal documents like your will. We never take action without proper verification to prevent false triggers.`,
            title: `How does LifeWipe know when I've passed away?`
        },{
            body: `Our field agents are vetted security professionals with backgrounds in intelligence services, military cybersecurity, or digital forensics. All agents undergo extensive background checks, training, and sign strict confidentiality agreements.`,
            title: 'Who performs the in-person device wiping?'
        },{
            body: `LifeWipe operates under a strict zero-knowledge and end-to-end encrypted architecture. That means we do not access or view any of your data or passwords. We only interact with the accounts and information you specifically authorize. Our system is designed to perform only the actions you've pre-approved—such as triggering a data wipe or releasing a message—under the exact conditions you set. We never access, store, or act on anything outside of those boundaries.`,
            title: 'Is my data safe with LifeWipe?'
        },{
            body: `Our tiers differ primarily in the number of devices and accounts covered, frequency of check-ins, types of legacy messages allowed, and access to in-person services. All tiers include in-person device wiping for the first 6 months, but only Elite tier maintains this service permanently.`,
            title: 'What is the difference between the service tiers?'
        },{
            body: `Emergency deletion allows you to trigger an immediate wipe protocol in urgent situations. Depending on your plan, you can activate this through a secure app, SMS verification, or by contacting our 24/7 emergency line. We'll verify your identity using pre-established methods before proceeding.`,
            title: 'How does the emergency deletion feature work?'
        },{
            body: `We offer two payment options: Monthly subscription with no commitment (cancel anytime) and Annual plan billed monthly with a 12-month commitment (save 10%).`,
            title: 'What payment options do you offer?'
        },{
            body: `Yes. Our service allows you to customize handling instructions for each device and account. For example, you might want some social media accounts memorialized, others deleted, and certain devices wiped completely.`,
            title: 'Can I specify different actions for different accounts?'
        },{
            body: `Currently, LifeWipe is only offered within the United States. We're actively evaluating international expansion based on privacy laws and infrastructure needs. Stay tuned for future updates if you're outside the U.S.`,
            title: 'Is LifeWipe available internationally?'
        },{
            body: `You can cancel your subscription at any time through your account dashboard or by contacting our customer support team. Upon cancellation, we delete all your instructions and information from our systems.`,
            title: 'How do I cancel my subscription?'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Have questions about how LifeWipe works? We've compiled answers to some of the most frequently asked questions to help you understand our service better.`}
                childrenStyle={{
                    ...system.window.isMobile && {
                        padding: 0
                    }
                }}
                title={'Frequently Asked Questions'}>
                    <div className={'row m-0'}>
                        {items.map((item, index) => {
                            if(Utils.isMobile() === true) {
                                return (
                                    <div
                                    className={'col-12 m-0 p-0'}
                                    key={index}>
                                        <div style={{
                                            borderTop: `1px solid ${Appearance.colors.divider()}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            padding: Appearance.styles.panelPadding(),
                                            width: '100%'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.header(),
                                                fontFamily: 'Zen Dots',
                                                marginBottom: 4,
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                whiteSpace: 'normal'
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                )
                            }
                            return (
                                <div
                                className={`col-12 px-4 pb-4 pt-0`}
                                key={index}>
                                    <div style={{
                                        ...Appearance.styles.unstyledPanel(),
                                        display: 'flex',
                                        flexDirection: 'row',
                                        minWidth: 0,
                                        height: '100%',
                                        padding: 24,
                                        width: '100%'
                                    }}>
                                        <img 
                                        src={window.theme === 'dark' ? 'images/faq-badge-dark.png' : 'images/faq-badge-light.png'}
                                        style={{
                                            height: 45,
                                            marginRight: 18,
                                            objectFit: 'contain',
                                            width: 45
                                        }} />
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flexGrow: 1,
                                            overflow: 'hidden'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.header(),
                                                fontFamily: 'Zen Dots',
                                                marginBottom: 4,
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                whiteSpace: 'normal'
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        )
    }

    const getSupportComponent = () => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: system.window.isMobile ? 0 : 10,
                    backgroundColor: Appearance.colors.panelBackground(),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginBottom: Appearance.styles.panelSpacing(),
                    maxWidth: 1250,
                    padding: Appearance.styles.panelPadding(),
                    width: '100%',
                }}>
                    <span style={{
                        ...Appearance.textStyles.panelHeaderText(),
                        marginBottom: 16
                    }}>{`Still Have Questions?`.toUpperCase()}</span>
                    <span 
                    className={'mb-4'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        maxWidth: 1000
                    }}>{`Our dedicated team is here to guide you through every step of understanding how LifeWipe can safeguard your digital legacy, ensuring that your personal data, memories, and online presence are handled according to your wishes—with clarity, care, and peace of mind.`}</span>
                    <div 
                    className={'row mx-0 mt-4'}
                    style={{
                        maxWidth: Utils.isMobile() ? null : '50%',
                        width: '100%'
                    }}>
                        <Button
                        color={Appearance.colors.primary()}
                        containerStyle={{
                            width: '100%'
                        }}
                        label={'Contact Support'}
                        onClick={utils.navigation.set.bind(this, { view: 'contact' })}
                        type={'large'} /> 
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            {getContent()}
            {getSupportComponent()}
        </div>
    )
}

export const getLegalDocuments = () => {
    return [{
        description: 'This document serves as a formal confirmation that you, the user, have been fully informed about the structure, purpose, and inherent risks of the LifeWipe Service. By signing this acknowledgment, you affirm that you understand how the system works—including emergency triggers, remote device control, posthumous data actions, and the responsibilities you hold as a user. It outlines your consent to the service terms, acceptance of possible outcomes, and the legal agreements that govern your use of the platform. This ensures both clarity and accountability before activation. If you disagree with any part or require clarification, you are urged to contact support before proceeding.',
        short_description: 'Confirms that the user fully understands the LifeWipe service, risks, and responsibilities before activation.',
        id: 'consent_and_disclosure_acknowledgment',
        title: 'Consent & Disclosure Acknowledgment'
    },{
        description: `This document explains how LifeWipe collects, encrypts, stores, accesses, and deletes user data in the course of operating its emergency wipe and device control services. It outlines technical safeguards like encryption and access control, describes where and how data is stored (primarily in secure U.S.-based cloud servers), and details our backup, deletion, and disaster recovery protocols. It also clarifies the Company's zero-access stance to personal content and emphasizes your responsibility to maintain backups and secure your own devices. By using the service, you acknowledge and agree to these practices.`,
        short_description: 'Describes how LifeWipe securely processes, stores, and protects user data throughout the service lifecycle.',
        id: 'data_processing_and_storage_policy',
        title: 'Data Processing & Storage Policy'
    },{
        description: 'This agreement gives LifeWipe formal, legally binding permission to erase your registered devices and close your account upon verified death. It reflects your explicit wish to have no data disclosed to heirs, family, or executors—even if they request access. Instead, your devices and account will be permanently wiped to protect your privacy. The document allows you to name a Digital Executor, outlines the process for verification, and confirms that your instructions override any conflicting will or estate documents. You also release the company from liability for acting on your directive and agree that your estate will indemnify LifeWipe if others object to the data deletion.',
        short_description: 'Authorizes LifeWipe to wipe your devices and account upon verified death according to your explicit instructions.',
        id: 'digital_estate_consent_and_device_control',
        title: 'Digital Estate Consent & Device Control Agreement'
    },{
        description: 'This agreement allows LifeWipe to act on emergency instructions from your designated Trusted Contacts in serious situations—such as your death, incapacity, coercion, or account compromise. You authorize specific actions like remotely wiping your devices, locking them, or suspending your account if a Trusted Contact verifies an emergency event. The agreement outlines how the Company will authenticate requests and exercise discretion to prevent misuse. It does not permit data sharing—only protective actions. You release LifeWipe from liability for acting in good faith and agree to indemnify them if third parties raise claims. This agreement remains active until revoked or your account is closed.',
        short_description: 'Authorizes Trusted Contacts to trigger protective emergency actions on your behalf under defined conditions.',
        id: 'emergency_trigger_authorization_agreement',
        title: 'Emergency Trigger Authorization Agreement'
    },{
        description: 'This agreement confirms that by using LifeWipe, you accept all known risks associated with emergency data wipe services—such as irreversible data loss, failed triggers, accidental activation, or technical limitations. You waive any legal claims against the Company for harm caused by these scenarios, except in cases of gross negligence or willful misconduct. You also agree to indemnify LifeWipe if your use of the service—or misuse by a Trusted Contact—results in legal claims or damages from others. This includes covering legal costs and cooperating in defense. The agreement survives account closure and reinforces that you are using the service voluntarily, with full awareness of the consequences.',
        short_description: 'Acknowledges the risks of emergency wipe services and waives claims against LifeWipe for outcomes within disclosed limitations.',
        id: 'indemnity_and_waiver_agreement',
        title: 'Indemnity & Waiver Agreement'
    },{
        description: 'This Code of Conduct outlines the expectations for partners who promote and sell LifeWipe services. It covers key areas such as ethical sales practices, data handling, brand use, team training, and platform access. Partners are expected to uphold LifeWipe’s standards of integrity, security, and customer trust throughout all interactions. The agreement also defines prohibited activities, including unauthorized data use, misrepresentation, and non-compliant marketing. Adherence to these standards is essential for maintaining access to the platform and participating in the LifeWipe Partner Program.',
        short_description: 'Defines ethical, operational, and data-handling standards required for partners representing LifeWipe.',
        id: 'partner_code_of_conduct_agreement',
        title: 'Partnership Code of Conduct Agreement'
    },{
        description: 'This Privacy Policy explains how LifeWipe collects, uses, and protects your personal data—including contact info, device details, and emergency settings. Your data is encrypted, used only to operate the service, and never sold. It may be shared with Trusted Contacts or service providers during verified emergencies. You have rights to access, correct, or delete your data. LifeWipe complies with U.S. privacy laws and GDPR, and keeps your data only as long as needed for service or legal reasons.',
        short_description: 'Explains how LifeWipe collects, uses, protects, and retains your personal information.',
        id: 'privacy_policy',
        title: 'Privacy Policy'
    },{
        description: `This document outlines the rules and responsibilities for using LifeWipe's emergency access and remote wipe service. By signing up, you agree to use the service only in legitimate emergencies and to follow all security and legal requirements. The terms cover how the service works, user responsibilities, emergency limitations, acceptable use, liability disclaimers, and your consent for device control actions. You must configure your emergency settings accurately and understand that data wipes are irreversible. Misuse may lead to account suspension. These Terms are legally binding and incorporate several related agreements, including privacy and consent policies.`,
        short_description: 'Defines the rules, responsibilities, and conditions for using LifeWipe’s emergency access and remote wipe services.',
        id: 'terms_of_service',
        title: 'Terms of Service'
    }];
}

export const getRequiemActionMetadata = (key, action, metadata) => {
    switch(key) {
        case 'description':
        return getRequiemActionPromptDescription(action, metadata);

        case 'icon':
        return getRequiemActionPromptIcon(action, metadata);

        case 'title':
        return getRequiemActionPromptTitle(action, metadata);
    }
}

export const getRequiemActionPromptDescription = (action, metadata = {}) => {
    switch (action) {
        case 'attach_additional_trusted_contact_to_emergency_trigger':
        return 'Adding another trusted contact ensures redundancy. If your primary contact is unavailable, this backup can authorize emergency actions.';

        case 'attach_additional_trusted_contact_to_life_checks':
        return 'Multiple contacts increase the reliability of your LifeCheck responses. It helps ensure someone notices if something’s wrong.';

        case 'attach_existing_trigger_instruction':
        return 'Use a trigger you’ve already created to guide how LifeWipe should respond to future events. Saves time, ensures consistency.';

        case 'attach_existing_trigger_instruction_to_linked_account':
        return `Define how LifeWipe should interact with ${metadata.account_nickname || 'this account'} during an emergency.`;

        case 'attach_trigger_instruction_to_linked_account':
        return `Assign a clear response plan for ${metadata.account_nickname || 'a linked account'} if a trigger condition is met.`;

        case 'attach_trigger_instruction_to_emergency_trigger':
        return 'Set a specific response when your Emergency Trigger is activated — such as wiping devices or closing accounts.';

        case 'attach_trigger_instruction_to_life_checks':
        return 'Choose how LifeWipe should respond if you stop confirming LifeChecks — for example, after prolonged inactivity.';

        case 'attach_trusted_contact_to_emergency_trigger':
        return `Authorize ${metadata.full_name || 'this contact'} to trigger emergency actions if you're unreachable.`;

        case 'attach_trusted_contact_to_life_checks':
        return `${metadata.full_name || 'This contact'} will be notified or prompted if your LifeChecks go unanswered.`;

        case 'create_data_vault_file':
        return 'Store a secure file in your Data Vault — like credentials, legal docs, or final messages. Military-grade encryption included.';

        case 'create_linked_account_trigger_instruction':
        return `Define how LifeWipe should manage ${metadata.account_nickname || 'your linked account'} in case of emergency.`;

        case 'create_trusted_contact_trigger_instruction':
        return `Give ${metadata.full_name || 'your contact'} specific instructions to follow if your emergency trigger is activated.`;

        case 'create_linked_account':
        return 'Connect an external account to LifeWipe so it can be protected, wiped, or transferred according to your wishes.';

        case 'create_trusted_contact':
        return 'Choose someone you trust to act on your behalf during emergencies or posthumous events.';

        case 'create_trigger_instruction':
        return 'Define exactly what LifeWipe should do if a trigger is activated — from wiping data to transferring files.';

        case 'learn_more_linked_accounts':
        return 'Understand how linking accounts enables LifeWipe to protect your digital assets when you can’t.';

        case 'other':
        return 'Explore additional options or tell Requiem what you’d like to work on next.';

        case 'setup_emergency_trigger':
        return 'Create a “kill switch” that allows you or a trusted contact to trigger emergency actions — fast.';

        case 'setup_lifechecks':
        return 'Set up periodic check-ins. If you stop responding, LifeWipe can take action automatically.';

        case 'start_smart_flow':
        return 'Requiem will break this process down for you — step by step, no stress.';

        default:
        return 'This action will help you strengthen your digital safety plan. Let’s take a closer look.';
    }
};


export const getRequiemActionPromptIcon = action => {
    switch(action) {
        
        case 'attach_additional_trusted_contact_to_emergency_trigger':
        case 'attach_additional_trusted_contact_to_life_checks':
        case 'attach_trusted_contact_to_emergency_trigger':
        case 'attach_trusted_contact_to_life_checks':
        case 'create_trusted_contact':
        return window.theme === 'dark' ? 'images/requiem-action-trusted-contacts-dark.png' : 'images/requiem-action-trusted-contacts-light.png';
        
        case 'attach_existing_trigger_instruction':
        case 'attach_trigger_instruction_to_emergency_trigger':
        case 'attach_trigger_instruction_to_life_checks':
        case 'attach_trigger_instruction_to_linked_account':
        case 'create_linked_account_trigger_instruction':
        case 'create_trigger_instruction':
        case 'create_trusted_contact_trigger_instruction':
        return window.theme === 'dark' ? 'images/requiem-action-instructions-dark.png' : 'images/requiem-action-instructions-light.png';

        case 'attach_existing_trigger_instruction_to_linked_account':
        case 'create_linked_account':
        case 'learn_more_linked_accounts':
        return window.theme === 'dark' ? 'images/requiem-action-linked-accounts-dark.png' : 'images/requiem-action-linked-accounts-light.png';

        case 'create_data_vault_file':
        return window.theme === 'dark' ? 'images/requiem-action-data-vault-dark.png' : 'images/requiem-action-data-vault-light.png';

        case 'setup_emergency_trigger':
        return window.theme === 'dark' ? 'images/requiem-action-emergency-trigger-dark.png' : 'images/requiem-action-emergency-trigger-light.png';

        case 'setup_lifechecks':
        return window.theme === 'dark' ? 'images/requiem-action-life-checks-dark.png' : 'images/requiem-action-life-checks-light.png';

        default:
        return window.theme === 'dark' ? 'images/requiem-next-action-dark.png' : 'images/requiem-next-action-light.png';
    }
}

export const getRequiemActionPromptTitle = (action, metadata = {}) => {
    switch(action) {
        case 'attach_additional_trusted_contact_to_emergency_trigger':
        return `Add Another Trusted Contact to Emergency Trigger`;
        
        case 'attach_additional_trusted_contact_to_life_checks':
        return `Add Another Trusted Contact to LifeChecks`;

        case 'attach_existing_trigger_instruction':
        return 'Attach Existing Trigger Instruction to LifeChecks or Emergency Trigger';

        case 'attach_existing_trigger_instruction_to_linked_account':
        return `Attach Existing Trigger Instruction to ${metadata.account_nickname}`;

        case 'attach_trigger_instruction_to_linked_account':
        return `Attach ${metadata.instruction_name ? 'This ' : ''}Trigger Instruction to a Linked Account`;
        
        case 'attach_trigger_instruction_to_emergency_trigger':
        return `Attach ${metadata.instruction_name ? 'This ' : ''}Trigger Instruction to Emergency Trigger`;

        case 'attach_trigger_instruction_to_life_checks':
        return `Attach ${metadata.instruction_name ? 'This ' : ''}Trigger Instruction to LifeChecks`;

        case 'attach_trusted_contact_to_emergency_trigger':
        return `Add ${metadata.full_name || 'Trusted Contact'} to Emergency Trigger`;

        case 'attach_trusted_contact_to_life_checks':
        return `Add ${metadata.full_name || 'Trusted Contact'} to LifeChecks`;

        case 'book_consultation': 
        return 'Book Your Free Consultation';

        case 'create_data_vault_file':
        return `Upload ${metadata.category === 'data_vault_items' && metadata.config?.name ? 'Another' : 'a'} File to Data Vault`;

        case 'create_linked_account_trigger_instruction':
        return `Create Trigger Instruction for ${metadata.account_nickname || 'Linked Account'}`;
        
        case 'create_trusted_contact_trigger_instruction':
        return `Create Trigger Instruction for ${metadata.full_name || 'Trusted Contact'}`;

        case 'create_linked_account':
        return metadata.account_nickname ? 'Link Another External Account' : 'Link an External Account';

        case 'create_trusted_contact':
        return 'Create a Trusted Contact';

        case 'create_trigger_instruction':
        return metadata.instruction_name ? 'Create Another Trigger Instruction' : 'Create a Trigger Instruction';

        case 'learn_more_linked_accounts':
        return 'Learn More About Linked Accounts';

        case 'other':
        return 'Work on Something Else';

        case 'setup_emergency_trigger':
        return 'Setup your Emergency Trigger';

        case 'setup_lifechecks':
        return 'Setup your LifeChecks';

        case 'start_free_no_credit_card':
        return 'Start Free — No Credit Card Required';

        case 'start_smart_flow':
        return 'Explain This Flow To Me';

        default:
        return 'Unknown Action';
    }
}

const HomeReveal = ({ children, className, delay = 0, inView = true, style }) => {
    const transition = { duration: 0.65, delay, ease: [0.21, 0.88, 0.41, 1] };
    const initial = { opacity: 0, y: 14 };
    const target = { opacity: 1, y: 0 };
    if(inView === false) {
        return (
            <motion.div
            className={className}
            style={style}
            initial={initial}
            animate={target}
            transition={transition}>
                {children}
            </motion.div>
        );
    }
    return (
        <motion.div
        className={className}
        style={style}
        initial={initial}
        whileInView={target}
        viewport={{ once: true, amount: 0.15 }}
        transition={transition}>
            {children}
        </motion.div>
    );
};

const HomeEyebrow = ({ children, light = false }) => (
    <div style={{
        alignItems: 'center',
        display: 'inline-flex'
    }}>
        <div style={{
            backgroundColor: Appearance.colors.primary(),
            height: 2,
            marginRight: 14,
            width: 32
        }} />
        <span style={{
            color: light ? Appearance.colors.text() : Appearance.colors.primary(),
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase'
        }}>{children}</span>
    </div>
);

export const HomeSplash = ({ utils }) => {

    const { system, user } = useDataStore();
    const isMobile = system.window.isMobile;

    const onDemoVideoClick = () => {
        if(Utils.isMobile() === true) {
            window.open('https://www.youtube.com/watch?v=X58i3c4GWhI');
            return;
        }
        utils.layer.open({
            Component: VideoPlayer.bind(this, { url: 'https://www.youtube.com/embed/X58i3c4GWhI?si=smRy8ZajBfP-Ri3A' }),
            id: 'video_player'
        });
    }

    const onApplyClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users',
            }),
            Component: NewPartnershipApplication.bind(this, {}),
            id: 'new_partnership_application'
        });
    };
    
    const onFAQClick = utils.navigation.set.bind(this, { view: 'faq' });

    const trustStripItems = ['Research-backed', '90-Day Pilot', '5–20% Rev Share', 'No Procurement'];

    const sectionPadding = {
        paddingBottom: isMobile ? '16vh' : '18vh',
        paddingLeft: isMobile ? 24 : '6vw',
        paddingRight: isMobile ? 24 : '6vw',
        paddingTop: isMobile ? '16vh' : '18vh'
    };

    const renderSectionHeader = ({ description, eyebrow, subTitle, title }) => (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            marginBottom: isMobile ? 64 : 104,
            maxWidth: 1100,
            textAlign: 'center',
            width: '100%'
        }}>
            <HomeReveal style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <HomeEyebrow>{eyebrow}</HomeEyebrow>
            </HomeReveal>
            <HomeReveal delay={0.06}>
                <h2 style={{
                    color: Appearance.colors.text(),
                    fontFamily: 'Zen Dots',
                    fontSize: isMobile ? 30 : 60,
                    fontWeight: 800,
                    letterSpacing: -1,
                    lineHeight: 1.08,
                    margin: 0,
                    marginTop: 24,
                    maxWidth: 1000,
                    textTransform: 'uppercase'
                }}>{title}</h2>
            </HomeReveal>
            {subTitle && (
                <HomeReveal delay={0.12}>
                    <p style={{
                        color: Appearance.colors.text(),
                        fontFamily: 'Instrument Serif',
                        fontSize: isMobile ? 22 : 30,
                        fontStyle: 'italic',
                        fontWeight: 400,
                        lineHeight: 1.3,
                        margin: 0,
                        marginTop: 22,
                        maxWidth: 800
                    }}>{subTitle}</p>
                </HomeReveal>
            )}
            {description && (
                <HomeReveal delay={0.18}>
                    <p style={{
                        color: Appearance.colors.text(),
                        fontSize: isMobile ? 16 : 17,
                        fontWeight: 400,
                        lineHeight: 1.6,
                        margin: 0,
                        marginTop: 22,
                        maxWidth: 660,
                        opacity: 0.72
                    }}>{description}</p>
                </HomeReveal>
            )}
        </div>
    );

    const getHeroSection = () => {
        return (
            <section style={{
                alignItems: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                justifyContent: 'center',
                maxWidth: '100vw',
                position: 'relative',
                width: '100%'
            }}>
                <div style={{
                    height: '100vh',
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: '100%'
                }}>
                    <video
                    autoPlay={true}
                    controls={false}
                    playsInline={true}
                    loop={true}
                    muted={true}
                    poster={'images/home-splash-video.png'}
                    src={'images/home-splash-video.mp4'}
                    style={{
                        height: '100vh',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        width: '100vw'
                    }} />
                    <div style={{
                        background: `linear-gradient(180deg, ${Appearance.colors.background({ alpha: 0.3 })} 0%, ${Appearance.colors.background({ alpha: 0.6 })} 60%, ${Appearance.colors.background({ alpha: 1 })} 100%)`,
                        bottom: 0,
                        left: 0,
                        position: 'absolute',
                        right: 0,
                        top: 0
                    }} />
                </div>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    paddingBottom: isMobile ? 56 : 88,
                    paddingLeft: isMobile ? 24 : '6vw',
                    paddingRight: isMobile ? 24 : '6vw',
                    paddingTop: isMobile ? 96 : 120,
                    position: 'relative',
                    textAlign: 'center',
                    width: '100%',
                    zIndex: 1
                }}>
                    <HomeReveal inView={false} style={{ display: 'flex', justifyContent: 'center' }}>
                        <HomeEyebrow>{'For Life Insurance Agencies'}</HomeEyebrow>
                    </HomeReveal>
                    <HomeReveal inView={false} delay={0.08} style={{ marginTop: 24, maxWidth: 1200 }}>
                        <h1 style={{
                            color: Appearance.colors.text(),
                            fontFamily: 'Zen Dots',
                            fontSize: isMobile ? 34 : 64,
                            fontWeight: 800,
                            letterSpacing: -1.5,
                            lineHeight: 1.02,
                            margin: 0,
                            textTransform: 'uppercase'
                        }}>
                            {`digital readiness platform `}
                            <span style={{
                                color: Appearance.colors.primary(),
                                fontFamily: 'Instrument Serif',
                                fontStyle: 'italic',
                                fontWeight: 400,
                                letterSpacing: -1,
                                textTransform: 'none'
                            }}>{'built for'}</span>
                            {` life insurance agencies.`}
                        </h1>
                    </HomeReveal>
                    <HomeReveal inView={false} delay={0.16} style={{ marginTop: 28, maxWidth: 720 }}>
                        <p style={{
                            color: Appearance.colors.text(),
                            fontSize: isMobile ? 17 : 20,
                            fontWeight: 400,
                            lineHeight: 1.5,
                            margin: 0,
                            opacity: 0.85
                        }}>{`A co-branded client-protection benefit your agency can deploy in under a week — with onboarding, materials, and an activation dashboard included.`}</p>
                    </HomeReveal>
                    <HomeReveal inView={false} delay={0.24} style={{ marginTop: isMobile ? 36 : 48 }}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isMobile ? 16 : 28,
                            justifyContent: 'center'
                        }}>
                            <Button
                            color={Appearance.colors.primary()}
                            containerStyle={{ minWidth: 220 }}
                            label={'Apply To The Pilot'}
                            onClick={onApplyClick}
                            type={'large'} />
                            <span
                            onClick={onDemoVideoClick}
                            style={{
                                borderBottom: `1px solid ${Appearance.colors.text()}`,
                                color: Appearance.colors.text(),
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: 2,
                                paddingBottom: 4,
                                textTransform: 'uppercase'
                            }}>{'Watch the demo →'}</span>
                        </div>
                    </HomeReveal>
                    <HomeReveal inView={false} delay={0.32} style={{
                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                        marginTop: isMobile ? 56 : 80,
                        maxWidth: 900,
                        paddingTop: 20,
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px 32px',
                            justifyContent: 'center'
                        }}>
                            {trustStripItems.map((label, index) => (
                                <span
                                key={index}
                                style={{
                                    color: Appearance.colors.text(),
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 2.5,
                                    opacity: 0.7,
                                    textTransform: 'uppercase'
                                }}>{label}</span>
                            ))}
                        </div>
                    </HomeReveal>
                </div>
            </section>
        )
    }

    const getClientProblem = () => {

        let items = [{
            title: 'Lost Logins',
            description: `Families inherit a maze of accounts with no passwords, no recovery, and no map. This leaves them locked out of everything — from photos to finances to social media.`
        },{
            title: 'Silent Subscriptions',
            description: `Recurring charges quietly continue for months — sometimes years — after a life event. Families are left footing the bill for services their loved ones can no longer use.`
        },{
            title: 'Critical Files',
            description: `Policies, wills, and instructions live behind cloud drives no one in the family can open. Important documents and final wishes are lost in the digital void.`
        },{
            title: 'Identity Exposure',
            description: `Dormant accounts become open doors for fraud, scams, and identity theft. Families face the risk of financial loss and emotional distress from digital vulnerabilities.`
        }];

        return (
            <section style={{
                alignItems: 'center',
                borderTop: Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100vw',
                overflow: 'hidden',
                width: '100%',
                ...sectionPadding
            }}>
                {renderSectionHeader({
                    description: `Your clients have a financial plan. Almost none of them have a digital one — and when something happens, the cost falls on the family and the agency they trusted.`,
                    eyebrow: 'The Problem',
                    subTitle: (
                        <span>
                            {`Your clients are `}
                            <span style={{ color: Appearance.colors.primary() }}>{'financially'}</span>
                            {` protected. Their digital lives aren't.`}
                        </span>
                    ),
                    title: 'The Digital Gap'
                })}
                <HomeReveal style={{ maxWidth: 960, width: '100%' }}>
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Appearance.colors.layerBackground(),
                        border: `1px solid ${Appearance.colors.divider()}`,
                        borderRadius: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: isMobile ? '40px 24px' : '56px 48px',
                        textAlign: 'center',
                        width: '100%'
                    }}>
                        <span style={{
                            color: Appearance.colors.primary(),
                            fontFamily: 'Zen Dots',
                            fontSize: 64,
                            fontWeight: 800,
                            letterSpacing: -3,
                            lineHeight: 0.95
                        }}>{'$1K–$6K+'}</span>
                        <span style={{
                            color: Appearance.colors.primary(),
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 2.5,
                            marginTop: 14,
                            textTransform: 'uppercase'
                        }}>{'Per Family · Quantifiable Cost'}</span>
                        <div style={{
                            backgroundColor: Appearance.colors.divider(),
                            height: 1,
                            marginBottom: 32,
                            marginTop: 32,
                            width: 64
                        }} />
                        <p style={{
                            color: Appearance.colors.text(),
                            fontFamily: 'Instrument Serif',
                            fontSize: isMobile ? 20 : 26,
                            fontStyle: 'italic',
                            fontWeight: 400,
                            lineHeight: 1.35,
                            margin: 0,
                            maxWidth: 720
                        }}>{`"The quantifiable cost of unhandled digital affairs can range from roughly $1,000 to $6,000+ for families, before accounting for emotional burden or lost memories."`}</p>
                        <span style={{
                            color: Appearance.colors.subText(),
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 2,
                            marginTop: 22,
                            textTransform: 'uppercase'
                        }}>{'University of Michigan — Ross School of Business Analysis'}</span>
                    </div>
                </HomeReveal>
                <div style={{
                    marginTop: isMobile ? 64 : 112,
                    maxWidth: 1280,
                    width: '100%'
                }}>
                    <HomeReveal style={{ textAlign: 'center' }}>
                        <span style={{
                            color: Appearance.colors.subText(),
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 2.5,
                            marginBottom: 40,
                            textTransform: 'uppercase'
                        }}>{'Four Failure Modes Families Inherit'}</span>
                    </HomeReveal>
                    <div style={{
                        display: 'grid',
                        gap: isMobile ? 16 : 24,
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        width: '100%'
                    }}>
                        {items.map((item, index) => (
                            <HomeReveal key={index} delay={index * 0.06}>
                                <div style={{
                                    alignItems: 'flex-start',
                                    backgroundColor: Appearance.colors.layerBackground(),
                                    border: `1px solid ${Appearance.colors.divider()}`,
                                    borderRadius: 12,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    padding: isMobile ? 24 : 28,
                                    textAlign: 'left'
                                }}>
                                    <span style={{
                                        color: Appearance.colors.primary(),
                                        fontFamily: 'Zen Dots',
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: 1,
                                        marginBottom: 18
                                    }}>{`0${index + 1}`}</span>
                                    <span style={{
                                        color: Appearance.colors.text(),
                                        fontFamily: 'Zen Dots',
                                        fontSize: isMobile ? 18 : 20,
                                        fontWeight: 800,
                                        letterSpacing: -0.3,
                                        lineHeight: 1.15,
                                        marginBottom: 12,
                                        textTransform: 'uppercase'
                                    }}>{item.title}</span>
                                    <span style={{
                                        color: Appearance.colors.subText(),
                                        fontSize: 14,
                                        fontWeight: 400,
                                        lineHeight: 1.6
                                    }}>{item.description}</span>
                                </div>
                            </HomeReveal>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    const getToolkit = () => {

        let items = [{
            title: 'Linked Accounts',
            tag: 'Accounts',
            description: `Clients connect the accounts that matter — banks, email, cloud, subscriptions — so families never face a scavenger hunt.`,
            onClick: utils.navigation.set.bind(this, { view: 'services_linked_accounts' })
        },{
            title: 'Data Vault',
            tag: 'Files',
            description: `Encrypted storage for credentials, final wishes, legal documents, photos, and video messages — released on the client's terms.`,
            onClick: utils.navigation.set.bind(this, { view: 'services_data_vault' })
        },{
            title: 'LifeChecks',
            tag: 'Monitoring',
            description: `Quiet, scheduled check-ins confirm the client is okay. If they go silent, trusted contacts are notified — not the world.`,
            onClick: utils.navigation.set.bind(this, { view: 'services_life_checks' })
        },{
            title: 'Emergency Trigger',
            tag: 'Action',
            description: `A client-controlled signal that activates instructions, transfers, or wipes when something goes wrong.`,
            onClick: utils.navigation.set.bind(this, { view: 'services_emergency_trigger' })
        },{
            title: 'Instructions',
            tag: 'Direction',
            description: 'Clients set specific instructions for what should happen when a LifeCheck is missed or an Emergency Trigger is activated.',
            onClick: utils.navigation.set.bind(this, { view: 'services_instructions' })
        },{
            title: 'Requiem AI',
            tag: 'Guidance',
            description: `A guided assistant that helps clients build, refine, and finalize their digital readiness plan with empathy and structure.`,
            onClick: utils.navigation.set.bind(this, { view: 'services_requiem_ai' })
        }];

        return (
            <section style={{
                alignItems: 'center',
                borderTop: Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100vw',
                overflow: 'hidden',
                width: '100%',
                ...sectionPadding
            }}>
                {renderSectionHeader({
                    description: `Six client-controlled tools, packaged into one platform you can co-brand and deploy in days. Your clients set the rules — we make sure they're followed when it matters.`,
                    eyebrow: 'The Platform',
                    subTitle: (
                        <span>
                            {`Everything your policyholders need, in `}
                            <span style={{ color: Appearance.colors.primary() }}>{'one place'}</span>
                            {`.`}
                        </span>
                    ),
                    title: 'Inside The Platform'
                })}
                <div style={{
                    display: 'grid',
                    gap: isMobile ? 16 : 24,
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    maxWidth: 1300,
                    width: '100%'
                }}>
                    {items.map((item, index) => (
                        <HomeReveal key={index} delay={index * 0.05}>
                            <motion.div
                            onClick={item.onClick}
                            whileHover={{ borderColor: Appearance.colors.primary() }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            style={{
                                alignItems: 'flex-start',
                                backgroundColor: Appearance.colors.layerBackground(),
                                border: `1px solid ${Appearance.colors.divider()}`,
                                borderRadius: 12,
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                padding: isMobile ? 28 : 32,
                                textAlign: 'left'
                            }}>
                                <div style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: 28,
                                    width: '100%'
                                }}>
                                    <span style={{
                                        color: Appearance.colors.primary(),
                                        fontFamily: 'Zen Dots',
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: 1
                                    }}>{`0${index + 1}`}</span>
                                    <span style={{
                                        color: Appearance.colors.subText(),
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: 2,
                                        textTransform: 'uppercase'
                                    }}>{item.tag}</span>
                                </div>
                                <span style={{
                                    color: Appearance.colors.text(),
                                    display: 'block',
                                    fontFamily: 'Zen Dots',
                                    fontSize: 20,
                                    fontWeight: 800,
                                    letterSpacing: -0.3,
                                    lineHeight: 1.15,
                                    marginBottom: 14,
                                    textTransform: 'uppercase'
                                }}>{item.title}</span>
                                <span style={{
                                    color: Appearance.colors.subText(),
                                    display: 'block',
                                    fontSize: 14,
                                    fontWeight: 400,
                                    lineHeight: 1.6,
                                    marginBottom: 24
                                }}>{item.description}</span>
                                <span style={{
                                    color: Appearance.colors.primary(),
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: 2,
                                    marginTop: 'auto',
                                    textTransform: 'uppercase'
                                }}>{'Explore →'}</span>
                            </motion.div>
                        </HomeReveal>
                    ))}
                </div>
            </section>
        )
    }

    const getAgencyBenefits = () => {

        let items = [{
            value: '+1',
            label: 'Differentiator',
            description: `A protection benefit quote-comparison sites and direct carriers cannot replicate. A reason for clients to choose and stay with your agency.`
        },{
            value: '+1',
            label: 'Post-Sale Touchpoint',
            description: `A natural reason to re-engage clients — without ever leading with mortality. A way to stay connected to families in the years after the policy sale.`
        },{
            value: '0',
            label: 'Implementation Lift',
            description: `No system migration, no procurement cycle, no compliance project. A simple add-on to your existing client services, with setup, materials, and support included.`
        },{
            value: '5-20%',
            label: 'Revenue Share',
            description: `Earned on every referred subscription. Aligned to client activation. A new revenue stream that scales as your clients protect their digital lives — and gives you a return on the time you invest.`
        }];

        return (
            <section style={{
                alignItems: 'center',
                borderTop: Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100vw',
                overflow: 'hidden',
                width: '100%',
                ...sectionPadding
            }}>
                {renderSectionHeader({
                    description: `Life insurance protects the financial side of an emergency. LifeWipe protects the digital side — and gives your agency a new way to differentiate, re-engage, and serve the families who feel the digital gap most.`,
                    eyebrow: 'For Your Agency',
                    subTitle: (
                        <span>
                            {`Four `}
                            <span style={{ color: Appearance.colors.primary() }}>{'outcomes'}</span>
                            {` in ninety days.`}
                        </span>
                    ),
                    title: 'Built For Your Agency'
                })}
                <div style={{
                    display: 'grid',
                    gap: isMobile ? 16 : 24,
                    gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(2, 1fr)',
                    maxWidth: 1300,
                    width: '100%'
                }}>
                    {items.map((item, index) => (
                        <HomeReveal key={index} delay={index * 0.06}>
                            <div style={{
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.layerBackground(),
                                border: `1px solid ${Appearance.colors.divider()}`,
                                borderRadius: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                padding: isMobile ? '32px 16px' : '40px 24px',
                                textAlign: 'center'
                            }}>
                                <span style={{
                                    color: Appearance.colors.primary(),
                                    display: 'block',
                                    fontFamily: 'Zen Dots',
                                    fontSize: 48,
                                    fontWeight: 800,
                                    letterSpacing: -2,
                                    lineHeight: 1,
                                    marginBottom: 22
                                }}>{item.value}</span>
                                <span style={{
                                    color: Appearance.colors.text(),
                                    fontFamily: 'Zen Dots',
                                    fontSize: 20,
                                    fontWeight: 800,
                                    letterSpacing: 1,
                                    marginBottom: 14,
                                    textTransform: 'uppercase'
                                }}>{item.label}</span>
                                <span style={{
                                    color: Appearance.colors.subText(),
                                    fontSize: 13,
                                    fontWeight: 400,
                                    lineHeight: 1.55
                                }}>{item.description}</span>
                            </div>
                        </HomeReveal>
                    ))}
                </div>
                <HomeReveal delay={0.32} style={{ marginTop: isMobile ? 48 : 72, maxWidth: 820, textAlign: 'center' }}>
                    <p style={{
                        color: Appearance.colors.text(),
                        fontFamily: 'Instrument Serif',
                        fontSize: isMobile ? 18 : 22,
                        fontStyle: 'italic',
                        fontWeight: 400,
                        lineHeight: 1.5,
                        margin: 0,
                        opacity: 0.85
                    }}>{`Best fit: independent and small-to-mid agencies serving younger families, business owners, and wealth-transfer-focused households.`}</p>
                </HomeReveal>
            </section>
        )
    }

    const getPilotOffer = () => {

        let phases = [{
            label: 'Phase 01',
            title: 'Setup',
            days: 'Days 1–14',
            description: `Co-brand materials, brief agents, generate referral codes, finalize policyholder list.`
        },{
            label: 'Phase 02',
            title: 'Run',
            days: 'Days 15–75',
            description: `Agency sends introduction. LifeWipe handles onboarding, support, and live activation reporting.`
        },{
            label: 'Phase 03',
            title: 'Review',
            days: 'Days 76–90',
            description: `Activation funnel review, agency interviews, decision: scale, iterate, or redesign.`
        }];

        let lifewipeProvides = [
            'Co-branded policyholder email and landing page',
            'Agent talking points and a plain-English security FAQ',
            'Setup support and a live activation dashboard',
            'Midpoint and end-of-pilot review sessions'
        ];

        let agencyProvides = [
            'A warm introduction to a selected policyholder group',
            'One email send or newsletter placement',
            'Optional agent talking points during client conversations',
            'Feedback at the midpoint and end of the pilot'
        ];

        return (
            <section style={{
                alignItems: 'center',
                borderTop: Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100vw',
                overflow: 'hidden',
                width: '100%',
                ...sectionPadding
            }}>
                {renderSectionHeader({
                    description: `We are selecting a limited number of life insurance agencies for a focused 90-day pilot developed with the University of Michigan — Ross School of Business MAP team. We handle setup, support, and reporting. At the end, we share results — and decide together.`,
                    eyebrow: 'How It Works',
                    subTitle: (
                        <span>
                            {`Low lift. `}
                            <span style={{ color: Appearance.colors.primary() }}>{'Real'}</span>
                            {` data. Clear decision.`}
                        </span>
                    ),
                    title: 'The 90-Day Pilot'
                })}
                <div style={{
                    display: 'grid',
                    gap: isMobile ? 16 : 24,
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    maxWidth: 1280,
                    width: '100%'
                }}>
                    {phases.map((phase, index) => (
                        <HomeReveal
                        key={index}
                        delay={index * 0.08}>
                            <div style={{
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.layerBackground(),
                                border: `1px solid ${Appearance.colors.divider()}`,
                                borderRadius: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                padding: isMobile ? '32px 24px' : '44px 28px',
                                textAlign: 'center'
                            }}>
                                <span style={{
                                    color: Appearance.colors.primary(),
                                    fontFamily: 'Zen Dots',
                                    fontSize: 56,
                                    fontWeight: 800,
                                    letterSpacing: -3,
                                    lineHeight: 0.95,
                                    marginBottom: 24
                                }}>{`0${index + 1}`}</span>
                                <span style={{
                                    color: Appearance.colors.primary(),
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: 2,
                                    textTransform: 'uppercase'
                                }}>{phase.days}</span>
                                <span style={{
                                    color: Appearance.colors.text(),
                                    fontFamily: 'Zen Dots',
                                    fontSize: isMobile ? 22 : 26,
                                    fontWeight: 800,
                                    letterSpacing: -0.3,
                                    lineHeight: 1.1,
                                    marginTop: 10,
                                    marginBottom: 16,
                                    textTransform: 'uppercase'
                                }}>{phase.title}</span>
                                <span style={{
                                    color: Appearance.colors.subText(),
                                    fontSize: 14,
                                    fontWeight: 400,
                                    lineHeight: 1.6,
                                    maxWidth: 320
                                }}>{phase.description}</span>
                            </div>
                        </HomeReveal>
                    ))}
                </div>
                <div style={{
                    display: 'grid',
                    gap: isMobile ? 16 : 24,
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    marginTop: isMobile ? 32 : 56,
                    maxWidth: 1280,
                    width: '100%'
                }}>
                    {[
                        { label: 'LifeWipe Provides', items: lifewipeProvides },
                        { label: 'Your Agency Provides', items: agencyProvides }
                    ].map((column, index) => (
                        <HomeReveal
                        key={index}
                        delay={index * 0.08}>
                            <div style={{
                                backgroundColor: Appearance.colors.layerBackground(),
                                border: `1px solid ${Appearance.colors.divider()}`,
                                borderRadius: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                padding: isMobile ? '32px 24px' : '40px 36px',
                                textAlign: 'left'
                            }}>
                                <span style={{
                                    color: Appearance.colors.primary(),
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: 2.5,
                                    marginBottom: 24,
                                    textTransform: 'uppercase'
                                }}>{column.label}</span>
                                {column.items.map((line, lineIndex) => (
                                    <div
                                    key={lineIndex}
                                    style={{
                                        alignItems: 'baseline',
                                        borderBottom: lineIndex === column.items.length - 1 ? 'none' : `1px solid ${Appearance.colors.divider()}`,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        paddingBottom: 16,
                                        paddingTop: lineIndex === 0 ? 0 : 16
                                    }}>
                                        <span style={{
                                            color: Appearance.colors.subText(),
                                            flexShrink: 0,
                                            fontFamily: 'Zen Dots',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            letterSpacing: 0.5,
                                            marginRight: 18,
                                            opacity: 0.5
                                        }}>{`0${lineIndex + 1}`}</span>
                                        <span style={{
                                            color: Appearance.colors.text(),
                                            fontSize: 15,
                                            fontWeight: 400,
                                            lineHeight: 1.55
                                        }}>{line}</span>
                                    </div>
                                ))}
                            </div>
                        </HomeReveal>
                    ))}
                </div>
            </section>
        )
    }

    const getEconomics = () => {

        let stats = [{
            value: '$27–$36',
            unit: 'per client / year',
            label: 'Recurring Commission',
            description: `Paid to your agency every year a referred client maintains their LifeWipe subscription. A 15–20% share of the $180 policyholder price.`
        },{
            value: '$0',
            unit: 'upfront',
            label: 'Cost To Your Agency',
            description: `No platform fee. No setup cost. No procurement cycle. We provide co-branded materials, onboarding, and an activation dashboard.`
        },{
            value: '5%+',
            unit: 'of referrals',
            label: 'Target Conversion Rate',
            description: `Of policyholders you introduce, 5% or more become paying clients. Activated = account + one stored item + one trusted contact.`
        }];

        let mathSteps = [
            { value: '1,500', label: 'Policyholders referred' },
            { value: '× 5%', label: 'Base-case activation' },
            { value: '= 75', label: 'New paying clients' },
            { value: '$1.5K–$2K', label: 'Year-one recurring' }
        ];

        return (
            <section style={{
                alignItems: 'center',
                borderTop: Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100vw',
                overflow: 'hidden',
                width: '100%',
                ...sectionPadding
            }}>
                {renderSectionHeader({
                    description: `Policyholders pay LifeWipe directly. Your agency earns a share of every referred subscription — recurring every year they renew, with nothing to deploy or maintain on your end.`,
                    eyebrow: 'Earnings',
                    subTitle: (
                        <span>
                            {`Recurring commission. `}
                            <span style={{ color: Appearance.colors.primary() }}>{'Zero'}</span>
                            {` upfront cost.`}
                        </span>
                    ),
                    title: 'What Your Agency Earns'
                })}
                <div style={{
                    display: 'grid',
                    gap: isMobile ? 16 : 24,
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    maxWidth: 1280,
                    width: '100%'
                }}>
                    {stats.map((stat, index) => (
                        <HomeReveal key={index} delay={index * 0.08}>
                            <div style={{
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.layerBackground(),
                                border: `1px solid ${Appearance.colors.divider()}`,
                                borderRadius: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                padding: isMobile ? '36px 24px' : '48px 32px',
                                textAlign: 'center'
                            }}>
                                <span style={{
                                    color: Appearance.colors.primary(),
                                    fontFamily: 'Zen Dots',
                                    fontSize: isMobile ? 40 : 50,
                                    fontWeight: 800,
                                    letterSpacing: -2,
                                    lineHeight: 1
                                }}>{stat.value}</span>
                                <span style={{
                                    color: Appearance.colors.subText(),
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: 2,
                                    marginTop: 14,
                                    textTransform: 'uppercase'
                                }}>{stat.unit}</span>
                                <div style={{
                                    backgroundColor: Appearance.colors.divider(),
                                    height: 1,
                                    marginBottom: 22,
                                    marginTop: 26,
                                    width: 48
                                }} />
                                <span style={{
                                    color: Appearance.colors.text(),
                                    fontFamily: 'Zen Dots',
                                    fontSize: 14,
                                    fontWeight: 800,
                                    letterSpacing: 1,
                                    marginBottom: 14,
                                    textTransform: 'uppercase'
                                }}>{stat.label}</span>
                                <span style={{
                                    color: Appearance.colors.subText(),
                                    fontSize: 14,
                                    fontWeight: 400,
                                    lineHeight: 1.6,
                                    maxWidth: 280
                                }}>{stat.description}</span>
                            </div>
                        </HomeReveal>
                    ))}
                </div>
                <HomeReveal delay={0.32} style={{ marginTop: isMobile ? 48 : 72, maxWidth: 1100, width: '100%' }}>
                    <div style={{
                        backgroundColor: Appearance.colors.layerBackground(),
                        border: `1px solid ${Appearance.colors.divider()}`,
                        borderRadius: 12,
                        padding: isMobile ? '32px 24px' : '40px 48px',
                        width: '100%'
                    }}>
                        <span style={{
                            color: Appearance.colors.primary(),
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 2.5,
                            marginBottom: 28,
                            textAlign: 'center',
                            textTransform: 'uppercase'
                        }}>{'Year One Earnings · Base Case'}</span>
                        <div style={{
                            display: 'grid',
                            gap: isMobile ? 24 : 12,
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                            width: '100%'
                        }}>
                            {mathSteps.map((step, index) => (
                                <div
                                key={index}
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    textAlign: 'center'
                                }}>
                                    <span style={{
                                        color: index === mathSteps.length - 1 ? Appearance.colors.primary() : Appearance.colors.text(),
                                        fontFamily: 'Zen Dots',
                                        fontSize: isMobile ? 26 : 28,
                                        fontWeight: 800,
                                        letterSpacing: -1,
                                        lineHeight: 1
                                    }}>{step.value}</span>
                                    <span style={{
                                        color: Appearance.colors.subText(),
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: 1.5,
                                        marginTop: 12,
                                        textTransform: 'uppercase'
                                    }}>{step.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </HomeReveal>
                <HomeReveal delay={0.4} style={{ marginTop: isMobile ? 40 : 56, maxWidth: 820, textAlign: 'center' }}>
                    <p style={{
                        color: Appearance.colors.text(),
                        fontFamily: 'Instrument Serif',
                        fontSize: isMobile ? 18 : 22,
                        fontStyle: 'italic',
                        fontWeight: 400,
                        lineHeight: 1.5,
                        margin: 0,
                        opacity: 0.85
                    }}>{`Earnings compound as activated cohorts renew. Base-case model targets LTV:CAC above 3x with payback under one month.`}</p>
                    <span style={{
                        color: Appearance.colors.subText(),
                        display: 'block',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 2.5,
                        marginTop: 16,
                        textTransform: 'uppercase'
                    }}>{'— Ross School of Business · MAP Analysis'}</span>
                </HomeReveal>
            </section>
        )
    }

    const getCTA = () => {
        return (
            <section style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                borderTop: Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: isMobile ? '20vh' : '24vh',
                paddingLeft: isMobile ? 24 : '6vw',
                paddingRight: isMobile ? 24 : '6vw',
                paddingTop: isMobile ? '20vh' : '24vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <HomeReveal style={{ display: 'flex', justifyContent: 'center' }}>
                    <HomeEyebrow>{'Next Step'}</HomeEyebrow>
                </HomeReveal>
                <HomeReveal delay={0.06} style={{ marginTop: 28, maxWidth: 1200 }}>
                    <h2 style={{
                        color: Appearance.colors.text(),
                        fontFamily: 'Zen Dots',
                        fontSize: isMobile ? 36 : 60,
                        fontWeight: 800,
                        letterSpacing: -2,
                        lineHeight: 1.0,
                        margin: 0,
                        textTransform: 'uppercase'
                    }}>
                        {`Apply for the pilot`}
                    </h2>
                </HomeReveal>
                <HomeReveal delay={0.12} style={{ marginTop: isMobile ? 24 : 36, maxWidth: 720 }}>
                    <p style={{
                        color: Appearance.colors.text(),
                        fontSize: isMobile ? 17 : 20,
                        fontWeight: 400,
                        lineHeight: 1.55,
                        margin: 0,
                        opacity: 0.85
                    }}>
                        {`Only a `}
                        <span style={{
                            color: Appearance.colors.primary(),
                            fontWeight: 700
                        }}>{'limited number'}</span>
                        {` of agency slots are available. Apply to start your founder-led conversation. Only requires 20 minutes up front. No slide deck or technical deep-dives — just a conversation about your agency, your clients, and how we can support you.`}
                    </p>
                </HomeReveal>
                <HomeReveal delay={0.18} style={{ marginTop: isMobile ? 32 : 48 }}>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 16 : 28,
                        justifyContent: 'center'
                    }}>
                        <Button
                        color={Appearance.colors.primary()}
                        containerStyle={{ minWidth: 240 }}
                        label={'Apply Now'}
                        onClick={onApplyClick}
                        type={'large'} />
                        <span
                        onClick={onFAQClick}
                        style={{
                            borderBottom: `1px solid ${Appearance.colors.text()}`,
                            color: Appearance.colors.text(),
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 2,
                            paddingBottom: 4,
                            textTransform: 'uppercase'
                        }}>{'Read the FAQ →'}</span>
                    </div>
                </HomeReveal>
                <HomeReveal delay={0.24} style={{
                    borderTop: `1px solid ${Appearance.colors.divider()}`,
                    marginTop: isMobile ? 64 : 96,
                    maxWidth: 900,
                    paddingTop: 20,
                    width: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px 32px',
                        justifyContent: 'center'
                    }}>
                        {trustStripItems.map((label, index) => (
                            <span
                            key={index}
                            style={{
                                color: Appearance.colors.subText(),
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 2.5,
                                opacity: 0.8,
                                textTransform: 'uppercase'
                            }}>{label}</span>
                        ))}
                    </div>
                </HomeReveal>
            </section>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100vw'
        }}>
            {getHeroSection()}
            {getClientProblem()}
            {getToolkit()}
            {getAgencyBenefits()}
            {getPilotOffer()}
            {getEconomics()}
            {getCTA()}
        </div>
    )
}

const HeroSplashAnimated = ({ children, className, style }) => {

    const { scrollY } = useScroll();
    const { system } = useDataStore();
    
    const y = useTransform(scrollY, [0, system.window.size.height], [0, -system.window.size.height]);
    const scale = useTransform(scrollY, [0, 300], [1, 0.85]);
    
    return (
        <motion.div
        className={className}
        style={{
            ...style,
            scale,
            y
        }}>
            {children}
        </motion.div>
    )
}

const HomeSplashSection = ({ actionLabel = 'Learn More', children, description, eyebrow, onClick, subTitle, titles }) => {

    const ref = useRef(null);
    const typedRef = useRef(null);
    const [isInView, setIsInView] = useState(false);
    const { system } = useDataStore();

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', children ? 'start center' : 'center center']
    });

    const blur = useTransform(scrollYProgress, [0, 0.75], [10, 0]);
    const buttonScale = useTransform(scrollYProgress, [0, 1], [0.5, 1]);
    const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

    const eyebrowY = useTransform(scrollYProgress, [0, 1], [175, 1]);
    const titleY = useTransform(scrollYProgress, [0, 1], [200, 1]);
    const subTitleY = useTransform(scrollYProgress, [0, 1], [225, 1]);
    const dividerY = useTransform(scrollYProgress, [0, 1], [250, 1]);
    const supportingY = useTransform(scrollYProgress, [0, 1], [275, 1]);
    const buttonY = useTransform(scrollYProgress, [0, 1], [300, 1]);
    const y = useTransform(scrollYProgress, [0, 1], [50, 1]);

    useEffect(() => {
        let unsubscribe = scrollYProgress.on('change', latest => {
            let shouldBeInView = latest > 0.25 && latest <= 1;
            if(shouldBeInView !== isInView) {
                setIsInView(shouldBeInView);
                if(shouldBeInView) {
                    typedRef.current?.start();
                } else {
                    typedRef.current?.reset();
                    typedRef.current?.stop();
                }
            }
        });
        return unsubscribe;
    }, [scrollYProgress, isInView]);

    return (
        <motion.div 
        ref={ref}
        style={{
            alignItems: 'center',
            display: 'flex',
            filter: useTransform(blur, (value) => `blur(${value}px)`),
            flexDirection: 'column',
            opacity,
            width: '100%',
            y
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                width: '100%'
            }}>
                {eyebrow && (
                    <motion.div
                    className={'mb-3 mb-lg-4 px-2 px-lg-0'}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        position: 'relative',
                        y: eyebrowY
                    }}>
                        <div style={{
                            backgroundColor: Appearance.colors.primary(),
                            height: 2,
                            marginRight: 14,
                            width: 32
                        }} />
                        <span style={{
                            color: Appearance.colors.primary(),
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 3,
                            textTransform: 'uppercase'
                        }}>{eyebrow}</span>
                    </motion.div>
                )}
                <motion.div
                className={'px-2 px-lg-0'}
                style={{
                    maxWidth: 1300,
                    position: 'relative',
                    y: titleY
                }}>
                    <ReactTyped
                    autoPlay={true}
                    backSpeed={50}
                    strings={titles.map(title => title.toUpperCase())}
                    style={{
                        color: Appearance.colors.text(),
                        fontSize: Utils.isMobile() ? 40 : 88,
                        fontWeight: 800,
                        fontFamily: 'Zen Dots',
                        letterSpacing: -2,
                        lineHeight: 1.0
                    }}
                    typedRef={(typed) => { typedRef.current = typed; }}
                    typeSpeed={60} />
                </motion.div>
                <motion.span
                className={'mt-3 mt-lg-4 px-2 px-lg-0'}
                style={{
                    color: Appearance.colors.text(),
                    display: 'block',
                    fontFamily: 'Instrument Serif',
                    fontSize: system.window.isMobile ? 24 : 36,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    lineHeight: 1.2,
                    maxWidth: 1100,
                    position: 'relative',
                    width: '100%',
                    y: subTitleY
                }}>{subTitle}</motion.span>
                <motion.div
                className={'mt-4 mb-5 mt-lg-5'}
                style={{
                    position: 'relative',
                    y: dividerY
                }}>
                    <img
                    src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                    style={{
                        height: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        width: 350,
                        ...system.window.isMobile && {
                            maxWidth: '75%'
                        }
                    }} />
                </motion.div>
                <motion.span
                className={'px-2 px-lg-0'}
                style={{
                    color: Appearance.colors.text(),
                    display: 'block',
                    fontSize: system.window.isMobile ? 16 : 19,
                    fontWeight: 400,
                    lineHeight: 1.55,
                    maxWidth: 820,
                    opacity: 0.85,
                    position: 'relative',
                    y: supportingY
                }}>{description}</motion.span>
                {typeof(onClick) === 'function' && (
                    <motion.div
                    className={'mt-5'}
                    style={{
                        scale: buttonScale,
                        y: buttonY
                    }}>
                        <Button
                        color={'primary'}
                        containerStyle={{
                            minWidth: 180
                        }}
                        label={actionLabel}
                        onClick={onClick}
                        type={'large'} />
                    </motion.div>
                )}
                <motion.div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    y: buttonY,
                    width: '100%'
                }}>
                    {children}
                </motion.div>
            </div>
        </motion.div>
    )
}

export const KnowledgeBase = ({ utils }) => {

    const { system } = useDataStore();

    const onItemClick = item => {
        window.open(item.url);
    }

    const getContent = () => {

        let items = [{
            description: `What if your phone could protect your legacy — even after you’re gone? We breakdown how our platform lets you trigger an emergency data wipe, assign Trusted Contacts, and securely pass on digital assets or destroy them — all based on your custom rules`,
            image: 'images/kb-emergency-triggers.jpg',
            title: 'Emergency Triggers, Trusted Contacts & Total Control',
            url: 'https://www.youtube.com/watch?v=X58i3c4GWhI'
        },{
            description: `Your digital life deserves better protection—especially when it comes to emergency triggers, legacy planning, or data deletion. In this quick video, we break down how LifeWipe protects your account using advanced, multi-layered security built on Amazon Web Services.`,
            image: 'images/kb-military-security.jpg',
            title: 'Military-Grade Security in 90 Seconds',
            url: 'https://www.youtube.com/watch?v=KCjMiKReuUI'
        },{
            description: `In this quick guide, we show you exactly how to add an external account to your LifeWipe vault—securely and intelligently. LifeWipe ensures your critical data can be wiped, preserved, or transferred in the event of emergency or death.`,
            image: 'images/kb-add-external-account.jpg',
            title: 'How to Add an External Account',
            url: 'https://www.youtube.com/watch?v=VM7kd3ENOl4'
        },{
            description: `LifeChecks are your silent safeguard—automated digital pulse-checks that confirm you're okay and in control. If you don’t respond, LifeWipe follows your exact instructions to wipe data, lock accounts, or notify trusted contacts.`,
            image: 'images/kb-setup-life-checks.jpg',
            title: 'How to Set Up LifeChecks',
            url: 'https://www.youtube.com/watch?v=hFrgLg0X_Yk'
        },{
            description: `Not all emergencies give you time to log in. LifeWipe’s emergency triggers are your digital last resort—pre-configured actions that activate instantly when danger strikes or when you're unable to respond.`,
            image: 'images/kb-setup-emergency-triggers.jpg',
            title: 'How to Set Up Emergency Triggers',
            url: 'https://www.youtube.com/watch?v=t8PWJ2Yn8mQ'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div 
                className={'text-left text-xl-center'}
                style={{
                    borderRadius: system.window.isMobile ? 0 : 10,
                    maxWidth: 1250,
                    overflow: 'hidden',
                    width: '100%',
                    ...Utils.isMobile() === false && {
                        backgroundColor: Appearance.colors.panelBackground()
                    }
                }}>
                    {items.map((item, index) => {
                        if(Utils.isMobile() === true) {
                            return (
                                <div 
                                className={'cursor-pointer'}
                                key={index}
                                onClick={onItemClick.bind(this, item)}
                                style={{
                                    backgroundColor: Appearance.colors.panelBackground(),
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: index !== items.length - 1 ? 32 : 0,
                                    width: '100%'
                                }}>
                                    <div style={{
                                        backgroundColor: Appearance.colors.softBorder(),
                                        height: 250,
                                        padding: 24,
                                        width: '100%'
                                    }}>
                                        <img 
                                        src={item.image}
                                        style={{
                                            borderRadius: 8,
                                            height: '100%',
                                            flex: 'none',
                                            objectFit: 'cover',
                                            width: '100%'
                                        }} />
                                    </div>
                                    <div style={{
                                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: 24,
                                        width: '100%',
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.header(),
                                            fontFamily: 'Zen Dots',
                                            marginBottom: 8,
                                            maxWidth: '100%',
                                            whiteSpace: 'normal'
                                        }}>{item.title.toUpperCase()}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            color: Appearance.colors.subText(),
                                            maxWidth: '100%'
                                        }}>{item.description}</span>
                                    </div>
                                </div>
                            )
                        }
                        return (
                            <div 
                            className={`view-entry ${window.theme}`}
                            key={index}
                            onClick={onItemClick.bind(this, item)}
                            style={{
                                alignItems: 'center',
                                borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                minWidth: 0,
                                padding: 24,
                                width: '100%'
                            }}>
                                <img 
                                src={item.image}
                                style={{
                                    border: `1px solid ${Appearance.colors.divider()}`,
                                    borderRadius: 8,
                                    height: 100,
                                    flex: 'none',
                                    marginRight: 18,
                                    objectFit: 'cover',
                                    width: 150
                                }} />
                                <div style={{
                                    alignItems: 'flex-start',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1,
                                    overflow: 'hidden',
                                    textAlign: 'left',
                                    textOverflow: 'ellipsis'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.header(),
                                        fontFamily: 'Zen Dots',
                                        maxWidth: '100%'
                                    }}>{item.title.toUpperCase()}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        color: Appearance.colors.subText(),
                                        maxWidth: '100%'
                                    }}>{item.description}</span>
                                </div>
                                <img
                                src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                                style={{
                                    height: 20,
                                    flex: 'none',
                                    marginLeft: 8,
                                    objectFit: 'contain',
                                    width: 15
                                }} />
                            </div>
                        )
                    })}
                </div>
            </AnimatedElement>
        )
    }

    const getSocialChannels = () => {
        return [{
            key: 'facebook',
            title: 'Facebook',
            url: 'https://www.facebook.com/p/LifeWipe-61574950785314/'
        },{
            key: 'instagram',
            title: 'Instagram',
            url: 'https://www.instagram.com/lifewipeinc/'
        },{
            key: 'linked-in',
            title: 'LinkedIn',
            url: 'https://www.linkedin.com/company/91089560'
        },{
            key: 'tiktok',
            title: 'TikTok',
            url: 'https://www.tiktok.com/@lifewipe'
        },{
            key: 'x',
            title: 'X',
            url: 'https://twitter.com/lifewipeinc'
        },{
            key: 'youtube',
            title: 'YouTube',
            url: 'http://www.youtube.com/@lifewipe'
        }]
    }

    const getSocialMediaAccounts = () => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Stay connected with us on social media for the latest updates, expert tips, and inspiring community stories. By following our channels, you'll gain early access to new features, security best practices, and real-life success stories from people who have protected their digital legacies with LifeWipe. Join the conversation, ask questions, and share your own experiences.`}
                childrenStyle={system.window.isMobile && {
                    padding: 0
                }}
                title={'Connect on Social Media'}>
                    {getSocialChannels().map((channel, index, channels) => {
                        if(Utils.isMobile() === true) {
                            return (
                                <div 
                                className={'cursor-pointer'}
                                key={index}
                                onClick={window.open.bind(this, channel.url)}
                                style={{
                                    alignItems: 'center',
                                    borderBottom: index !== channels.length - 1 && Appearance.styles.bottomBorder(),
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: 16,
                                    width: '100%'
                                }}>
                                    <img
                                    src={`images/${channel.key}-posts-icon.png`}
                                    style={{
                                        height: 25,
                                        flex: 'none',
                                        marginRight: 12,
                                        objectFit: 'contain',
                                        width: 25
                                    }} />
                                    <span style={{
                                        ...Appearance.textStyles.header(),
                                        flexGrow: 1,
                                        fontFamily: 'Zen Dots',
                                        maxWidth: '100%'
                                    }}>{channel.title.toUpperCase()}</span>
                                    <img
                                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                                    style={{
                                        height: 20,
                                        flex: 'none',
                                        marginLeft: 8,
                                        objectFit: 'contain',
                                        width: 15
                                    }} />
                                </div>
                            )
                        }
                        return (
                            <img
                            className={'text-button'}
                            key={index}
                            onClick={window.open.bind(this, channel.url)}
                            src={`images/${channel.key}-posts-icon.png`}
                            style={{
                                height: 60,
                                flex: 'none',
                                marginLeft: 8,
                                marginRight: 8,
                                objectFit: 'contain',
                                width: 60
                            }} />
                        )
                    })}
                </PanelContentContainer>
            </AnimatedElement>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 24,
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Master your digital legacy. Explore every tool, trigger, and safeguard built to protect what matters most. Our knowledge base is your personal guide to understanding, activating, and optimizing your LifeWipe subscription. Whether you’re just getting started or want to dive deeper into advanced features, these walkthroughs are here to give you confidence in your setup and clarity in your decisions. You’re not just protecting devices; you’re protecting your future, your privacy, and the people you care about most`}
                title={'Knowledge Base'} />
            </AnimatedElement>
            {getContent()}
            {getSocialMediaAccounts()}
        </div>
    )
}

export const Legal = ({ utils }) => {

    const documents = getLegalDocuments();

    const onProcessWaypoint = () => {
        let props = utils.navigation.get();
        if(props.view === 'legal' && props.waypoint) {
            let document = documents.find(doc => doc.id === props.waypoint);
            if(document) {
                onViewAgreementClick(document);
                utils.navigation.set({ ...props, waypoint: null })
            }
        }
    }

    const onViewAgreementClick = document => {
        utils.layer.open({
            Component: DocumentViewer.bind(this, { document }),
            id: 'document_viewer'
        });
    }

    useEffect(() => {
        onProcessWaypoint();
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            useContentDivider={true}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Welcome to LifeWipe's Legal Center — the definitive resource for reviewing all of our official user agreements and operational policies. Here, you'll find detailed documentation outlining how our service works, your rights, and our mutual responsibilities. If you have any questions or concerns about these agreements, reach out to our support team at support@lifewipe.com before using our service. If you disagree with or do not fully understand any part of our legal agreements, we recommend not using the LifeWipe service. Your safety, clarity, and informed consent are our top priorities.`}
                title={'Legal Agreements & Policies'} />
            </AnimatedElement>
            {documents.map((document, index) => {
                return (
                    <AnimatedElement
                    {...utils.animations.get('panel')}
                    animateWithPage={true}
                    key={index}
                    useContentDivider={index !== documents.length - 1}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                    }}>
                        <PanelContentContainer
                        body={document.description}
                        buttons={[{
                            color: 'primary',
                            key: 'view',
                            onClick: onViewAgreementClick.bind(this, document),
                            text: document.url ? 'Download Agreement' : 'View Agreement'
                        }]}
                        title={document.title} />
                    </AnimatedElement>
                )
            })}
        </div>
    )
}

export const NewCyberFindersApplication = ({ index, options, utils }) => {

    const layerID = 'new_cyber_finders_application';

    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { user } = useDataStore();

    const getBooleanItems = () => {
        return [{
            id: 'yes',
            title: 'Yes'
        },{
            id: 'no',
            title: 'No'
        },{
            id: 'maybe',
            title: 'Maybe'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'details',
            lastItem: true,
            style: {
                border: 'none'
            },
            items: [{
                component: 'textview',
                key: 'goals',
                onChange: text => onUpdateTarget({ goals: text }),
                title: 'What are your career goals in cybersecurity or tech, and how do you see LifeWipe helping you get there?',
                value: edits.goals
            },{
                component: 'textview',
                key: 'confidence',
                onChange: text => onUpdateTarget({ confidence: text }),
                title: 'How confident are you in your ability to grow and maintain at least 100 active subscribers?',
                value: edits.confidence
            },{
                component: 'textview',
                key: 'certifications',
                onChange: text => onUpdateTarget({ certifications: text }),
                title: 'Which certifications are you most interested in earning through this program?',
                value: edits.certifications
            },{
                component: 'list',
                key: 'job_interest',
                items: getBooleanItems(),
                onChange: item => onUpdateTarget({ job_interest: item?.id }),
                title: 'Are you interested in federal cybersecurity jobs or government clearance in the future?',
                value: getBooleanItems().find(item => item.id === edits.job_interest)?.title
            },{
                component: 'textview',
                key: 'resources',
                onChange: text => onUpdateTarget({ resources: text }),
                title: 'What kind of support or tools would help you succeed in building your subscriber base quickly?',
                value: edits.resources
            }]
        }];
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/users/', {
                ...edits,
                type: 'new_cyber_finders_application'
            });

            // show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: `Thanks for applying to the LifeWipe x CyberFinders training pathway. We've received your submission and are reviewing it now. If selected, you'll unlock access to fully paid cyber certifications, job placement incentives, and potential security clearance sponsorship.`,
                onClick: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your cyber finders application. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_cyber_finders_application.submit_click',
                title: 'Oops!'
            });
        }
    }


    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const setupEditing = () => {
        setEdits({ user_id: user.user_id });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={'CyberFinders Career Growth & Training'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`Ready to break into cybersecurity and grow your impact? Apply for our Career Growth Track and earn fully paid training, certification exams, and job placement perks as you build your LifeWipe subscriber base. If you're hungry, mission-driven, and ready to build both income and credentials, this is your launchpad.`}</span>
                </LayerItem>
            </div>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            <div style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <span style={{
                    color: Appearance.colors.subText(),
                    display: 'flex',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.75,
                    width: '100%'
                }}>{`Submitting this application does not guarantee acceptance into the Career Growth & Cyber Training program. All applicants will be reviewed based on eligibility criteria, including subscriber performance, engagement level, and alignment with LifeWipe's mission. Access to paid certification, job placement incentives, and clearance sponsorship is contingent on meeting specific benchmarks. LifeWipe reserves the right to approve, delay, or deny access to training benefits based on program capacity and applicant standing. By applying, you acknowledge that you understand our terms of service and agree to receive updates regarding your application status and next steps.`}</span>
            </div>
        </Layer>
    )
}

export const NewSalesOpsApplication = ({ index, options, utils }) => {

    const layerID = 'new_sales_ops_application';

    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { user } = useDataStore();

    const getAdvertisingMediums = () => {
        return [{
            id: 'social_media',
            title: 'Social Media'
        },{
            id: 'in_person',
            title: 'In-Person'
        },{
            id: 'both',
            title: 'Both'
        },{
            id: 'not_sure_yet',
            title: 'Not Sure Yet'
        }]
    }

    const getBooleanItems = () => {
        return [{
            id: 'yes',
            title: 'Yes'
        },{
            id: 'no',
            title: 'No'
        },{
            id: 'maybe',
            title: 'Maybe'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'details',
            lastItem: true,
            style: {
                border: 'none'
            },
            items: [{
                component: 'textview',
                key: 'justification',
                onChange: text => onUpdateTarget({ justification: text }),
                title: 'Why do you want to become a Sales Operative?',
                value: edits.justification
            },{
                component: 'textview',
                key: 'comfortability',
                onChange: text => onUpdateTarget({ comfortability: text }),
                title: 'How comfortable are you explaining privacy tools or cybersecurity concepts?',
                value: edits.comfortability
            },{
                component: 'bool_list',
                key: 'sales_experience',
                onChange: val => onUpdateTarget({ sales_experience: val }),
                title: 'Have you ever done referral, outreach, or sales work before?',
                value: edits.sales_experience
            },{
                component: 'multiple_list',
                items: getPositionItems(),
                key: 'positions_of_interest',
                onChange: items => onUpdateTarget({ positions_of_interest: items.map(item => item.id) }),
                title: 'Which positions are you interested in? Select all that apply.',
                value: getPositionItems().filter(item => (edits.positions_of_interest || []).includes(item.id))
            },{
                component: 'list',
                key: 'advertising_medium',
                items: getAdvertisingMediums(),
                onChange: item => onUpdateTarget({ advertising_medium: item?.id }),
                title: 'Do you plan to promote via social media, in-person, or both?',
                value: getAdvertisingMediums().find(item => item.id === edits.advertising_medium)?.title
            },{
                component: 'list',
                key: 'merch_tools',
                items: getBooleanItems(),
                onChange: item => onUpdateTarget({ merch_tools: item?.id }),
                title: 'Are you interested in using merch tools like QR stickers or shirts?',
                value: getBooleanItems().find(item => item.id === edits.merch_tools)?.title
            },{
                component: 'list',
                key: 'field_ops_opportunities',
                items: getBooleanItems(),
                onChange: item => onUpdateTarget({ field_ops_opportunities: item?.id }),
                title: 'Would you like to be considered for Field Operative roles in the future?',
                value: getBooleanItems().find(item => item.id === edits.field_ops_opportunities)?.title
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'What is your preferred phone number for follow up communications?',
                value: edits.phone_number
            }]
        }];
    }

    const getPayoutMethods = () => {
        return [{
            id: 'cash_app',
            title: 'Cash App'
        },{
            id: 'paypal',
            title: 'PayPal'
        },{
            id: 'venmo',
            title: 'Venmo'
        },{
            id: 'stripe',
            title: 'Stripe'
        },{
            id: 'zelle',
            title: 'Zelle'
        }]
    }

    const getPositionItems = () => {
        return [{
            id: 'affiliate',
            title: 'Affiliate Partner'
        },{
            id: 'setter',
            title: 'Appointment Coordinator'
        },{
            id: 'closer',
            title: 'Sales Closer'
        }];
    }
    
    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/users/', {
                ...edits,
                type: 'new_sales_ops_application'
            });

            // show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: `Thanks for applying to become a LifeWipe Sales Operative. We've got your submission and are reviewing it now. You'll hear from us soon with next steps, onboarding info, and how to activate your referral tools.`,
                onClick: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your sales operative application. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_sales_ops_application.submit_click',
                title: 'Oops!'
            });
        }
    }


    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const setupEditing = () => {
        setEdits({ 
            phone_number: user.phone_number,
            user_id: user.user_id 
        });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={'Sales Operatives Program'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`We're looking for motivated, privacy-conscious individuals ready to help others protect their digital legacy—and get paid doing it. As a Sales Operative, you'll earn recurring commissions, unlock salary tiers, and gain access to exclusive tools like referral dashboards, merch kits, and potential field ops opportunities. This short application helps us understand your goals, strengths, and how you want to grow with LifeWipe.`}</span>
                </LayerItem>
            </div>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            <div style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <span style={{
                    color: Appearance.colors.subText(),
                    display: 'flex',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.75,
                    width: '100%'
                }}>{`By submitting your application, you acknowledge and agree that you are representing yourself as an independent contractor and not as an employee, agent, or representative of LifeWipe Inc. Submission of this application does not establish an employment relationship of any kind. You are solely responsible for complying with any applicable laws, regulations, or tax obligations related to your independent contractor status. Additionally, you confirm that you have read, understood, and agree to the Terms of Service and all other applicable legal agreements. Please review them carefully before submitting your application`}</span>
            </div>
        </Layer>
    )
}

export const Pricing = ({ utils }) => {

    const [loading, setLoading] = useState(false);
    const { request, user } = useDataStore();

    const onMilitaryDiscountClick = () => {
        utils.alert.show({
            title: 'Military & Veteran Discount',
            message: `We're proud to support those who serve. LifeWipe offers exclusive discounts for active duty, reserve, and retired military personnel — including veterans and honorably discharged service members. Would you like to learn more about applying for these discounts?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(() => user ? onMilitaryDiscountConfirm() : utils.user.request.login({}, onMilitaryDiscountConfirm), 500);
                    return;
                }
            }
        });
    }

    const onMilitaryDiscountConfirm = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('military_discount');
            await Request.post(utils, '/utils/', {
                type: 'new_military_discount_information_request'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: `We've emailed you the full details, including what's required to verify your military status and how to submit your documentation. Please allow up to 5 minutes for the message to be delivered.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your request. ${e.message || 'An unknown error occurred'}`,
                origin: 'pricing.military_discount_confirm',
                title: 'Oops!'
            });
        }
    }

    const getFAQ = () => {

        let items = [{
            body: `Our tiers differ primarily in the number of devices and accounts covered, frequency of check-ins, types of legacy messages allowed, and access to in-person services. All tiers include in-person device wiping for the first 6 months, but only Elite tier maintains this service permanently.`,
            title: 'What is the difference between the service tiers?'
        },{
            body: 'Yes, you can upgrade your plan at any time. Your age-based discount will apply to the new plan rate based on your age when you first signed up.',
            title: 'Can I upgrade my plan later?'
        },{
            body: 'Active duty, veterans, and their immediate family members can verify their status through our secure verification partner. Once verified, the 10% discount is automatically applied to any plan you choose.',
            title: 'How do I apply for the military discount?'
        },{
            body: `You can cancel your subscription at any time through your account dashboard or by contacting our customer support team. Upon cancellation, we delete all your instructions and information from our systems.`,
            title: 'How do I cancel my subscription?'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Got questions? We've got answers. Here are some of the most common inquiries we receive about our pricing plans, discounts, and services. If you don't see your question here, feel free to reach out to our support team for more information.`}
                childrenStyle={{
                    padding: 0
                }}
                title={'Frequently Asked Questions'}>
                    <div className={'row m-0 p-0 p-lg-4'}>
                        {items.map((item, index) => {
                            if(Utils.isMobile() === true) {
                                return (
                                    <div
                                    className={'col-12 m-0 p-0'}
                                    key={index}>
                                        <div style={{
                                            borderTop: index !== 0 && `1px solid ${Appearance.colors.divider()}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            padding: Appearance.styles.panelPadding(),
                                            width: '100%'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.header(),
                                                fontFamily: 'Zen Dots',
                                                marginBottom: 4,
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                whiteSpace: 'normal'
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                )
                            }
                            return (
                                <div
                                className={'col-12 col-xl-6 p-2'}
                                key={index}>
                                    <div 
                                    className={'p-4'}
                                    style={{
                                        ...Appearance.styles.unstyledPanel(),
                                        display: 'flex',
                                        flexDirection: 'row',
                                        minWidth: 0,
                                        height: '100%',
                                        textAlign: 'left',
                                        width: '100%'
                                    }}>
                                        <img 
                                        src={window.theme === 'dark' ? 'images/faq-badge-dark.png' : 'images/faq-badge-light.png'}
                                        style={{
                                            display: Utils.isMobile() ? 'none' : 'flex',
                                            height: 45,
                                            marginRight: 18,
                                            objectFit: 'contain',
                                            width: 45
                                        }} />
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flexGrow: 1,
                                            overflow: 'hidden'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.header(),
                                                fontFamily: 'Zen Dots',
                                                marginBottom: 4,
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                whiteSpace: 'normal'
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        )
    }

    const getMilitaryDiscountComponent = () => {

        const buttons = [{
            color: 'primary',
            key: 'verify_apply_discount',
            loading: loading === 'military_discount',
            onClick: onMilitaryDiscountClick,
            text: 'Verify & Apply Discount'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}>
                <PanelContentContainer
                body={`As a gesture of our deep appreciation and respect, we proudly offer a 10% discount on all our plans to active duty military personnel, veterans, first responders, and their families. Your dedication, sacrifice, and unwavering commitment to serving our country do not go unnoticed, and this is one small way we can say thank you. Whether you're currently serving or have served in the past, we are honored to support you and your loved ones with this exclusive offer.`}
                buttons={buttons}
                title={'Military and First Responders Discount'} />
            </AnimatedElement>
        )
    }

    const getPaymentOptions = () => {
        return (
            <div style={{
                maxWidth: 1500,
                width: '100%'
            }}>
                <AnimatedElement
                {...utils.animations.get('panel')}
                animateWithPage={true}
                useContentDivider={true}>
                    <SubscriptionOptions utils={utils} />
                </AnimatedElement>  
            </div>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            {getPaymentOptions()}
            {getMilitaryDiscountComponent()}
            {getFAQ()}
            <StartConversation utils={utils} />
        </div>
    )
}

export const RequiemChat = ({ utils }) => {

    const chatIDRef = useRef(null);
    const container = useRef(null);
    const messagesRef = useRef([]);
    const scrollContainer = useRef(null);
    const streamingMessageRef = useRef(null);
    const textAreaRef = useRef(null);

    const [animations, animationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        right: -500
    }));
    const [askButtonAnimations, askButtonAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        bottom: 0
    }));
    const [askMenuAnimations, askMenuAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 18 },
        bottom: -500
    }));
    const [mobileAnimations, mobileAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        bottom: -500,
        y: -125
    }));
    const [textInputAnimations, textInputAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        bottom: -250,
        opacity: 0
    }));

    const [askMenuVisible, setAskMenuVisible] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [files, setFiles] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [mobileVisibilityState, setMobileVisibilityState] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const [title, setTitle] = useState(null);
    const [text, setText] = useState(null);
    const [textContainerHeight, setTextContainerHeight] = useState(0);
    const { requiem, sockets, user, setRequiem } = useDataStore();
    const [visibilityState, setVisibilityState] = useState(false);

    // prepare dropzone props
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
        onDrop: files => {
            onDataVaultFileDrop(files);
        },
        multiple: false,
        noClick: true,
        noKeyboard: true,
        noDragEventsBubbling: true,
        preventDropOnDocument: true
    });

    const mobileDrag = useDrag(({ cancel, down, dragging, last, movement: [mx, my], velocity: [vx, vy], xy: [px, py], }) => {

        // update dragging flag within local state and set distance from bottom value
        setDragging(dragging);
        let distanceY = window.innerHeight - py;

        // animate component to collapsed state if enough distance has been covered or if enough velocity in the down direction is detected
        if(distanceY < 150 || (my > 0 && vy > 2.5)) {
            console.log(`animating out of view, distanceY: ${distanceY}`);
            cancel();
            mobileAnimationsApi.start({
                to: async next => {
                    await next({ y: window.innerHeight });
                    setMobileVisibilityState(false);
                }
            });
            return;
        }

        // move component to new location if dragging is still in progress
        if(last === false) {
            mobileAnimationsApi.start({ y: my, immediate: down });
            return;
        }

        // snap back to standard location when drag is complete
        console.log('snapping to default origin');
        mobileAnimationsApi.start({ y: 0 });


        /*
        // set component to full expansion if dragged far enough
        if(my < -125 && mobileTextExpanded === false) {
            console.log(`expanding to full content, my: ${my}`);
            cancel();
            setMobileTextExpanded(true);
            mobileAnimationsApi.start({ y: 0 });
            return;
        }

        // animate component to collapsed state if enough distance has been covered or if enough velocity in the down direction is detected
        if((distanceY < 150 || (my > 0 && vy > 2.5)) && mobileTextExpanded === true) {
            console.log(`collapsing to minimal, my: ${my}`);
            cancel();
            setMobileTextExpanded(false);
            mobileAnimationsApi.start({ y: 0 });
            return;
        }

        // animate component out of view if dragged far enough and if not expanded
        if(distanceY < 50 && mobileTextExpanded === false) {
            console.log(`animating out of view, distanceY: ${distanceY}`);
            cancel();
            mobileAnimationsApi.start({
                to: async next => {
                    await next({ y: 500 });
                    setMobileTextExpanded(false);
                    setMobileVisibilityState(false);
                }
            });
            return;
        }

        // move component to new location if dragging is still in progress
        if(last === false) {
            mobileAnimationsApi.start({ y: my, immediate: down });
            return;
        }

        // snap back to standard location when drag is complete
        console.log('snapping to default origin');
        mobileAnimationsApi.start({ y: 0 });
        */
        
    }, {
        axis: 'y'
    });

    const onDataChange = (index, data) => {

        // TODO => this needs to be updated server-side as well
        setMessages(messages => {
            return update(messages, {
                [index]: {
                    $set: data
                }
            });
        });
    }

    const onDataVaultFileDrop = files => {

        // no additional logic is required if no files were provided
        if(!files || files.length === 0) {
            return;
        }

        // update local state with files and start loading
        setFiles(files);
        setLoading(true);

        // send request to requiem to start data vault item guided action
        sockets.send('requiem', 'new_chat_completion_message', {
            ...getSubmissionProps(),
            metadata: {
                file_name: files[0].name,
                mime_type: files[0].type
            },
            preset: 'create_data_vault_file'
        });
    }

    const onEditTitleClick = () => {

        let result = title;
        utils.alert.show({
            title: 'Edit Conversation Title',
            message: 'What would you like to call this conversation?',
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    onChange={text => result = text}
                    placeholder={title} 
                    value={result} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Save',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {

                // TODO => update mongo with new title
                if(key === 'confirm' && result && result.trim().length > 0) {
                    setTitle(result.trim());
                    return;
                }
            }
        });
    }

    const onMessagesChange = () => {

        // update messages ref
        messagesRef.current = messages || [];

        // scroll to bottom of the messages container
        scrollToBottom();
    }

    const onMobileBodyClick = evt => {
        if(container.current?.contains(evt.target) === false) {
            //setMobileTextExpanded(false);
            mobileAnimationsApi.start({ y: 0 });
        }
    }

    const onNewChatCompletionResponse = evt => {

        // notify subscribers that a new chat conversation was created if applicable
        if(!chatIDRef.current) {
            chatIDRef.current = evt.id;
            utils.content.fetch('requiem_conversations');
        }

        // update loading flag
        setLoading(false);

        // no further logic is require if no messages were returned
        if(!evt.messages) {
            return;
        }

        // update messages and title state
        setMessages(evt.messages);
        setTitle(evt.title);
    }

    const onNewChatCompletionStreamResponse = evt => {
        
        if(evt.event === 'start') {

            // end loading 
            setLoading(false);

            // initialize streaming message in ref
            streamingMessageRef.current = {
                content: evt.content,
                message_index: messagesRef.current.length,
                timestamp: moment().unix(),
                role: 'assistant',
                visible: true
            };

            // add to messages state
            const newMessages = [...messagesRef.current, streamingMessageRef.current];
            messagesRef.current = newMessages;
            setMessages(newMessages);
        }
        
        if(evt.event === 'text') {

            // accumulate content in ref
            if(streamingMessageRef.current) {

                // add content to ref
                streamingMessageRef.current.content += evt.content;

                // update state with new array and new message object reference
                const newMessages = [...messagesRef.current];
                const lastIndex = newMessages.length - 1;
                if(lastIndex >= 0) {
                    newMessages[lastIndex] = { ...streamingMessageRef.current };
                }
                messagesRef.current = newMessages;
                setMessages(newMessages);
            }
        }
        
        if(evt.type === 'complete') {
            streamingMessageRef.current = null;
            setLoading(false);
        }
    }

    const onProcessIncomingRequiemMessage = async () => {

        // submit message to server on behalf of the user
        submitRequest(requiem.message);

        // open chat window
        onSetVisibilityState(true);

        // update requiem state by removing message request
        setRequiem({ ...requiem, message: null });
    }

    const onProcessIncomingRequiemRequest = async () => {

        let { action, event, message, metadata, preset, smart_flow } = requiem.request;

        // determine if a request to open the chat was received
        if(preset) {

            // update local state with visibility value
            onSetVisibilityState(true);

            // set loading flag if applicable
            if(requiem.request.loading == true) {
                setLoading(true);
            }

            // submit preset to websockets server
            sockets.send('requiem', 'new_chat_completion_message', {
                ...getSubmissionProps(),
                metadata,
                preset: requiem.request.preset
            });

            // remove request from requiem object
            setRequiem({ ...requiem, request: null });
            return;
        }

        // determine if a request to close the chat was received
        if(action === 'close') {

            // update local state with visibility value
            onSetVisibilityState(false);

            // remove request from requiem object
            setRequiem({ ...requiem, request: null });
            return;
        }

        // determine if a file upload request was received
        if(action === 'file_upload') {

            // submit guided action details and file metadata to server
            setLoading(true);
            sockets.send('requiem', 'new_chat_completion_message', {
                ...getSubmissionProps(),
                metadata,
                preset: action
            });

            // remove request from requiem object
            setRequiem({ ...requiem, request: null });
            return;
        }

        // determine if a message was provided
        if(action === 'message' && typeof(message) === 'string') {

            // update local state with visibility value
            onSetVisibilityState(true);

            // add user message to list of messages
            onUpdateMessages([{
                content: message,
                role: 'user',
                visible: true
            }]);
            
            // submit request to server and show thinking loader after a brief sleep
            submitRequest(message);
            setTimeout(setLoading.bind(this, true), 500);

            // remove request from requiem object
            setRequiem({ ...requiem, request: null });
            return;
        }

        // determine if a request to open the chat was received
        if(action === 'open') {

            // update local state with visibility value
            onSetVisibilityState(true);

            // request user approval to overwrite current conversation
            if(requiem.request.session_id) {
                onRequestResumeSession(action, requiem.request.session_id);
            }

            // present a default prompt if no session id was provided
            if(!requiem.request.session_id && messagesRef.current.length === 0) {
                onShowWelcomePrompt();
            }
            
            // remove request from requiem object
            setRequiem({ ...requiem, request: null });
            return;
        }

        // determine if a request to start a new conversation was received
        if(action === 'new_conversation') {

            // update local state with visibility value
            onSetVisibilityState(true);

            // request user approval to overwrite current conversation
            onRequestNewSession(action);
            
            // remove request from requiem object
            setRequiem({ ...requiem, request: null });
            return;
        }

        // determine if a smart flow request was received
        if(action?.includes('smart_flow') === true) {

            // update local state with visibility value and start loading
            onSetVisibilityState(true);
            setLoading(true);

            // submit form change data to chat completions
            sockets.send('requiem', 'new_chat_completion_message', {
                ...getSubmissionProps(),
                smart_flow: { 
                    action, 
                    payload: update(smart_flow, {
                        $unset: ['target'],
                        items: {
                            $set: smart_flow.items?.reduce((array, section) => {
                                section.items.forEach(item => {
                                    if(item.requiemSmartFlow === true) {
                                        array.push({
                                            description: item.description,
                                            ...item.items?.length > 0 && {
                                                items: item.items || [],
                                            },
                                            key: item.key,
                                            required: item.required || false,
                                            section: {
                                                authentication_required: section.authentication?.required || false,
                                                key: section.key,
                                                title: section.title
                                            },
                                            title: item.title
                                        });
                                    }
                                });
                                return array;
                            }, [])
                        }
                    })
                }
            });

            // determine how the requiem object should be updated using the provided action
            setRequiem(requiem => {

                // prepare target for state update
                let target = { ...requiem, request: null };

                // remove smart flow object if a cancel or complete request were received
                if(['smart_flow_cancel', 'smart_flow_complete'].includes(action) === true) {
                    target.smart_flow = null;
                }

                // update smart flow object if start action or event acton were received
                if(['smart_flow_event', 'smart_flow_start'].includes(action) === true) {
                    target.smart_flow = {
                        ...smart_flow,
                        enabled: true
                    };
                }

                return target;
            });
        }

        // process conversation removed event
        if(event === 'conversation_removed') { 

            // clear chat if the removed conversation is currently open
            if(chatIDRef.current === requiem.request.id) {
                chatIDRef.current = null;
                setMessages([]);   
                onSetVisibilityState(false);
            }
        }
    }

    const onPromptClick = key => {

        // animate prompts menu out of view
        onSetAskMenuVisibility(false);

        // update local state with visibility value and start loading
        onSetVisibilityState(true);
        setLoading(true);

        // submit prompt key to websockets server 
        sockets.send('requiem', 'new_chat_completion_message', {
            ...getSubmissionProps(),
            preset: key
        });
    }

    const onRequestNewSession = action => {

        const onConfirm = () => {

            // clear chat id, messages, and text input
            chatIDRef.current = null;
            setMessages([]);
            setText(null);

            // send request to server to start new chat
            setTimeout(onShowWelcomePrompt, 500);
        }

        // determine if a conversation is already in progress
        if(messagesRef.current.length === 0) {
            onConfirm();
            return;
        }

        // request confirmation from user to start new conversation
        utils.alert.show({
            title: 'New Conversation',
            message: `Would you like to start a new conversation or continue your current conversation? Any unsent messages in your current chat will be lost.`,
            buttons: [{
                key: 'confirm',
                title: 'New Conversation',
                style: 'destructive'
            },{
                key: 'continue',
                title: 'Continue Conversation',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onConfirm();
                    return;
                }
            }
        });
    }

    const onRequestResumeSession = (action, sessionId) => {

        const onConfirm = () => {

            // add loading indicator
            setLoading(true);
            
            // submit request to server
            sockets.send('requiem', 'resume_session', {
                ...getSubmissionProps(),
                id: sessionId
            });
        }

        // determine if a conversation is already in progress
        if(messagesRef.current.length === 0) {
            onConfirm();
            return;
        }

        // request confirmation to load session 
        utils.alert.show({
            title: 'Resume Conversation',
            message: `Would you like to resume your previous conversation with Requiem? Any unsent messages in your current chat will be lost.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onConfirm();
                    return;
                }
            }
        });
    }

    const onSetAskMenuVisibility = visibility => {
        askMenuAnimationsApi.start({ 
            to: async next => {
                
                // show elements if applicable
                if(visibility === true) {
                    setAskMenuVisible(true);
                }

                // animate components 
                await next({ bottom: visibility ? 8 : -500 });

                // hide elements if applicable
                if(visibility === false) {
                    setAskMenuVisible(false);
                }
            }
        });
    }

    const onSetVisibilityState = state => {

        let isMobile = Utils.isMobile();
        
        // animate desktop and tablet contents into or out of view based on requested state
        if(isMobile === false) {
            animationsApi.start({
                to: async next => {

                    // update visibility state prior to animations if true
                    if(state === true) {
                        setVisibilityState(true);
                    }
                    
                    // animate contents into view
                    await next({
                        opacity: state ? 1 : 0,
                        right: state ? 16 : -500
                    });

                    // update visibility state after animations if false
                    if(state === false) {
                        setVisibilityState(false);
                    }
                }
            });
        }
       
        // animate mobile contents into or out of view based on requested state
        if(isMobile === true) {
            mobileAnimationsApi.start({
                to: async next => {

                    // update visibility state prior to animations if true
                    if(state === true) {
                        setMobileVisibilityState(true);
                    }
                    
                    // animate contents into view
                    await next({
                        bottom: state ? 0 : -window.innerHeight,
                        y: 0
                    });

                    // update visibility state after animations if false
                    if(state === false) {
                        setMobileVisibilityState(false);
                    }
                }
            });
        }
        
        // animate ask menu out of view
        onSetAskMenuVisibility(false);
    }

    const onSendMessage = async () => {
        try {

            // clear text from field
            setText(null);

            // add user message to list of messages
            onUpdateMessages([{
                content: text,
                role: 'user',
                visible: true
            }]);
            
            // submit request to server and show thinking loader after a brief sleep
            submitRequest(text);
            setTimeout(setLoading.bind(this, true), 500);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `An error occurred while processing your request. ${e.message || 'An unknown error occurred'}.`,
                origin: 'requiem_chat.send_message',
                title: 'Oops!'
            });
        }
    }

    const onShowWelcomePrompt = () => {
        setLoading(true);
        sockets.send('requiem', 'new_chat_completion_message', {
            ...getSubmissionProps(),
            preset: 'welcome'
        });
    }

    const onStartOverClick = () => {
        utils.alert.show({
            title: 'Start Over',
            message: `Are you sure that you want to start over? Any unsent messages in your current chat will be lost.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'continue',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {

                    // clear chat id, messages, and text input
                    chatIDRef.current = null;
                    setMessages([]);
                    setText(null);

                    // send request to server to start new chat
                    setTimeout(onShowWelcomePrompt, 500);
                    return;
                }
            }
        });
    }
    
    const onUpdateMessages = targets => {
        messagesRef.current = messagesRef.current.concat(targets);
        setMessages(messagesRef.current);
    }

    const onVoiceRecognitionClick = () => {

        // no additional logic is required if browser does not support speech recognition
        if(!recognition) {
            utils.alert.show({
                title: 'Speech Recognition Not Supported',
                message: 'Your browser does not support speech recognition. Please type your message instead.'
            });
            return;
        }

        // toggle speech recognition
        isListening ? recognition.stop() : recognition.start();
    }

    const getAskRequiemButton = () => {

        let backgroundColor = window.theme === 'dark' ? 'rgba(85,85,85,1)' : 'rgba(250,250,250,1)';
        let padding = 8;
        let size = 55;

        return (
            <animated.div style={{
                alignSelf: 'center',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                padding: 16,
                position: 'fixed',
                ...askButtonAnimations
            }}>
                {askMenuVisible === true && (
                    <animated.div style={{
                        alignItems: 'center',
                        backdropFilter: 'blur(12px)',
                        backgroundColor: Appearance.colors.divider(),
                        border: `2px solid ${Appearance.colors.softBorder()}`,
                        borderRadius: 15,
                        bottom: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        padding: padding,
                        position: 'relative',
                        overflow: 'hidden',
                        WebkitBackdropFilter: 'blur(12px)',
                        ...askMenuAnimations
                    }}> 
                        <div style={{
                            alignItems: 'center',
                            backgroundColor: backgroundColor,
                            borderRadius: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            overflow: 'hidden'
                        }}> 
                            {getSamplePromptComponents()}
                        </div>
                    </animated.div>
                )}
                <div style={{
                    alignItems: 'center',
                    backdropFilter: 'blur(12px)',
                    backgroundColor: Appearance.colors.divider(),
                    borderRadius: size / 2,
                    display: 'flex',
                    flexDirection: 'row',
                    height: size,
                    minWidth: 0,
                    padding: padding,
                    position: 'relative',
                    WebkitBackdropFilter: 'blur(12px)'
                }}>
                    <div 
                    className={`text-button`}
                    onClick={() => {
                        onSetVisibilityState(true);
                        if(messagesRef.current.length === 0) {
                            onShowWelcomePrompt();
                        }
                    }} 
                    style={{
                        alignItems: 'center',
                        backgroundColor: backgroundColor,
                        borderRadius: size / 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: '100%',
                        paddingLeft: 16,
                        paddingRight: 16,
                        minWidth: 0,
                        whiteSpace: 'normal',
                        width: '100%',
                    }}>
                        <span style={{
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontSize: 12,
                            fontFamily: 'Zen Dots',
                            fontWeight: 800,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{'Ask Requiem'.toUpperCase()}</span>
                    </div>
                    <div 
                    className={`text-button`}
                    onClick={onSetAskMenuVisibility.bind(this, !askMenuVisible)}
                    style={{
                        alignItems: 'center',
                        backgroundColor: backgroundColor,
                        borderRadius: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flex: 'none',
                        height: size - (padding * 2),
                        marginLeft: 6,
                        padding: padding,
                        width: size - (padding * 2)
                    }}>
                        <img
                        src={window.theme === 'dark' ? 'images/ask-requiem-white.png' : 'images/ask-requiem-grey.png'}
                        style={{
                            height: '100%',
                            width: '100%'
                        }} />
                    </div>
                    <BorderBeam
                    colorFrom={Appearance.colors.yellow}
                    colorTo={Appearance.colors.red}
                    duration={8}
                    useSize={true}
                    style={{
                        border: `2px solid ${Appearance.colors.softBorder()}`,
                        borderRadius: size / 2
                    }} />
                </div>
            </animated.div>
        )
    }

    const getContent = () => {

        // return a mobile optimized layout if applicable
        if(Utils.isMobile() === true) {
            
            // determine if content should be shown as a full chat window
            return mobileVisibilityState === true && (
                <animated.div 
                ref={container}
                {...mobileDrag()}
                style={{
                    bottom: 0,
                    left: 0,
                    padding: 14,
                    position: 'fixed',
                    touchAction: 'none',
                    userSelect: dragging ? 'none' : 'auto',
                    width: '100%',
                    zIndex: LayerStartIndex,
                    ...mobileAnimations
                }}>
                    <div 
                    className={'requiem-chat'}
                    style={{
                        ...Appearance.styles.unstyledPanel(),
                        boxShadow: '0px -10px 25px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        width: '100%'
                    }}>
                        {getMobileContent()}
                        {getTextInput()}
                        <div style={{
                            background: window.theme === 'dark' ? 'linear-gradient(180deg, rgba(45, 45, 45, 1) 50%, rgba(45, 45, 45, 0) 100%)' : 'linear-gradient(180deg, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0) 100%)',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            left: 0,
                            paddingBottom: 84,
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingTop: 16,
                            position: 'absolute',
                            top: 0,
                            width: '100%'
                        }}>
                            <img 
                            className={'text-button'}
                            onClick={onStartOverClick}
                            title={'Start Over'}
                            src={window.theme === 'dark' ? 'images/requiem-chat-reset-dark.png' : 'images/requiem-chat-reset-light.png'}
                            style={{
                                height: 22,
                                marginRight: 12,
                                objectFit: 'contain',
                                width: 22
                            }} />
                            <div style={{
                                alignSelf: 'center',
                                backgroundColor: Appearance.colors.softBorder(),
                                borderRadius: 2,
                                height: 4,
                                width: 75
                            }} />
                            <img 
                            className={'text-button'}
                            onClick={onSetVisibilityState.bind(this, false)}
                            title={'Close'}
                            src={window.theme === 'dark' ? 'images/close-button-dark.png' : 'images/close-button-light.png'}
                            style={{
                                height: 20,
                                marginLeft: 12,
                                objectFit: 'contain',
                                width: 20
                            }} />
                        </div>
                    </div>
                </animated.div>
            )
        }
        return visibilityState === true && (  
            <animated.div 
            className={'requiem-chat'}
            ref={container}
            style={{
                ...Appearance.styles.panel(),
                bottom: 16,
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 135px)',
                maxWidth: Utils.isMobile() ? '100%' : 500,
                overflow: 'hidden',
                position: 'fixed',
                width: '100%',
                zIndex: 4,
                ...animations
            }}>
                <div 
                {...getRootProps()}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    position: 'relative'
                }}>
                    <input {...getInputProps()} />
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        height: '100%', 
                        width: '100%'
                    }}>
                        {getMessages()}
                        {getTextInput()}
                    </div>
                    <div style={{
                        alignItems: 'center',
                        background: window.theme === 'dark' ? 'linear-gradient(180deg, rgba(45, 45, 45, 1) 50%, rgba(45, 45, 45, 0) 100%)' : 'linear-gradient(180deg, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0) 100%)',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        padding: '24px 24px 96px 24px',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0
                    }}>
                        <img 
                        className={'text-button'}
                        onClick={onStartOverClick}
                        title={'Start Over'}
                        src={window.theme === 'dark' ? 'images/requiem-chat-reset-dark.png' : 'images/requiem-chat-reset-light.png'}
                        style={{
                            height: 28,
                            marginRight: 16,
                            objectFit: 'contain',
                            width: 25
                        }} />
                        <img 
                        className={'text-button'}
                        onClick={onSetVisibilityState.bind(this, false)}
                        title={'Close'}
                        src={window.theme === 'dark' ? 'images/close-button-dark.png' : 'images/close-button-light.png'}
                        style={{
                            height: 25,
                            marginLeft: 16,
                            objectFit: 'contain',
                            width: 25
                        }} />
                    </div>
                    {isDragActive && (
                        <div style={{
                            alignItems: 'center',
                            backgroundColor: Appearance.colors.background({ alpha: 0.85 }),
                            borderColor: Appearance.colors.text(),
                            borderRadius: 12,
                            borderStyle: 'dashed',
                            borderWidth: 3,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            left: 0,
                            padding: 24,
                            pointerEvents: 'none',
                            position: 'absolute',
                            right: 0,
                            textAlign: 'center',
                            top: 0
                        }}>
                            <div style={{
                                ...Appearance.styles.unstyledPanel(),
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.layerBackground(),
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                padding: 24,
                                width: '75%'
                            }}>
                                <img 
                                src={'images/requiem-file-upload-green.png'}
                                style={{
                                    height: 50,
                                    marginBottom: 16,
                                    width: 50,
                                }} />
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots',
                                    marginBottom: 4
                                }}>{'Upload File'.toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText()
                                }}>{'Drop a file here to start an upload to your Secure Data Vault'}</span>
                            </div>
                        </div>
                    )}
                </div>
            </animated.div>
        )
    }

    const getMessages = () => {

        let targets = messages.filter(message => message.visible !== false);
        return (
            <div 
            ref={scrollContainer}
            style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                maxHeight: '100%',
                overflowY: 'scroll',
                position: 'relative', 
                paddingBottom: textContainerHeight + 16,
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 135,
                width: '100%'
            }}>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    marginTop: 'auto' 
                }}>
                    {targets.map((message, index, messages) => {
                        return (
                            <RequiemMessage 
                            data={message} 
                            files={files}
                            key={index}
                            index={index}
                            isLastItem={loading === false && index === messages.length - 1}
                            loading={message.loading === true}
                            onDataChange={onDataChange.bind(this, index)}
                            utils={utils} />
                        )
                    })}
                    {loading === true && (
                        <RequiemMessageContainer 
                        isLastItem={true}
                        useLoader={true} />
                    )}
                </div>
            </div>
        )
    }

    const getMobileContent = () => {

        // return a loading indicator if applicable
        let targets = messages.filter(message => message.visible !== false);

        // return the most recent message content
        return (
            <div 
            ref={scrollContainer}
            style={{
                paddingTop: 84,
                maxHeight: '50vh',
                overflowY: 'scroll',
                width: '100%'
            }}>
                <div 
                className={'requiem-messages'}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: 16,
                    width: '100%'
                }}>
                    {targets.map((message, index, messages) => {
                        return (
                            <RequiemMessage 
                            data={message} 
                            expanded={true}
                            files={files}
                            key={index}
                            index={index}
                            isLastItem={loading === false && index === messages.length - 1}
                            loading={message.loading === true}
                            utils={utils} />
                        )
                    })}
                    {loading === true && (
                        <RequiemMessageContainer 
                        isLastItem={true}
                        useLoader={true} />
                    )}
                </div>
            </div>
        )
    }

    const getPrimaryButton = () => {

        // determine if loading is in progress
        if(loading !== false) {
            return;
        }

        // determine if a text input can be submitted
        let canSendMessageVal = canSendMessageText();
        if(canSendMessageVal === true && isListening === false) {
            return (
                <div
                className={'text-button'}
                onClick={onSendMessage}
                style={{
                    backgroundColor: Appearance.colors.primary(utils),
                    borderRadius: 17.5,
                    marginLeft: 8
                }}>
                    <img 
                    src={'images/ai-send-button-clear.png'}
                    style={{
                        height: 35,
                        width: 35
                    }} />
                </div>
            )
        }

        // determine if voice recognition can be started or ended
        return (
            <div
            className={'text-button'}
            onClick={onVoiceRecognitionClick}
            style={{
                borderRadius: 17.5,
                marginLeft: 8
            }}>
                <img 
                src={isListening ? 'images/ai-voice-icon-stop.png' : 'images/ai-voice-icon.png'}
                style={{
                    height: 35,
                    width: 35
                }} />
            </div>
        )
    }

    const getSamplePromptComponents = () => {
        
        let prompts = [{
            key: 'how_does_lifewipe_work',
            title: 'Walk me through how LifeWipe works.'
        },{
            key: 'emergency_trigger_setup',
            title: 'Help me set up my Emergency Trigger.'
        },{
            key: 'data_vault_upload_support',
            title: 'How do I upload files to the Data Vault?'
        },{
            key: 'compare_plan_options',
            title: 'Compare subscription plan options side-by-side.'
        },{
            key: 'linked_account_setup',
            title: 'Show me how to secure my passwords and logins.'
        },{
            key: 'setup_family_plan',
            title: 'Can I use this for someone else in my family?'
        }];

        return prompts.map((prompt, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onPromptClick.bind(this, prompt.key)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== prompts.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        flexGrow: 1
                    }}>{prompt.title}</span>
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 12,
                        marginLeft: 12,
                        objectFit: 'contain',
                        width: 10
                    }} />
                </div>
            )
        })
    }

    const getSubmissionProps = () => {
        return {
            auth: { 
                token: user?.token 
            },
            context: {
                locale: window.navigator.language || 'en-US',
                timezone: moment.tz.guess()
            },
            id: chatIDRef.current, 
            origin: 'lifewipe_website'
        }
    }

    const getTextInput = () => {
        return (
            <animated.div
            ref={ref => {
                setTextContainerHeight(ref?.clientHeight || 0);
            }}
            style={{
                backgroundColor: Appearance.colors.layerBackground(),
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                left: 0,
                position: Utils.isMobile() ? 'relative' : 'absolute',
                right: 0,
                ...textInputAnimations
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '16px 16px 8px 16px',
                    width: '100%'
                }}>
                    <textarea
                    disabled={loading === true}
                    onChange={evt => setText(evt.target.value)}
                    onKeyDown={evt => {
                        if(evt.key === 'Enter' && !evt.shiftKey) {
                            evt.preventDefault();
                            if(text && text.trim().length > 0 && loading === false) {
                                onSendMessage();
                            }
                        }
                    }}
                    placeholder={isListening ? 'Listening...' : 'Ask me anything...'}
                    ref={textAreaRef}
                    rows={1}
                    value={text || ''}
                    style={{
                        backgroundColor: loading === true ? Appearance.colors.softBorder() : Appearance.colors.textField(),
                        border: `1px solid ${Appearance.colors.divider()}`,
                        borderRadius: 20,
                        color: Appearance.colors.text(),
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '24px',
                        maxHeight: 200,
                        minHeight: 40,
                        outline: 'none',
                        overflow: 'hidden',
                        padding: '8px 16px',
                        resize: 'none',
                        width: '100%',
                    }}
                    onInput={evt => {
                        evt.target.style.height = 'auto';
                        evt.target.style.height = Math.min(evt.target.scrollHeight, 200) + 'px';
                    }} />
                    {getPrimaryButton()}
                </div>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                    paddingLeft: 16,
                    paddingRight: 16
                }}>
                    <span style={{
                        color: Appearance.colors.subText(),
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.75,
                        textAlign: 'center'
                    }}>{'Requiem can make mistakes. Check important info.'}</span>
                </div>
            </animated.div>
        )
    }

    const canSendMessageText = () => {
        return loading === true || !text || text?.length === 0 ? false : true;
    }

    const runChatSetup = () => {
        textInputAnimationsApi.start({
            bottom: 0,
            opacity: 1
        });
    }

    const scrollToBottom = () => {
        scrollContainer.current?.scrollTo({
            behavior: 'smooth',
            top: scrollContainer.current.scrollHeight
        });
    }

    const setupSpeechRecognition = () => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {

            // setup and configure recognition class
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = window.navigator.language || 'en-US';
            
            // register onstart and onend handler for state updates
            recognition.onstart = setIsListening.bind(this, true);
            recognition.onend = setIsListening.bind(this, false);

            // register result handler
            recognition.onresult = evt => {

                // prepare transcript variable
                let transcript = '';

                // loop through results and build complete transcript
                for(let i = 0; i < evt.results.length; i++) {
                    transcript += evt.results[i][0].transcript;
                }
                
                // update text field with current transcript (both interim and final)
                setText(transcript);
            }

            // register error handler
            recognition.onerror = evt => {

                // update state with ending value
                console.error('Speech recognition error:', evt.error);
                setIsListening(false);

                // show an error if anything other than a cancellation took place
                if(evt.error !== 'aborted') {
                    utils.alert.show({
                        title: 'Speech Recognition Error',
                        message: `Unable to recognize speech: ${evt.error}. Please try again.`
                    });
                }
            };

            // set recognition instance within local state
            setRecognition(recognition);
        }
    }

    const setupWebSockets = () => {

        // run requiem chat setup for ui elements
        runChatSetup();

        // subscribe to event for new chat completion responses
        sockets.on('requiem', 'new_chat_completion_response', onNewChatCompletionResponse);
        sockets.on('requiem', 'chat_stream_chunk', onNewChatCompletionStreamResponse);
        console.log('[requiem]: setup complete');

        // return unsubscribe function
        return () => {
            sockets.off('requiem', 'new_chat_completion_response', onNewChatCompletionResponse);
            sockets.off('requiem', 'chat_stream_chunk', onNewChatCompletionStreamResponse);
        }
    };

    const submitRequest = (input, props) => {

        // emit message data to server
        sockets.send('requiem', 'new_chat_completion_message', {
            ...getSubmissionProps(),
            message: input,
            ...props
        });
    }

    useEffect(() => {
        setTimeout(() => {
            scrollToBottom();
            if(loading === false) {
                textAreaRef.current?.focus();
            }
        }, 250);
    }, [loading]);

    useEffect(() => {

        let state = Utils.isMobile() ? mobileVisibilityState: visibilityState;
        askButtonAnimationsApi.start({
            bottom: state ? -150 : 0
        });
        if(state === true) {
            setTimeout(scrollToBottom, 250);
        }
        window.document.addEventListener('click', onMobileBodyClick);
        return () => {
            window.document.removeEventListener('click', onMobileBodyClick);
        }
    }, [mobileVisibilityState, visibilityState]);

    useEffect(() => {
        onMessagesChange();
    }, [messages]);

    useEffect(() => {
        requiem.message && onProcessIncomingRequiemMessage();
    }, [requiem.message]);

    useEffect(() => {
        requiem.request && onProcessIncomingRequiemRequest();
    }, [requiem.request]);

    useEffect(() => {
        if(sockets.connected) {
            return setupWebSockets();
        }
    }, [sockets.connected]);

    useEffect(() => {
        setupSpeechRecognition();
    }, []);

    return (
        <>
        {getAskRequiemButton()}
        {getContent()}
        </>
    )
}

export const RequiemChatHistory = ({ utils }) => {

    const limit = 5;
    const offset = useRef(0);

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const { setRequiem } = useDataStore();

    const onDeleteConversationClick = (entry, evt) => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Remove Conversation',
            message: `Are you sure you want to remove the conversation titled "${entry.title || 'Untitled Conversation'}"? This action cannot be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: async key => {
                if(key === 'confirm') {
                    setTimeout(onDeleteConversationConfirm.bind(this, entry), 500);
                    return;
                }
            }
        });
    }

    const onDeleteConversationConfirm = async entry => {
        try {   

            // set loading flag and send request to server
            setLoading(true);
            await Request.post(utils, '/requiem/', {
                id: entry.id,
                type: 'remove_conversation'
            });

            // end loading and notify requiem that conversation was removed
            setLoading(false);
            setRequiem(requiem => {
                requiem.request = {
                    event: 'conversation_removed',
                    id: entry.session_id
                }
                return requiem;
            });

            // show confirmation banner
            utils.banner.show({
                title: 'Requiem Conversation Removed',
                message: `The conversation titled "${entry.title || 'Untitled Conversation'}" has been successfully removed from your account.`,
                onShow: fetchEntries
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue removing your conversation. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_chat_history.delete_click',
                title: 'Oops!'
            });
        }
    }

    const onEntryClick = entry => {
        setRequiem(requiem => ({ 
            ...requiem,
            request: {
                action: 'open',
                session_id: entry.session_id
            }
        }));
    }

    const getEntries = () => {
        if(entries.length === 0) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    borderWidth: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/requiem-chat-history-dark.png' : 'images/requiem-chat-history-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Conversations Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'We were unable to locate any Requiem conversations for your account.'}</span>
                    </div>
                </div>
            )
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {entries.map((entry, index) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        key={index}
                        onClick={onEntryClick.bind(this, entry)}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== entries.length - 1 && Appearance.styles.bottomBorder(),
                            display: 'flex',
                            flexDirection: 'row',
                            minWidth: 0,
                            padding: 16,
                            width: '100%'
                        }}>
                            <img 
                            className={'d-none d-xl-flex'}
                            src={window.theme === 'dark' ? 'images/requiem-chat-history-dark.png' : 'images/requiem-chat-history-light.png'}
                            style={{
                                height: 50,
                                marginRight: 16,
                                objectFit: 'contain',
                                width: 50
                            }} />
                            <div style={{
                                alignItems: 'flex-start',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                minWidth: 0
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots'
                                }}>{(entry.title || 'Untitled Conversation').toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    color: Appearance.colors.subText()
                                }}>{Utils.format.date(entry.date)}</span>
                            </div>
                            <img 
                            className={'text-button'}
                            onClick={onDeleteConversationClick.bind(this, entry)}
                            src={'images/delete-red-icon.png'}
                            style={{
                                height: 25,
                                marginLeft: 12,
                                objectFit: 'contain',
                                width: 20
                            }} />
                        </div>
                    )
                })}
            </div>
        )
    }

    const fetchEntries = async () => {
        try {
            
            let { entries, paging } = await Request.get(utils, '/requiem/', {
                limit: limit,
                offset: offset.current,
                type: 'conversations'
            });

            setLoading(false);
            setEntries(entries.map(entry => ({
                ...entry,
                date: moment.utc(entry.date).local()
            })));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving your conversations list. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_chat_history.fetch_entries',
                title: 'Oops!'
            });
        }
    }

    useEffect(() => {
        fetchEntries();
        utils.content.subscribe('requiem_chat_history', ['requiem_conversations'], {
            onFetch: fetchEntries
        });
        return () => {
            utils.content.unsubscribe('requiem_chat_history');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Each Requiem AI session is saved so you can revisit insights, pick up unfinished planning, or share findings with your trusted contacts. These past conversations include searchable summaries of your data vault, estate directives, and file-specific flags. Use this space to track your digital footprint over time and ensure nothing is left unresolved.`}
                loading={loading}
                title={'My Conversations'}>
                    {getEntries()}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchEntries();
                        }}
                        style={{
                            borderTop: null,
                            marginTop: 16
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const RequiemChatPanel = ({ utils }) => {

    const { setRequiem } = useDataStore();

    const onStartConversationClick = () => {
        setRequiem(requiem => ({ 
            ...requiem,
            request: {
                action: 'new_conversation'
            }
        }));
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'contact',
            onClick: onStartConversationClick,
            text: 'Start A Conversation'
        }];
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Requiem AI is your digital legacy assistant—designed to help you make sense of what’s stored, what matters, and what should happen next. It scans your encrypted vault, identifies key documents, and provides plain-language summaries to support your estate planning. Whether you're preparing for the future or responding to an emergency, Requiem helps turn scattered data into clear decisions. Every insight is filtered through your preferences, privacy settings, and legacy goals.`}
                buttons={getContainerButtons()}
                leftContent={(
                    <video
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: '100%'
                    }}>
                        <source 
                        src={"images/requiem-ai.mp4"}
                        type={"video/mp4"} />
                    </video>
                )}
                title={'Requiem AI'} />
            </AnimatedElement>
        </div>
    )
}

export const RequiemUserKnowledgeChat = ({ token }, { index, options, utils }) => {

    const layerID = 'requiem_user_knowledge_chat';
    const messagesRef = useRef([]);
    const scrollContainer = useRef(null);

    const [splineAnimations, splineAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        transform: 'scale(0.75)'
    }));
    const [textInputAnimations, textInputAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        bottom: -250,
        opacity: 0
    }));

    const [chatID, setChatID] = useState(null);
    const [headPos, setHeadPos] = useState([0, 1.6, 0])
    const [isListening, setIsListening] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [recognition, setRecognition] = useState(null);
    const [text, setText] = useState(null);
    const [textContainerHeight, setTextContainerHeight] = useState(0);
    const { requiem, sockets, setRequiem } = useDataStore();

    const onMessagesChange = () => {

        // update ref with new content
        //console.log(messages);
        messagesRef.current = messages || [];

        // scroll to bottom of the messages container
        scrollContainer.current?.scrollTo({
            behavior: 'smooth',
            top: scrollContainer.current.scrollHeight
        });
    }

    const onNewChatCompletionResponse = evt => {

        // end loading and set chat id within local state
        setLoading(false);
        setChatID(evt.id);

        // no further logic is require if no messages were returned
        if(!evt.messages) {
            setLoading(false);
            setMessages(messages => {
                messages.pop();
                return messages;
            });
            return;
        }

        // prepare message targets for state update
        let targets = evt.messages.map(message => {
            try {
                if(message.role === 'assistant' && message.content) {
                    message.content = JSON.parse(message.content);
                }
            } catch(e) {
                console.error(e.message);
            }
            return message;
        });

        // update messages state with new messages
        setMessages(targets);

        // determine if most recent message contains any client-side actions
        let target = targets[targets.length - 1];
        (target.content?.actions || []).forEach(action => {
            switch(action) {
                case 'navigate': 
                onProcessNavigationAction(target.content.navigation || {});
                break;

                default:
                console.warn(`[requiem]: unsupported action ${action}`);
            }
        });
    }

    const onProcessNavigationAction = ({ target, target_action }) => {
        
        // prepare navigation key using target variable, this normally lines up with internal routes
        let key = target;
        switch(target) {

            case 'linked_accounts':
            key = 'accounts';
            break;

            case 'life_checks':
            key = 'lifechecks_history';
            break;
        }

        // navigation to target view with target action
        console.log(`[requiem]: navigating to ${key} with action ${target_action}`);
        utils.navigation.set({ view: key });

        // update requiem state object with details about this action
        setRequiem({ ...requiem, action: { key: target_action, target }});
    }

    const onSendMessage = async props => {
        try {

            let isFirstMessage = messages.length === 0;

            // clear text from field and set loading flag
            setLoading(true);
            setText(null);

            // update layout for spline animation if applicable
            if(isFirstMessage === true) {
                updateSplineAnimationSize();
            }

            // add user message to list of messages
            onUpdateMessages([{
                content: text,
                role: 'user',
                visible: true
            }]);

            // sleep briefly before adding a "thinking" message for the chat completion
            await Utils.sleep(0.5);
            onUpdateMessages([{
                loading: true,
                role: 'assistant',
                visible: true
            }]);

            // prepare and submit request to server
            submitRequest(text, { edits: props });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `An error occurred while processing your request. ${e.message || 'An unknown error occurred'}.`,
                origin: 'requiem_chat.edit_message',
                title: 'Oops!'
            });
        }
    }

    const onUpdateMessages = targets => {
        messagesRef.current = messagesRef.current.concat(targets);
        setMessages(messagesRef.current);
    }

    const onVoiceRecognitionClick = () => {

        // no additional logic is required if browser does not support speech recognition
        if(!recognition) {
            utils.alert.show({
                title: 'Speech Recognition Not Supported',
                message: 'Your browser does not support speech recognition. Please type your message instead.'
            });
            return;
        }

        // toggle speech recognition
        isListening ? recognition.stop() : recognition.start();
    }

    const getContent = () => {
        return (
            <div style={{
                position: 'relative',
                width: '100%'
            }}>
                <div style={{
                    padding: 16,
                    width: '100%'
                }}>
                    <div 
                    ref={scrollContainer}
                    style={{
                        maxHeight: 'calc(100vh - 160px)',
                        overflowY: 'scroll',
                        paddingBottom: textContainerHeight,
                        width: '100%'
                    }}>
                        {getSplineAnimation()}
                        {getMessages()}
                    </div>
                    {getTextInput()}
                </div>
                <div style={{
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0) 100%)',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    padding: '24px 24px 24px 24px',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    width: '100%'
                }}>
                    <img 
                    className={'text-button'}
                    onClick={setLayerState.bind(this, 'close')}
                    src={window.theme === 'dark' ? 'images/close-button-dark.png' : 'images/close-button-light.png'}
                    style={{
                        height: 25,
                        justifySelf: 'flex-end',
                        objectFit: 'contain',
                        width: 25
                    }} />
                </div>
            </div>
        )
    }

    const getMessages = () => {

        let targets = messages.filter(message => message.visible !== false);
        return targets.length > 0 && (
            <div 
            className={'requiem-messages'}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: 16,
                width: '100%'
            }}>
                {targets.map((message, index, messages) => {
                    return (
                        <RequiemMessage 
                        data={message} 
                        key={index}
                        index={index}
                        isLastItem={index === messages.length - 1}
                        loading={loading === index || message.loading === true}
                        utils={utils} />
                    )
                })}
            </div>
        )
    }

    const getPrimaryButton = () => {

        // determine if a text input can be submitted
        let canSendMessageVal = canSendMessageText();
        if(canSendMessageVal === true && isListening === false) {
            return (
                <div
                className={'text-button'}
                onClick={() => {
                    setLoading('textfield');
                    onSendMessage();
                }}
                style={{
                    backgroundColor: Appearance.colors.primary(utils),
                    borderRadius: 17.5,
                    marginLeft: 8
                }}>
                    <img 
                    src={'images/ai-send-button-clear.png'}
                    style={{
                        height: 35,
                        width: 35
                    }} />
                </div>
            )
        }

        // determine if voice recognition can be started or ended
        return (
            <div
            className={'text-button'}
            onClick={onVoiceRecognitionClick}
            style={{
                borderRadius: 17.5,
                marginLeft: 8
            }}>
                <img 
                src={isListening ? 'images/ai-voice-icon-stop.png' : 'images/ai-voice-icon.png'}
                style={{
                    height: 35,
                    width: 35
                }} />
            </div>
        )
    }

    const getSplineAnimation = () => {
        let size = getSplineAnimationSize();
        return (
            <animated.div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: size,
                marginTop: 24,
                overflow: 'hidden',
                padding: 24,
                width: '100%',
                ...splineAnimations
            }}>
                <div style={{
                    border: `5px solid ${Appearance.colors.divider()}`,
                    borderRadius: size / 2,
                    height: '100%',
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    {/*<Canvas 
                    camera={{ position: [0, 1.6, 2.5], fov: 13 }}
                    onError={error => {
                        console.error(error.message)
                    }}>
                        <ambientLight intensity={0.5} />
                        <directionalLight position={[0, 5, 5]} intensity={2.5} castShadow />
                        <pointLight position={[-2, 2, 4]} intensity={0.7} />
                        <spotLight position={[2, 5, 2]} angle={0.3} intensity={1} />
                        <OrbitControls target={headPos} />
                        <Avatar url={'model.glb'} onHeadReady={obj => {
                            const pos = new Vector3();
                            obj.getWorldPosition(pos);
                            if(!headPos) {
                                console.log(pos);
                                setHeadPos([pos.x, pos.y, pos.z])
                            }
                        }}/>
                    </Canvas>*/}
                </div>
            </animated.div>
        )
    }

    const getSplineAnimationSize = () => {  
        return Utils.isMobile() ? window.innerWidth : 512;
    }

    const getTextInput = () => {
        return (
            <animated.div
            ref={ref => {
                setTextContainerHeight(ref?.clientHeight || 0);
            }}
            style={{
                backgroundColor: Appearance.colors.layerBackground(),
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                left: 0,
                position: Utils.isMobile() ? 'relative' : 'absolute',
                right: 0,
                ...textInputAnimations
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '16px 16px 8px 16px',
                    width: '100%'
                }}>
                    <textarea
                    disabled={loading === true}
                    onChange={evt => setText(evt.target.value)}
                    onKeyDown={evt => {
                        if(evt.key === 'Enter' && !evt.shiftKey) {
                            evt.preventDefault();
                            if(text && text.trim().length > 0 && !loading) {
                                onSendMessage();
                            }
                        }
                    }}
                    placeholder={isListening ? 'Listening...' : 'Ask me anything...'}
                    rows={1}
                    value={text || ''}
                    style={{
                        backgroundColor: loading === true ? Appearance.colors.softBorder() : Appearance.colors.textField(),
                        border: `1px solid ${Appearance.colors.divider()}`,
                        borderRadius: 20,
                        color: Appearance.colors.text(),
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '24px',
                        maxHeight: 200,
                        minHeight: 40,
                        outline: 'none',
                        overflow: 'hidden',
                        padding: '8px 16px',
                        resize: 'none',
                        width: '100%',
                    }}
                    onInput={evt => {
                        evt.target.style.height = 'auto';
                        evt.target.style.height = Math.min(evt.target.scrollHeight, 200) + 'px';
                    }} />
                    {getPrimaryButton()}
                </div>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                    paddingLeft: 16,
                    paddingRight: 16
                }}>
                    <span style={{
                        color: Appearance.colors.subText(),
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.75,
                        textAlign: 'center'
                    }}>{'Requiem can make mistakes. Check important info.'}</span>
                </div>
            </animated.div>
        )
    }

    const canSendMessageText = () => {
        return loading === 'submit' || !text || text?.length === 0 ? false : true;
    }

    const runChatSetup = () => {

        // run animations for spline component
        setTimeout(() => {
            splineAnimationsApi.start({
                opacity: 1,
                transform: 'scale(1)'
            });
        }, 1000);
        
        
        // run animations for text input component
        setTimeout(() => {
            textInputAnimationsApi.start({
                bottom: 0,
                opacity: 1
            });
        }, 1250);
    }

    const setupSpeechRecognition = () => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {

            // setup and configure recognition class
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = window.navigator.language || 'en-US';
            
            // register onstart and onend handler for state updates
            recognition.onstart = setIsListening.bind(this, true);
            recognition.onend = setIsListening.bind(this, false);

            // register result handler
            recognition.onresult = evt => {

                // prepare transcript variable
                let transcript = '';

                // loop through results and build complete transcript
                for(let i = 0; i < evt.results.length; i++) {
                    transcript += evt.results[i][0].transcript;
                }
                
                // update text field with current transcript (both interim and final)
                setText(transcript);
            }

            // register error handler
            recognition.onerror = evt => {

                // update state with ending value
                console.error('Speech recognition error:', evt.error);
                setIsListening(false);

                // show an error if anything other than a cancellation took place
                if(evt.error !== 'aborted') {
                    utils.alert.show({
                        title: 'Speech Recognition Error',
                        message: `Unable to recognize speech: ${evt.error}. Please try again.`
                    });
                }
            };

            // set recognition instance within local state
            setRecognition(recognition);
        }
    }

    const setupWebSockets = () => {

        // run requiem chat setup for ui elements
        runChatSetup();

        // subscribe to event for new user knowledge responses
        sockets.on('requiem', 'new_user_knowledge_response', onNewChatCompletionResponse);
        console.log('[requiem]: setup complete');

        // send wake up message to model
        sockets.send('requiem', 'new_user_knowledge_message', {
            auth: { token },
            context: {
                locale: window.navigator.language || 'en-US',
                timezone: moment.tz.guess()
            }, 
            mode: 'user_knowledge',
            origin: 'lifewipe_website'
        });

        // return unsubscribe function
        return () => {
            sockets.off('requiem', 'new_user_knowledge_response', onNewChatCompletionResponse);
        }
    };

    const submitRequest = (input, props) => {

        // emit message data to server
        sockets.send('requiem', 'new_user_knowledge_message', {
            auth: { token },
            context: {
                locale: window.navigator.language || 'en-US',
                timezone: moment.tz.guess()
            },
            id: chatID, 
            message: input,
            mode: 'user_knowledge',
            origin: 'lifewipe_website',
            ...props
        });
    }

    const updateSplineAnimationSize = () => {
        let size = getSplineAnimationSize();
        splineAnimationsApi.start({
            from: {
                height: size
            },
            to: { 
                height: size * 0.75
            }
        });
    }

    useEffect(() => {
        onMessagesChange();
    }, [messages]);

    useEffect(() => {
        if(sockets.connected) {
            return setupWebSockets();
        }
    }, [sockets.connected]);

    useEffect(() => {
        setupSpeechRecognition();
    }, []);

    return (
        <Layer 
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            removePadding: true
        }}>
            {getContent()}
        </Layer>
    )
}

const RequiemMessage = ({ data, expanded = true, files = [], isLastItem = false, onClick, onDataChange, utils }) => {

    const formatText = input => {

        if(!input) {
            return null;
        }

        // if the text contains literal escaped newlines (\n), convert them to real newlines
        let normalized = input.replace(/\\n/g, '\n');

        const tokens = [];
        let remaining = normalized;

        // order matters: images and links first, then headings, then bold before italic, then inline styles and newlines
        const patterns = [
            { type: "image", regex: /!\[([^\]]*?)\]\(([^)]+)\)/s },
            { type: "link", regex: /\[([^\]]+?)\]\(([^)]+)\)/s },
            { type: "heading1", regex: /^####\s+(.+?)$/m },
            { type: "heading2", regex: /^###\s+(.+?)$/m },
            { type: "heading3", regex: /^##\s+(.+?)$/m },
            { type: "heading4", regex: /^#\s+(.+?)$/m },
            { type: "bold", regex: /\*\*([^*]+?)\*\*/s },
            { type: "italic", regex: /(?<!\*)\*([^*]+?)\*(?!\*)|_([^_]+?)_/s },
            { type: "code", regex: /`([^`]+)`/s },
            { type: "double-newline", regex: /\n\n+/ },
            { type: "newline", regex: /\n+/ }
        ];

        function pushText(text) {
            if(text) tokens.push({ type: "text", value: text });
        }

        // main parsing loop
        while(remaining.length > 0) {

            let earliest = null;
            let earliestIndex = Infinity;
            let earliestPattern = null;

            // find earliest formatting match
            for(const p of patterns) {
                const match = p.regex.exec(remaining);
                if (match && match.index < earliestIndex) {
                    earliest = match;
                    earliestIndex = match.index;
                    earliestPattern = p;
                }
            }

            // no more formatting — push remaining text
            if(!earliest) {
                pushText(remaining);
                break;
            }

            // push leading text before formatting token
            pushText(remaining.substring(0, earliest.index));

            // handle each pattern type
            switch (earliestPattern.type) {
                case 'heading1':
                tokens.push({ type: 'heading1', value: earliest[1] });
                break;

                case 'heading2':
                tokens.push({ type: 'heading2', value: earliest[1] });
                break;

                case 'heading3':
                tokens.push({ type: 'heading3', value: earliest[1] });
                break;

                case 'bold':
                tokens.push({ type: 'bold', value: earliest[1] });
                break;

                case 'italic':
                tokens.push({ type: 'italic', value: earliest[1] || earliest[2] });
                break;

                case 'code':
                tokens.push({ type: 'code', value: earliest[1] });
                break;

                case 'double-newline':
                tokens.push({ type: 'double-newline' });
                break;

                case 'newline':
                tokens.push({ type: 'newline' });
                break;

                default:
                pushText(earliest[0]);
            }

            // move cursor forward
            remaining = remaining.slice(earliest.index + earliest[0].length);
        }

        return (
            <>
            {tokens.map((token, i) => {
                switch (token.type) {
                    case "heading1":
                    case "heading2":
                    case "heading3":
                    case "heading4":
                    return (
                        <span 
                        key={i} 
                        style={{ 
                            display: 'block',
                            fontSize: 16,
                            fontWeight: 800
                        }}>
                            {token.value}
                        </span>
                    );

                    case "bold":
                    return (
                        <span key={i} style={{ fontWeight: 700 }}>{token.value}</span>
                    );

                    case "italic":
                    return (
                        <span key={i} style={{ fontStyle: 'italic' }}>{token.value}</span>
                    );

                    case "newline":
                    return <br key={i} />;

                    case "double-newline":
                    return <span key={i}><br /><br /></span>;

                    case "text":
                    return <span key={i}>{token.value}</span>;

                    default:
                    return <span key={i}>{token.value || ''}</span>;
                }
            })}
            </>
        );
    }

    const getContent = () => {
        if(data.visible === false) {
            return null;
        }
        if(data.loading === true) {
            return (
                <RequiemMessageContainer useLoader={true} />
            )
        }
        if(data.content) {
            if(data.role === 'user') {
                return (
                    <RequiemMessageContainer 
                    data={data}
                    expanded={expanded}
                    isLastItem={isLastItem}
                    onClick={onClick}
                    style={{
                        padding: '10px 16px 10px 16px'
                    }}
                    utils={utils}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            color: 'white',
                            fontWeight: 500,
                            whiteSpace: 'normal',
                        ...getMobileExpandedClampProps()
                        }}>{data.content || ''}</span>
                    </RequiemMessageContainer>
                )
            }
            return data.content?.length > 0 && (
                <RequiemMessageContainer 
                data={data}
                expanded={expanded}
                files={files}
                isLastItem={isLastItem}
                onClick={onClick}
                onDataChange={onDataChange}
                style={{
                    padding: '10px 16px 10px 16px',
                    width: '100%'
                }}
                utils={utils}>
                    <span style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.text(),
                        fontWeight: 500,
                        whiteSpace: 'normal',
                        ...getMobileExpandedClampProps()
                    }}>{formatText(data.content)}</span>
                </RequiemMessageContainer>
            )
        }
    }

    const getMobileExpandedClampProps = () => {
        return expanded === false && {
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical'
        }
    }

    return getContent();
}

const RequiemMessageContainer = ({ children, data = {}, files = [], isLastItem = false, useLoader = false, onClick, onDataChange, style, utils }) => {

    const fileInputRef = useRef(null);

    const [animations, animationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 18 },
        opacity: 0,
        ...data.role === 'user' && {
            right: '-50%',
        },
        ...data.role !== 'user' && {
            left: '-50%',
        }
    }));
    const [loading, setLoading] = useState(false);
    const { setRequiem, setUser } = useDataStore();

    const onDataVaultItemClick = async (config, file) => {

        const onConfirm = () => {

            // prepare new data vault item
            let item = DataVaultItem.create({
                ai_enabled: config.ai_enabled,
                metadata: {
                    analyze_communication_style: config.analyze_communication_style, 
                    description: config.description 
                },
                name: config.name,
                user_id: config.user_id
            });
            
            // close requiem for mobile devices
            closeRequiemForMobile();

            // open editing layer for new target
            utils.layer.open({
                abstract: Abstract.create({
                    object: item,
                    type: 'data_vault_items'
                }),
                Component: AddEditDataVaultItem.bind(this, { 
                    fileTarget: file,
                    isNewTarget: true 
                }),
                id: `new_data_vault_item`
            });
        }

        // automatically move to confirmation if a local file handle was found
        if(file) {
            return onConfirm();
        }

        // show alert explaining the need for a new file handle
        utils.alert.show({
            title: 'File Required',
            message: 'It looks like you need to reselect the file from your device. This can happen if you refresh your page or if you opened an older Requiem conversation.',
            buttons: [{
                key: 'confirm',
                title: 'Browse Files',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    fileInputRef.current?.click();
                    return;
                }
            }
        });
    }

    const onEmergencyTriggerReviewClick = async (subscriptionID, resourceID) => {
        try {

            // fetch details for subscription
            setLoading(resourceID);
            let subscription = await Subscription.get(utils, subscriptionID);

            // update user object with subscription details
            setUser(user => {
                user.onboarding.has_subscription = true;
                user.subscription = subscription;
                return user;
            });

            // verify that user has access to this feature
            await utils.user.capabilities.verify('emergency_triggers', 'create');

            // close requiem for mobile devices
            closeRequiemForMobile();

            // end loading and show layer with options to configure triggers
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: subscription,
                    type: 'subscriptions'
                }),
                Component: ConfigureTriggers.bind(this, { category: 'emergency_triggers' }),
                id: `emergency_triggers.${subscriptionID}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving your Emergency Trigger setup. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_user_knowledge_chat.configure_emergency_triggers',
                title: 'Oops!'
            });
        }
    }

    const onInstructionReviewClick = async (id, resourceID) => {
        try {

            // fetch details for instruction
            setLoading(resourceID);
            let instruction = await Instruction.get(utils, id);

            // notify subscribers that new content may be available
            utils.content.fetch('trigger_instructions');

            // close requiem for mobile devices
            closeRequiemForMobile();

            // end loading and show layer with options to review instruction
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: instruction,
                    type: 'trigger_instructions'
                }),
                Component: AddEditInstruction.bind(this, { 
                    isNewTarget: false
                }),
                id: `trigger_instructions.${id}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving your instruction details. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_user_knowledge_chat.instruction_review_click',
                title: 'Oops!'
            });
        }
    }

    const onLifeChecksReviewClick = async (subscriptionID, resourceID) => {
        try {

            // fetch details for subscription
            setLoading(resourceID);
            let subscription = await Subscription.get(utils, subscriptionID);

            // update user object with subscription details
            setUser(user => {
                user.onboarding.has_subscription = true;
                user.subscription = subscription;
                return user;
            });

            // close requiem for mobile devices
            closeRequiemForMobile();

            // end loading and show layer with options to configure triggers
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: subscription,
                    type: 'subscriptions'
                }),
                Component: ConfigureTriggers.bind(this, { category: 'life_checks' }),
                id: `life_checks.${subscriptionID}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving your LifeChecks setup. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_user_knowledge_chat.life_checks_review_click',
                title: 'Oops!'
            });
        }
    }

    const onLinkedAccountClick = async (id, resourceID) => {
        try {

            // fetch details for account
            setLoading(resourceID);
            let account = await Account.get(utils, id);

            // notify subscribers that new content may be available
            utils.content.fetch('accounts');

            // close requiem for mobile devices
            closeRequiemForMobile();

            // end loading and show layer with options to configure triggers
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: account,
                    type: 'accounts'
                }),
                Component: AddEditAccount.bind(this, { 
                    isNewTarget: false
                }),
                id: `linked_accounts.${id}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving your linked account setup. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_user_knowledge_chat.linked_account_click',
                title: 'Oops!'
            });
        }
    }

    const onNewAccountReviewClick = data => {

        // create user object with review data
        let user = User.new();
        user.email_address = data.email_address;
        user.first_name = data.first_name;
        user.last_name = data.last_name;
        user.phone_number = data.phone_number;

        // close requiem for mobile devices
        closeRequiemForMobile();

        // open user layer to complete the flow
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: true,
                onComplete: () => {
                    let next = update(data, {
                        content: {
                            meta: {
                                complete_user_account_signup_data: { 
                                    complete: {
                                        $set: true
                                    }
                                }
                            }
                        }
                    });
                    onDataChange(next);
                }
            }),
            id: 'new_user'
        });
    }

    const onPlanCheckoutClick = async props => {
        try {

            // fetch list of available tiers and find the requested plan
            let { tiers } = await Request.get(utils, '/subscriptions/', { type: 'available_tiers' });
            let target = tiers.find(tier => tier.id === props.plan_id);
            
            // throw an error if no plan was found
            if(!target) {
                throw new Error('The requested subscription plan could not be found. Please try again.');
            }
            
            // close requiem for mobile devices
            closeRequiemForMobile();

            // open layer with selections to complete checkout flow
            utils.layer.open({
                Component: NewSubscription.bind(this, {
                    freeTrial: true,
                    onComplete: () => {
                        let next = update(data, {
                            content: {
                                meta: {
                                    complete_user_plan_checkout: { 
                                        complete: {
                                            $set: true
                                        }
                                    }
                                }
                            }
                        });
                        onDataChange(next);
                    },
                    selection: target
                }),
                id: 'new_subscription'
            });

        } catch(e) {
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving the details for this subscription. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_user_knowledge_chat.subscription_review_click',
                title: 'Oops!'
            });
        }
    }

    const onPromptClick = action => {

        // determine if action represents a consultation booking request
        if(action === 'book_consultation') {

            // log event with firebase
            utils.analytics.log('book_consultation_click', { origin: 'requiem_ui_action' });

            // open layer for consultation booking
            utils.layer.open({
                Component: BookConsultation,
                id: 'book_consultation'
            });
            return;
        }

        // determine if action represents a subscription signup request
        if(action === 'start_free_no_credit_card') {

            // log event with firebase
            utils.analytics.log('start_free_no_credit_card_click', { origin: 'requiem_ui_action' });

            // open layer for new subscription signup
            utils.layer.open({
                Component: NewSubscription.bind(this, { 
                    freeTrial: true,
                    showStarterPlan: true 
                }),
                id: 'new_subscription'
            });
            return;
        }

        // offload action to requiem for processing
        setRequiem(requiem => {
            requiem.request = { 
                loading: true,
                metadata: data.client_metadata?.payload,
                preset: action 
            }
            return requiem;
        });
    }

    const onTrustedContactClick = async (id, resourceID) => {
        try {

            // fetch details for trusted contact
            setLoading(resourceID);
            let contact = await TrustedContact.get(utils, id);

            // notify subscribers that new content may be available
            utils.content.fetch('trusted_contacts');

            // close requiem for mobile devices
            closeRequiemForMobile();

            // end loading and show layer with options to configure triggers
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: contact,
                    type: 'trusted_contacts'
                }),
                Component: AddEditTrustedContact.bind(this, { 
                    isNewTarget: false
                }),
                id: `trusted_contacts.${id}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue retrieving your trusted contact details. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_user_knowledge_chat.trusted_contact_review_click',
                title: 'Oops!'
            });
        }
    }

    const getAccessory = () => {
        let content = getAccessoryContent();
        return content !== null && (
            <div 
            className={'p-3'}
            style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.textField(),
                border: `1px solid ${Appearance.colors.divider()}`,
                borderRadius: 15,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                marginBottom: 24,
                marginTop: 24,
                textAlign: 'center',
                width: '100%'
            }}>
                <img 
                className={'mb-3'}
                src={content.icon}
                style={{
                    height: 75,
                    objectFit: 'contain',
                    width: 75
                }} />
                <span style={{
                    ...Appearance.textStyles.header(),
                    fontFamily: 'Zen Dots',
                    marginBottom: 4
                }}>{content.title.toUpperCase()}</span>
                <span style={{
                    ...Appearance.textStyles.title(),
                    color: Appearance.colors.text(),
                    fontWeight: 500,
                    whiteSpace: 'normal'
                }}>{content.description}</span>
                {content.component}
                {content.complete !== true && (
                    <Button 
                    className={'mt-3'}
                    color={'primary'}
                    containerStyle={{
                        width: '100%'
                    }}
                    label={content.button_label}
                    loading={content.loading}
                    onClick={content.onClick}
                    type={'large'} />
                )}
            </div>
        )
    }

    const getAccessoryContent = () => {

        let { actions, complete_user_account_signup_data = {}, complete_user_plan_checkout, guided_action, payload } = data.client_metadata || {};

        // determine if an account confirmation is needed for new account signups
        if(complete_user_account_signup_data && actions?.includes('complete_user_account_signup') === true) {
            return {
                button_label: 'Complete Signup',
                complete: complete_user_account_signup_data.complete,
                description: 'Your account details are ready for review.',
                icon: `images/user-${window.theme}.png`,
                onClick: onNewAccountReviewClick.bind(this, complete_user_account_signup_data),
                title: 'Accounts'
            }
        }

        // determine if a checkout flow for a plan purchase was requested
        if(complete_user_plan_checkout && actions?.includes('complete_user_plan_checkout') === true) {
            return {
                button_label: `Continue With ${complete_user_plan_checkout.plan_title || 'Plan'}`,
                complete: complete_user_plan_checkout.complete,
                description: `We've recommended the ${complete_user_plan_checkout.plan_title || 'selected'} plan for you. Use the button below to start the checkout process.`,
                icon: `images/billing-${window.theme}.png`,
                onClick: onPlanCheckoutClick.bind(this, complete_user_plan_checkout),
                title: complete_user_plan_checkout.plan_title ? `${complete_user_plan_checkout.plan_title} Subscription` : 'New Subscription'
            }
        }

        // determine if an accessory is needed for guided action user input
        if(guided_action?.id === 'create_data_vault_file' && guided_action?.current_step_id === 'public_file_name') {
            return {
                button_label: 'Browse Files and Folders',
                component: getFileInput(file => {
                    setRequiem(requiem => {
                        requiem.request = {
                            action: 'file_upload',
                            metadata: {
                                execution_id: guided_action.execution_id,
                                file_name: file.name,
                                guided_action_id: guided_action.id,
                                mime_type: file.type,
                                step_id: guided_action.current_step_id
                            }
                        };
                        return requiem; 
                    });
                }),
                description: 'Choose a file to upload to your Data Vault.',
                icon: `images/data-vault-${window.theme}.png`,
                onClick: () => fileInputRef.current?.click(),
                title: 'Secure Data Vault'
            }
        }

        // determine if an explicit follow-up action was requested for an in-progress resource
        if(actions?.includes('explicit_user_action') === true) {
            switch(payload.category) {
                case 'data_vault_items': {
                    let file = files?.find(file => file.name === payload.config?.public_file_name);
                    return {
                        button_label: 'Complete Upload',
                        complete: payload.complete,
                        component: getFileInput(file => {
                            onDataVaultItemClick(payload.config, file);
                        }),
                        description: `Click to review and complete your file upload for ${payload.config?.public_file_name}.`,
                        icon: `images/data-vault-${window.theme}.png`,
                        onClick: onDataVaultItemClick.bind(this, payload.config, file),
                        title: 'Secure Data Vault'
                    }
                }
            }
        }

        // determine if a review was requested for a resource
        if(actions?.includes('explicit_user_review') === true) {

            // explode resource_id into category and id
            let resourceID = payload.resource_id;
            let [resource_type, id] = payload.resource_id.split('.');

            // determine which resource type to render
            switch(resource_type) {
                case 'emergency_triggers':
                return {
                    button_label: 'Review Setup',
                    complete: payload.complete,
                    description: 'Your configuration is ready to be reviewed.',
                    icon: `images/emergency-trigger-${window.theme}.png`,
                    loading: loading === resourceID,
                    onClick: onEmergencyTriggerReviewClick.bind(this, id, resourceID),
                    title: 'Emergency Trigger'
                }

                case 'trigger_instructions':
                return {
                    button_label: 'Review Instruction',
                    complete: payload.complete,
                    description: 'Your new trigger instruction is ready to be reviewed.',
                    icon: window.theme === 'dark' ? 'images/instructions-dark.png' : 'images/instructions-light.png',
                    loading: loading === resourceID,
                    onClick: onInstructionReviewClick.bind(this, id, resourceID),
                    title: payload.instruction_name || 'New Instruction'
                }

                case 'life_checks':
                return {
                    button_label: 'Review Setup',
                    complete: payload.complete,
                    description: 'Your configuration is ready to be reviewed.',
                    icon: `images/life-check-${window.theme}.png`,
                    loading: loading === resourceID,
                    onClick: onLifeChecksReviewClick.bind(this, id, resourceID),
                    title: 'LifeChecks'
                }

                case 'linked_accounts':
                return {
                    button_label: 'Review Linked Account',
                    complete: payload.complete,
                    description: 'Your linked account is ready to be finalized.',
                    icon: window.theme === 'dark' ? 'images/linked-accounts-dark.png' : 'images/linked-accounts-light.png',
                    loading: loading === resourceID,
                    onClick: onLinkedAccountClick.bind(this, id, resourceID),
                    title: payload.account_nickname || 'New Linked Account'
                }

                case 'trusted_contacts':
                return {
                    button_label: 'Review Contact Details',
                    complete: payload.complete,
                    description: 'Your new trusted contact is ready to be reviewed.',
                    icon: window.theme === 'dark' ? 'images/trusted-contact-dark.png' : 'images/trusted-contact-light.png',
                    loading: loading === resourceID,
                    onClick: onTrustedContactClick.bind(this, id, resourceID),
                    title: payload.full_name || 'New Trusted Contact'
                }

                default:
                return null;
            }
        }

        return null;
    }

    const getChildren = () => {
        return (
            <div style={{
                alignSelf: 'flex-start',
                backgroundColor: Appearance.colors.divider(),
                borderRadius: 15,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                lineHeight: 1.5,
                maxWidth: '100%',
                position: 'relative',
                wordBreak: 'break-word',
                ...style
            }}>
                {children}
            </div>
        )
    }

    const getContent = () => {
        if(useLoader === true) {
            return (
                <animated.div style={{
                    alignItems: 'center',
                    backgroundColor: Appearance.colors.divider(),
                    borderRadius: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '4px 16px 4px 16px',
                    width: 75,
                    ...animations
                }}>
                    <LottieView
                    autoPlay={true}
                    duration={2000}
                    loop={true}
                    source={require('files/lottie/dots-grey.json')}
                    style={{
                        height: 35,
                        width: 35
                    }}/>
                </animated.div>
            )
        }
        if(data.role === 'user') {
            return (
                <animated.div style={{
                    alignSelf: 'flex-end',
                    backgroundColor: Appearance.colors.primary(),
                    borderRadius: 15,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    lineHeight: 1.5,
                    marginBottom: isLastItem ? 0 : 24,
                    maxWidth: '75%',
                    position: 'relative',
                    wordBreak: 'break-word',
                    ...style,
                    ...animations
                }}>
                    {children}
                </animated.div>
            )
        }
        return (
            <animated.div 
            className={typeof(onClick) === 'function' ? 'cursor-pointer' : ''}
            onClick={onClick}
            style={{
                marginBottom: isLastItem ? 0 : 24,
                maxWidth: '100%',
                position: 'relative',
                ...animations
            }}>
                {getChildren()}
                {getAccessory()}
                {getNextActions()}
            </animated.div>
        )
    }

    const getFileInput = onChange => {
        return (
            <input 
            onChange={evt => {

                // notify subscriber that a file has been selected if applicable
                let file = evt.target.files[0];
                if(file) {
                    onChange(file);
                }

                // reset file input for next selection
                evt.target.value = null;
            }}
            ref={fileInputRef}
            type={'file'}
            style={{
                display: 'none'
            }} />
        )
    }

    const getNextActions = () => {
        
        let { actions = [], message } = data.client_metadata?.next_actions || {};
        let hasMessage = typeof(message) === 'string';
        return actions?.length > 0 && (
            <>
            <div style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.divider(),
                borderRadius: 15,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                lineHeight: 1.5,
                marginTop: 8,
                maxWidth: '100%',
                overflow: 'hidden',
                position: 'relative',
                textAlign: 'left',
                width: '100%',
                wordBreak: 'break-word'
            }}>
                {hasMessage && (
                    <span style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.text(),
                        fontWeight: 500,
                        padding: '10px 16px 10px 16px',
                        whiteSpace: 'normal'
                    }}>{message}</span>
                )}
                {actions.map((action, index) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        key={action}
                        onClick={onPromptClick.bind(this, action)}
                        style={{
                            alignItems: 'center',
                            borderTop: index === 0 && hasMessage === false ? null : `1px solid ${Appearance.colors.divider()}`,
                            display: 'flex',
                            flexDirection: 'row',
                            padding: 10,
                            width: '100%'
                        }}>
                            <img 
                            src={getRequiemActionPromptIcon(action)}
                            style={{
                                height: 25,
                                marginRight: 8,
                                objectFit: 'contain',
                                width: 25,
                            }} />
                            <span style={{
                                ...Appearance.textStyles.title(),
                                color: Appearance.colors.text(),
                                fontWeight: 500,
                                whiteSpace: 'normal'
                            }}>{getRequiemActionPromptTitle(action, data.client_metadata?.payload)}</span>
                        </div>
                    )
                })}
            </div>
            {isLastItem === false && (
                <animated.img 
                src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                style={{
                    height: 'auto',
                    marginTop: 24,
                    maxHeight: 16,
                    objectFit: 'contain',
                    position: 'relative',
                    width: '100%',
                    ...animations
                }} />
            )}
            </>
        )
    }

    const closeRequiemForMobile = () => {
        if(Utils.isMobile() === true) {
            setRequiem(requiem => {
                requiem.request = { action: 'close' };
                return requiem;
            });
        }
    }

    useEffect(() => {
        animationsApi.start({
            opacity: 1,
            ...data.role === 'user' && {
                right: '0%',
            },
            ...data.role !== 'user' && {
                left: '0%',
            }
        });
    }, []);

    return getContent();
}

const ResponsiveTable = ({ items, style, title }) => {

    const [active, setActive] = useState(1);

    const getButtons = () => {
        if(active === 1 || active === items.length - 1) {
            return (
                <Button 
                color={'primary'}
                containerStyle={{
                    width: '100%'
                }}
                label={active === 1 ? 'Next' : 'Back'}
                onClick={() => setActive(active === 1 ? active + 1 : active - 1)}
                style={{
                    borderRadius: 0
                }}
                type={'large'} />
            )
        }
        return (
            <>
            <Button 
            color={'dark'}
            containerStyle={{
                width: '100%'
            }}
            label={'Back'}
            onClick={() => setActive(active - 1)}
            style={{
                borderRadius: 0
            }}
            type={'large'} />
            <Button 
            color={'primary'}
            containerStyle={{
                width: '100%'
            }}
            label={'Next'}
            onClick={() => setActive(active + 1)}
            style={{
                borderRadius: 0
            }}
            type={'large'} />
            </>
        )
    }

    const getContent = () => {

        // determine if the device is using a mobile layout
        if(Utils.isMobile() === true) {

            // prepare headers array and loop through provided items
            let headers = items[0];
            return (
                <div style={{
                    width: '100%',
                    ...style
                }}>
                    {items.map((item, index) => {
                        if(index === 0 || active !== index) {
                            return null
                        }
                        return (
                            <div 
                            key={index}
                            style={{
                                ...Appearance.styles.unstyledPanel(),
                                display: 'flex',
                                flexDirection: 'column',
                                marginBottom: 0,
                                width: '100%'
                            }}>
                                <div style={{
                                    backgroundColor: Appearance.colors.divider(),
                                    padding: '8px 12px 8px 12px',
                                    width:' 100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        fontFamily: 'Zen Dots',
                                    }}>{`${title} ${index}/${items.length - 1}`.toUpperCase()}</span>
                                </div>
                                {item.map((text, index) => {
                                    return (
                                        <div 
                                        key={index}
                                        style={{
                                            borderBottom: index !== headers.length - 1 && Appearance.styles.bottomBorder(),
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: 12,
                                            width: '100%'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                display: 'flex',
                                                flexDirection: 'column',
                                                fontWeight: 'bold',
                                                width: '100%'
                                            }}>{`${headers[index]}:`}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: '100%'
                                            }}>{text}</span>
                                        </div>
                                    )
                                })}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%'
                                }}>
                                    {getButtons()}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )
        }
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%',
                ...style
            }}>
                <table style={{
                    tableLayout: 'fixed',
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <tbody>
                        {items.map((row, index) => {
                            return (
                                <tr 
                                key={index}
                                style={{
                                    backgroundColor: index % 2 === 0 ? 'rgba(150,150,150,0.1)' : null,
                                }}>{row.map((text, i) => {
                                    return (
                                        <td 
                                        key={i}
                                        style={{
                                            border: `1px solid ${Appearance.colors.divider()}`,
                                            padding: `12px 16px 12px 16px`
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                fontWeight: index === 0 && 'bold'
                                            }}>{text}</span>
                                        </td>
                                    )
                                })}</tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }
    return getContent();
}

const ScrollBlur = ({ children, className, style }) => {
    
    const ref = useRef(null);
    
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'start center']
    });
    
    const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
    const blur = useTransform(scrollYProgress, [0, 0.75], [10, 0]);
    const y = useTransform(scrollYProgress, [0, 1], [200, 0]);
    
    return (
        <motion.div
        ref={ref}
        className={className}
        style={{
            ...style,
            filter: useTransform(blur, (value) => `blur(${value}px)`),
            opacity,
            y
        }}>
            {children}
        </motion.div>
    );
}

export const ServiceContainer = ({ academics = {}, cta = {}, description, hero, onDemoVideoClick, overview = {}, stats = {}, utils, videoPoster, videoSrc, videoStyle }) => {

    const isAdPlacement = false;
    const { system } = useDataStore();

    const getAcademics = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: '25vh',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: '25vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <ScrollBlur 
                className={'p-0 p-lg-5'}
                style={{
                    alignItems: 'center',
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1500,
                    textAlign: 'center',
                    width: '100%',
                    ...system.window.isMobile === false && {
                        backgroundColor: `linear-gradient(${Appearance.colors.background({ alpha: 0 })}, ${Appearance.colors.divider()})`,
                    }
                }}>
                    <ScrollBlur style={{
                        position: 'relative' 
                    }}>
                        <ReactTyped 
                        autoPlay={true}
                        backSpeed={50} 
                        startWhenVisible={true}
                        strings={['What Experts Are Saying'.toUpperCase()]} 
                        style={{
                            color: Appearance.colors.text(),
                            fontSize: Utils.isMobile() ? 32 : 56,
                            fontWeight: 800,
                            fontFamily: 'Zen Dots',
                            lineHeight: 1.25
                        }}
                        typeSpeed={75} />
                    </ScrollBlur>
                    <ScrollBlur className={'mt-3 mt-lg-0 px-2 px-lg-0'}>
                        <span style={{
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontSize: system.window.isMobile ? 20 : 24,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            maxWidth: 1000,
                            position: 'relative',
                            width: '100%'
                        }}>
                            {academics.subTitle}
                        </span>
                    </ScrollBlur>
                    <ScrollBlur className={'mt-4 mb-5 mt-lg-5'}>
                        <img 
                        src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                        style={{
                            height: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            width: 350,
                            ...system.window.isMobile && {
                                maxWidth: '75%'
                            }
                        }} />
                    </ScrollBlur>
                    <ScrollBlur 
                    className={'px-2 px-lg-0 mb-5'}
                    style={{
                        maxWidth: 1000
                    }}>
                        <span style={{
                            color: Appearance.colors.text(),
                            fontSize: 16,
                            fontWeight: 400,
                            lineHeight: 1.75,
                            position: 'relative',
                        }}>{academics.description}</span>
                    </ScrollBlur>
                    <div 
                    className={'row p-0 mt-0 mt-lg-5'}
                    style={{
                        width: '100%'
                    }}>
                        {academics.items.map((item, index) => {
                            return (
                                <ScrollBlur
                                className={'col-12 col-lg-6 p-2'}
                                key={index}>
                                    <div 
                                    className={'p-3 p-lg-5'}
                                    style={{
                                        alignItems: 'center',
                                        backgroundColor: window.theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.25)',
                                        borderRadius: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        width: '100%'
                                    }}>
                                        <img 
                                        className={'my-3 mt-lg-0'}
                                        src={'images/academic-quote-green.png'}
                                        style={{
                                            height: 100,
                                            objectFit: 'contain',
                                            width: 100
                                        }} />
                                        <span 
                                        className={'mb-4'}
                                        style={{
                                            color: Appearance.colors.text(),
                                            fontSize: 16,
                                            fontWeight: 400,
                                            lineHeight: 1.5,
                                            display: 'block'
                                        }}>
                                            {item.description}
                                        </span>
                                        <span 
                                        className={'mb-4 mb-lg-3'}
                                        style={{
                                            color: Appearance.colors.subText(),
                                            display: 'block',
                                            fontSize: 16,
                                            fontWeight: 900,
                                            lineHeight: 1.5
                                        }}>{item.source.toUpperCase()}</span>
                                        <Button
                                        animateBorder={true}
                                        color={Appearance.colors.primary()}
                                        containerStyle={{
                                            width: system.window.isMobile ? '100%' : 'auto'
                                        }}
                                        label={'Read More'}
                                        onClick={window.open.bind(this, item.url)}
                                        type={'large'} /> 
                                    </div>
                                </ScrollBlur>
                            )
                        })}
                    </div>
                </ScrollBlur>
            </div>
        )
    }

    const getAcademicsDescription = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: '25vh',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: '25vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <ScrollBlur>
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontSize: system.window.isMobile ? 26 : 36,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        maxWidth: 1000,
                        width: '100%'
                    }}>
                        {academics.scrollDescription}
                    </span>
                </ScrollBlur>
            </div>
        )
    }

    const getAnimationProps = (props = {}) => {

        if(isAdPlacement === true) {
            return null;
        }

        let { animate, whileInView = true } = props;
        return {
            duration: 1.5,
            initial: { 
                opacity: 0, 
                top: -50
            },
            transition: {
                delay: props.delay || 0,
                type: 'spring',
                stiffness: 100,
                damping: 15
            },
            ...whileInView === false && {
                animate: { 
                    ...animate,
                    opacity: 1, 
                    top: 0
                }
            },
            ...whileInView === true && {
                viewport: { 
                    amount: 0.25
                },
                whileInView: { 
                    ...animate,
                    opacity: 1, 
                    top: 0
                }
            }
        }
    }
    
    const getCTA = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: '50vh',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: '25vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1500,
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <ScrollBlur style={{
                        position: 'relative' 
                    }}>
                        <ReactTyped 
                        autoPlay={true}
                        backSpeed={50}
                        startWhenVisible={true}
                        strings={cta.titles} 
                        style={{
                            color: Appearance.colors.text(),
                            fontSize: Utils.isMobile() ? 32 : 56,
                            fontWeight: 800,
                            fontFamily: 'Zen Dots',
                            lineHeight: 1.25
                        }}
                        typeSpeed={75} />
                    </ScrollBlur>
                    <ScrollBlur className={'mt-3 mt-lg-0'}>
                        <span style={{
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontSize: system.window.isMobile ? 20 : 24,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            maxWidth: 1000,
                            position: 'relative',
                            width: '100%'
                        }}>
                            {cta.subTitle}
                        </span>
                    </ScrollBlur>
                    <ScrollBlur style={{
                        marginBottom: 36,
                        marginTop: 28,
                        maxWidth: 1000
                    }}>
                        <span style={{
                            color: Appearance.colors.text(),
                            fontSize: 16,
                            fontWeight: 400,
                            lineHeight: 1.75,
                            position: 'relative',
                        }}>{cta.description}</span>
                    </ScrollBlur>
                    <ScrollBlur 
                    className={'row m-0 '}
                    style={{
                        maxWidth: '100%',
                        width: 500
                    }}>
                        <div className={'col-12 col-xl-6 px-0 pt-0 pb-2 px-xl-2'}>
                            <Button
                            color={Appearance.colors.primary()}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={'Get Started'}
                            onClick={utils.navigation.set.bind(this, { view: 'onboarding' })}
                            type={'large'} /> 
                        </div>
                        <div className={'col-12 col-xl-6 p-0 px-xl-2'}>
                            <Button
                            color={Appearance.colors.secondary()}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={'Read FAQ'}
                            onClick={utils.navigation.set.bind(this, { view: 'faq' })}
                            type={'large'} /> 
                        </div>
                    </ScrollBlur>
                </div>
            </div>
        )
    }
    
    const getDescription = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: '25vh',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: '25vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <ScrollBlur>
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontSize: system.window.isMobile ? 26 : 36,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        maxWidth: 1000,
                        width: '100%'
                    }}>
                        {description}
                    </span>
                </ScrollBlur>
            </div>
        )
    }

    const getHeroButtons = () => {
        if(isAdPlacement === true) {
            return null;
        }
        if(typeof(onDemoVideoClick) === 'function') {
            return (
                <>
                <div className={'col-12 col-xl-6 px-0 pt-0 pb-2 px-xl-2'}>
                    <Button
                    color={Appearance.colors.primary()}
                    containerStyle={{
                        width: '100%'
                    }}
                    label={'Get Started'}
                    onClick={utils.navigation.set.bind(this, { view: 'onboarding' })}
                    type={'large'} /> 
                </div>
                <div className={'col-12 col-xl-6 p-0 px-xl-2'}>
                    <Button
                    color={Appearance.colors.secondary()}
                    containerStyle={{
                        width: '100%'
                    }}
                    label={'Watch Demo'}
                    onClick={onDemoVideoClick}
                    type={'large'} /> 
                </div>
                </>
            )
        }
        return (
            <div className={'col-12 px-0 pt-0 px-xl-2'}>
                <Button
                color={Appearance.colors.primary()}
                containerStyle={{
                    width: '100%'
                }}
                label={'Get Started'}
                onClick={utils.navigation.set.bind(this, { view: 'onboarding' })}
                type={'large'} />   
            </div>
        ) 
    }

    const getHeroSection = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100vh',
                position: 'relative',
                textAlign: 'center',
                width: '100%'
            }}>
                <div style={{
                    height: `calc(100vh + 250px)`,
                    left: 0,
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    width: '100%'
                }}>
                    <video 
                    autoPlay={true} 
                    controls={false}
                    playsInline={true}
                    loop={true} 
                    muted={true}
                    poster={videoPoster}
                    src={videoSrc}  
                    style={{
                        height: 'calc(100vh + 250px)',
                        objectFit: 'cover',
                        position: 'relative',
                        top: -10,
                        width: '100vw',
                        ...videoStyle
                    }} />
                    <div style={{
                        background: `linear-gradient(180deg, ${Appearance.colors.background({ alpha: 0 })} 0%, ${Appearance.colors.background({ alpha: 1 })} 100%)`,
                        bottom: 0,
                        height: 500,
                        position: 'absolute',
                        left: 0,
                        right: 0
                    }} />
                </div>
                <HeroSplashAnimated style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 16,
                    width: '100%'
                }}>
                    <div 
                    className={'p-3 p-lg-5'}
                    style={{
                        alignItems: 'center',
                        backdropFilter: 'blur(20px)',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderRadius: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        maxWidth: 1100,
                        position: 'relative',
                        textAlign: 'center',
                        WebkitBackdropFilter: 'blur(20px)',
                        width: '100%',
                        zIndex: 0
                    }}>
                        <motion.div 
                        {...getAnimationProps({ whileInView: false })}
                        style={{
                            position: 'relative'
                        }}>
                            <AltBadge content={{
                                color: Appearance.colors.primary(),
                                text: 'LifeWipe Core Service'
                            }} />
                        </motion.div>
                        <motion.div 
                        {...getAnimationProps({ delay: 0.25, whileInView: false })}
                        style={{
                            marginTop: 36,
                            position: 'relative'
                        }}>
                            <ReactTyped 
                            autoPlay={true}
                            backSpeed={50} 
                            backDelay={2500}
                            strings={isAdPlacement ? [hero.titles[0]] : hero.titles} 
                            style={{
                                color: Appearance.colors.text(),
                                fontSize: Utils.isMobile() ? 32 : 56,
                                fontFamily: 'Zen Dots',
                                fontWeight: 800,
                                lineHeight: 1.25
                            }}
                            typeSpeed={isAdPlacement ? 1 : 75} />
                        </motion.div>
                        <motion.span 
                        className={'mt-3 mt-lg-0'}
                        {...getAnimationProps({ delay: 0.5, whileInView: false })}
                        style={{
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontSize: system.window.isMobile ? 20 : 24,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            maxWidth: 1000,
                            position: 'relative',
                            width: '100%'
                        }}>
                            {hero.subTitle}
                        </motion.span>
                        <motion.img 
                        className={'px-4 pt-4'}
                        {...getAnimationProps({ delay: 0.75, whileInView: false })}
                        src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                        style={{
                            height: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            position: 'relative',
                            width: 350,
                            ...system.window.isMobile && {
                                maxWidth: '75%'
                            }
                        }} />
                        <motion.span 
                        className={'my-4'}
                        {...getAnimationProps({ delay: 1, whileInView: false })}
                        style={{
                            color: Appearance.colors.text(),
                            fontSize: 16,
                            fontWeight: 500,
                            lineHeight: 1.5,
                            maxWidth: 750,
                            position: 'relative',
                        }}>{hero.description}</motion.span>
                        <motion.div 
                        {...getAnimationProps({ delay: 1.25, whileInView: false })}
                        className={'row'}
                        style={{
                            maxWidth: 400,
                            position: 'relative',
                            width: '100%'
                        }}>
                            {getHeroButtons()}
                        </motion.div>
                        <BorderBeam
                        duration={8}
                        initial={{ visibility: 'hidden' }}
                        whileInView={{ visibility: 'visible' }} 
                        style={{
                            border: '2px solid rgba(255,255,255,0.25)',
                            borderRadius: 12 
                        }} />
                    </div>
                </HeroSplashAnimated>
            </div>
        )
    }

    const getOverview = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: '25vh',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: '25vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <div 
                className={'p-0 p-lg-5'}
                style={{
                    alignItems: 'center',
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1500,
                    textAlign: 'center',
                    width: '100%',
                    ...system.window.isMobile === false && {
                        backgroundColor: `linear-gradient(${Appearance.colors.background({ alpha: 0 })}, ${Appearance.colors.divider()})`,
                    }
                }}>
                    <ScrollBlur style={{
                        position: 'relative' 
                    }}>
                        <ReactTyped 
                        autoPlay={true}
                        backSpeed={50} 
                        startWhenVisible={true}
                        strings={overview.titles} 
                        style={{
                            color: Appearance.colors.text(),
                            fontSize: system.window.isMobile ? 32 : 56,
                            fontWeight: 800,
                            fontFamily: 'Zen Dots',
                            lineHeight: 1.25
                        }}
                        typeSpeed={75} />
                    </ScrollBlur>
                    <ScrollBlur className={'mt-3 mt-lg-0 px-2 px-lg-0'}>
                        <span style={{
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontSize: system.window.isMobile ? 20 : 24,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            maxWidth: 1000,
                            position: 'relative',
                            width: '100%'
                        }}>
                            {overview.subTitle}
                        </span>
                    </ScrollBlur>
                    <ScrollBlur className={'mt-4 mb-5 mt-lg-5'}>
                        <img 
                        src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                        style={{
                            height: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            width: 350,
                            ...system.window.isMobile && {
                                maxWidth: '75%'
                            }
                        }} />
                    </ScrollBlur>
                    <ScrollBlur 
                    className={'px-2 px-lg-0 mb-5'}
                    style={{
                        maxWidth: 1000
                    }}>
                        <span style={{
                            color: Appearance.colors.text(),
                            fontSize: 16,
                            fontWeight: 400,
                            lineHeight: 1.75,
                            position: 'relative',
                        }}>{overview.description}</span>
                    </ScrollBlur>
                    <ScrollBlur 
                    className={'mt-5'}
                    style={{
                        maxWidth: 1000,
                        width: '100%'
                    }}>
                        <CardStack items={overview.items} />
                    </ScrollBlur>
                </div>
            </div>
        )
    }

    const getStats = () => {
        return (
            <div style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100vh',
                paddingBottom: '25vh',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: '25vh',
                textAlign: 'center',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1500,
                    textAlign: 'center',
                    width: '100%',
                    ...system.window.isMobile === false && {
                        backgroundColor: `linear-gradient(${Appearance.colors.background({ alpha: 0 })}, ${Appearance.colors.divider()})`,
                    }
                }}>
                    <ScrollBlur 
                    className={'px-2 px-lg-0'}
                    style={{
                        position: 'relative' 
                    }}>
                        <ReactTyped 
                        autoPlay={true}
                        backSpeed={50}
                        startWhenVisible={true} 
                        strings={stats.titles} 
                        style={{
                            color: Appearance.colors.text(),
                            fontSize: Utils.isMobile() ? 32 : 56,
                            fontWeight: 800,
                            fontFamily: 'Zen Dots',
                            lineHeight: 1.25
                        }}
                        typeSpeed={75} />
                    </ScrollBlur>
                    <ScrollBlur className={'mt-3 mt-lg-0 px-2 px-lg-0'}>
                        <span style={{
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontSize: system.window.isMobile ? 20 : 24,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            maxWidth: 1000,
                            position: 'relative',
                            width: '100%'
                        }}>
                            {stats.subTitle}
                        </span>
                    </ScrollBlur>
                    <ScrollBlur className={'mt-4 mb-5 mt-lg-5'}>
                        <img 
                        src={window.theme === 'dark' ? 'images/mobile-layout-divider-white.png' : 'images/mobile-layout-divider-grey.png'}
                        style={{
                            height: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            width: 350,
                            ...system.window.isMobile && {
                                maxWidth: '75%'
                            }
                        }} />
                    </ScrollBlur>
                    <ScrollBlur 
                    className={'px-2 px-lg-0 mb-5'}
                    style={{
                        maxWidth: 1000
                    }}>
                        <span style={{
                            color: Appearance.colors.text(),
                            fontSize: 16,
                            fontWeight: 400,
                            lineHeight: 1.75,
                            position: 'relative',
                        }}>{stats.description}</span>
                    </ScrollBlur>
                    <div 
                    className={'row p-0 mt-0 mt-lg-5'}
                    style={{
                        width: '100%'
                    }}>
                        {stats.items.map((item, index) => {
                            return (
                                <ScrollBlur
                                className={'col-12 col-lg-6 p-0 p-lg-2 mb-2 mb-lg-0'}
                                key={index}>
                                    <div 
                                    className={'p-3 p-lg-5'}
                                    style={{
                                        alignItems: 'center',
                                        backgroundColor: window.theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.25)',
                                        borderRadius: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        width: '100%'
                                    }}>
                                        <img 
                                        className={'my-3 mt-lg-0'}
                                        src={'images/double-quotes-green.png'}
                                        style={{
                                            height: 100,
                                            objectFit: 'contain',
                                            width: 100
                                        }} />
                                        <span 
                                        className={'mb-4'}
                                        style={{
                                            color: Appearance.colors.text(),
                                            fontSize: 16,
                                            fontWeight: 400,
                                            lineHeight: 1.5,
                                            display: 'block'
                                        }}>
                                            {item.description}
                                        </span>
                                        <span 
                                        className={'mb-4 mb-lg-3'}
                                        style={{
                                            color: Appearance.colors.subText(),
                                            display: 'block',
                                            fontSize: 16,
                                            fontWeight: 900,
                                            lineHeight: 1.5
                                        }}>{item.source.toUpperCase()}</span>
                                    </div>
                                </ScrollBlur>
                            )
                        })}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            {getHeroSection()}
            {getDescription()}
            {getStats()}
            {getAcademics()}
            {getAcademicsDescription()}
            {getOverview()}
            {getCTA()}
        </div>
    )
}

export const ServicesDataVault = ({ utils }) => {

    const { system } = useDataStore();
    
    const getAcademics = () => {
        return {
            description: `Scholars across technology, law, and archive science agree: without secure vault‑style systems that combine encryption, intent‑driven rules, and legacy transition logic, digital assets are at risk of being lost, misused, or inaccessible. Here are four studies that validate the need for exactly what the Vault delivers.`,
            items: [{
                description: 'People create an ever‑growing number of digital belongings yet lack consistent systems to preserve and access them over time… personal storage is highly distributed and current frameworks do not support long‑term access.',
                source: 'Marshall, C., Bly, S., & Brun‑Cottan, F. (2007)',
                url: 'https://arxiv.org/abs/0704.3653'
            },{
                description: `There is no uniform practice of preservation, removal, and inheritance of digital assets … studies show a lack of awareness among users about how to plan their digital estate while they are alive.`,
                source: `Mali, D. P. (2019)`,
                url: 'https://repository.nls.ac.in/cgi/viewcontent.cgi?article=1365&context=nlsj'
            },{
                description: 'Digital‑asset knowledge, income and other factors positively influence the likelihood of including digital assets in inheritance planning… Without adequate documentation... heirs may encounter significant obstacles.',
                source: 'Kraiwanit, T. (2025)',
                url: 'https://www.mdpi.com/1911-8074/18/6/330'
            },{
                description: `As digital assets become more valuable and widely distributed, secure mechanisms for transferring them after death or incapacity are increasingly necessary.`,
                source: 'Goldston, J., Chaffer, T. J., Osowska, J., & von Goins II, C. (2023)',
                url: 'https://arxiv.org/abs/2301.11074'
            }],
            scrollDescription: (
                <>
                {`Without structured storage — and rules for what happens `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'after'}</span>
                {` death — even the simplest transitions can become emotional and legal  `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'disasters'}</span>
                {`. Academic researchers and tech ethicists agree: secure, `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'user-directed'}</span>
                {` digital storage is no longer optional.`}
                </>
            ),
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`When There’s No `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Vault'}</span>
                    {`, ${system.window.isMobile ? '\n' : ''}There’s Confusion and Delay`}
                </span>
            )
        };
    }

    const getCTA = () => {
        return {
            description: `Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`,
            subTitle: (
                <>
                {`Don’t Let Your `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Legacy'}</span>
                {` Get ${system.window.isMobile ? '\n' : ''}Lost in the Cloud.`}
                </>
            ),
            titles: ['Lock the Vault'.toUpperCase()]
        };
    }

    const getDescription = () => {
        return (
            <>
            {`A huge percentage of people die without `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700,
            }}>{'sharing'}</span>
            {` their device passwords — let alone their most sensitive documents or backup plans. From families `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'unable'}</span>
            {` to access wills, to lost crypto, to social profiles left unmoderated, the vault solves a `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'crisis'}</span>
            {` you never want your loved ones to face.`}
            </>
        )
    }

    const getHeroSection = () => {
        return {
            description: `Your most important files, credentials, and final wishes deserve more than a folder or a cloud drive. LifeWipe Vault gives you encrypted storage — with built-in handoff rules for what happens next.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`A Vault That `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Lives'}</span>
                    {` With You — ${system.window.isMobile ? '\n' : ''}And Ends When You Say So`}
                </span>
            ),
            titles: ['Secure Data Vault'.toUpperCase(), 'Protect Your Data'.toUpperCase(), 'Secure Your Files'.toUpperCase(), 'Secure Data Vault'.toUpperCase()]
        }
    }

    const getOverview = () => {
        return {
            colors: [],
            description: `How many accounts have you created over the years? Email. Banks. Cloud drives. Streaming services. Loyalty cards. Old photo apps. Imagine your family trying to find, access, or close all of them — without your logins. Linked Accounts makes sure that never happens. You link what matters while you're alive. We help control, transfer, or erase it when you can’t.`,
            items: [{
                description: `From tax docs to family photos to crypto keys, the Vault accepts any format. You can drag and drop, import from cloud platforms, or add structured entries for credentials. Everything is protected with AES‑256 encryption and multi-factor access.`,
                icon: 'images/services_data_vault_step1.png',
                id: 1,
                title: 'Upload Your Files'
            },{
                description: `Create folders like "Medical", "Legal", "Personal Letters", or "Business Accounts". Tag files for access by specific contacts or link them to LifeCheck and Emergency Trigger instructions.`,
                icon: 'images/services_data_vault_step2.png',
                id: 2,
                title: `Organize and \nTag Your Data`
            },{
                description: `Choose to share decrypted files, password-protected links, or full folders — exactly as you’ve defined. You control who sees what, and when. Link access to your Emergency Trigger or LifeChecks to automate delivery when it matters most.`,
                icon: 'images/services_data_vault_step3.png',
                id: 3,
                title: 'Set Your Triggers'
            },{
                description: `Assign who can access specific Vault items, set how long they can view them, and get notified any time they do. You can grant limited-time access for trusted contacts, attorneys, or family. Access can be revoked instantly — you stay in control, always.`,
                icon: 'images/services_data_vault_step4.png',
                id: 4,
                title: `Secure ${system.window.isMobile ? '\n' : ''}Timed Access`
            }],
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Store What `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Matters'}</span>
                    {`. ${system.window.isMobile ? '\n' : ''}Control How It’s Used.`}
                </span>
            ),
            titles: ['Secure the Vault'.toUpperCase()]
        }
    }

    const getStats = () => {
        return {
            description: `When someone dies, it’s not just their social media that stays behind. It’s their tax records. Their cloud-stored memories. Their crypto. Their medical history. And almost none of it is easy to access. The vast majority of people leave no guidance for what should happen to their data. Secure Data Vault exists to make sure you’re not part of that majority.`,
            items: [{
                description: 'Only 18% have a plan for what happens to their digital files after death.',
                source: 'AARP Tech & Estate Study, 2023'
            },{
                description: '$1.4B in unclaimed crypto assets are estimated to be "locked after death".',
                source: `Chainalysis, 2023`
            },{
                description: '74% of people say they’d want loved ones to access their files — but don’t have a system to allow it.',
                source: 'Dropbox Privacy Poll, 2022'
            },{
                description: `Just 1 in 10 people report storing sensitive digital materials in encrypted or secure environments`,
                source: 'Consumer Reports Digital Security Poll, 2021'
            }],
            subTitle: (
                <span style={{
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>
                    {`Don’t Let Your `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic'
                    }}>{'Legacy'}</span>
                    {` ${system.window.isMobile ? '\n' : ''}Get Lost in the Cloud`}
                </span>
                
            ),
            titles: [`No Plan = Lost Files`.toUpperCase()]
        }
    }

    return (
        <ServiceContainer
        academics={getAcademics()}
        cta={getCTA()}
        description={getDescription()}
        hero={getHeroSection()}
        overview={getOverview()}
        stats={getStats()}
        utils={utils}
        videoPoster={'images/services.data_vault.png'}
        videoSrc={'images/services.data_vault.mp4'} />
    )
}

export const ServicesEmergencyTrigger = ({ utils }) => {

    const { system } = useDataStore();
    
    const onDemoVideoClick = () => {
        if(Utils.isMobile() === true) {
            window.open('https://www.youtube.com/watch?v=t8PWJ2Yn8mQ');
            return;
        }
        utils.layer.open({
            Component: VideoPlayer.bind(this, { url: 'https://www.youtube.com/embed/t8PWJ2Yn8mQ?si=smRy8ZajBfP-Ri3A' }),
            id: 'video_player'
        });
    }

    const getAcademics = () => {
        return {
            description: `Studies show that most estate and digital privacy systems are designed for slow death - not sudden crisis. But the world isn't always that generous. That's why legal scholars, cryptographers, and sociologists are beginning to advocate for real-time, user-controlled failsafes - not just wills.`,
            items: [{
                description: 'Emergency events increase data risk due to sudden incapacity or surveillance conditions… regulatory frameworks lack user-driven protective controls during these critical windows.',
                source: 'Ventrella et al., Springer, 2020',
                url: 'https://link.springer.com/article/10.1007/s12027-020-00629-3'
            },{
                description: `Increased surveillance and post-crisis reinforces the urgency of real-time digital autonomy. Preemptive user safeguards are no longer optional - they're ethical.`,
                source: `Malagocka, Cogitatio Press, 2024`,
                url: 'https://www.cogitatiopress.com/politicsandgovernance/article/viewFile/8572/4094'
            },{
                description: 'Emergency management systems must account for personal data sovereignty - empowering individuals to initiate protection regardless of institutional delays.',
                source: 'Visave et al., Wolrd Scientific, 2024',
                url: 'https://cmci.colorado.edu/idlab/assets/bibliography/pdf/Doyle2023.pdf'
            },{
                description: `Data autonomy must be preserved in emergency scenarios — individuals should retain agency over their digital footprint, especially when incapacitation or communication failure is likely.`,
                source: 'Rodríguez & Pagallo, 2022 – Journal of Ethics & InfoTech',
                url: 'https://link.springer.com/article/10.1007/s10676-022-09635-1'
            }],
            scrollDescription: (
                <>
                {`These researchers aren’t `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'imagining'}</span>
                {` dystopias. They’re responding to `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'reality'}</span>
                {`  — one where data moves faster than protection laws. Emergency Trigger is our `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'answer'}</span>
                {` to that gap.`}
                </>
            ),
            subTitle: (
                <>
                {'The Science of '}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Instant'}</span>
                {` Protection`}
                </>
            )
        };
    }

    const getCTA = () => {
        return {
            description: `Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`,
            subTitle: (
                <>
                {`Don't Wait For A `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Crisis'}</span>
                {`, Be Ready.`}
                </>
            ),
            titles: ['Lock Down Your Data'.toUpperCase()]
        };
    }

    const getDescription = () => {
        return (
            <>
            {`Medical crisis. Natural disaster. Sudden incapacitation. Emergency Trigger gives you a `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700,
            }}>{'trusted'}</span>
            {` , secure way to instantly `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'lock down'}</span>
            {`  your digital life — so sensitive data stays private, and your wishes stay `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'protected.'}</span>
            </>
        )
    }

    const getHeroSection = () => {
        return {
            description: `The Emergency Trigger is your instant failsafe — a digital switch that wipes devices, activates legacy protocols, or locks down access when you’re in danger.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Because Your `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Privacy'}</span>
                    {` Shouldn't ${system.window.isMobile ? '\n' : ''} Depend On Luck`}
                </span>
            ),
            titles: ['Emergency Trigger'.toUpperCase(), 'Digital Panic Button'.toUpperCase(), 'System Lockdown'.toUpperCase(), 'Emergency Trigger'.toUpperCase()]
        }
    }

    const getOverview = () => {
        return {
            description: `Emergency situations are rarely planned — but what happens next can be. The Emergency Trigger gives you the ability to take action, even when you physically can’t. Whether it’s a medical event, a hostile encounter, or a total disappearance, this system steps in where others freeze. No lawyers. No waiting. No delay. Just immediate protection for your digital life.`,
            items: [{
                description: `Lock in your peace of mind. Start by enabling your Emergency Trigger and setting your preferred authentication method—MFA, 2FA, or a custom passphrase. This ensures that only you (or someone you've truly authorized) can unleash the protocol. No trigger works without your digital fingerprint.`,
                icon: 'images/services_emergency_trigger_step1.png',
                id: 1,
                title: 'Enable & Secure \nYour Trigger'
            },{
                description: `Optionally appoint one or more Trusted Contacts who can verify emergencies, flag concerns, or—if you’ve authorized it—initiate a trigger when you can’t. These allies don’t gain control; they serve as sentinels. Whether you're unreachable or compromised, they help keep your digital legacy safe.`,
                icon: 'images/services_emergency_trigger_step2.png',
                id: 2,
                title: `Designate \nTrusted Contacts`
            },{
                description: `When the moment hits—whether it's a crisis, a breach, or worse—initiate the wipe or lockdown instantly. Use the LifeWipe home screen app or log in to the sethe website. One tap to get started. Zero hesitation. Your privacy moves faster than the threat.`,
                icon: 'images/services_emergency_trigger_step3.png',
                id: 3,
                title: `Activate ${system.window.isMobile ? '\n' : ''}the Trigger`
            },{
                description: `Once activated, LifeWipe begins executing your instructions—device wipes, message transfers, vault locks—based on your settings. If Trusted Contacts are part of your plan, we’ll hold momentarily for verification before completing the action. You’re in control, even when you're not.`,
                icon: 'images/services_emergency_trigger_step4.png',
                id: 4,
                title: 'Commands Execute, \nWith Safeguards'
            }],
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                {`Trigger What `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Matters'}</span>
                    {` ${system.window.isMobile ? '\n' : ''}When It Matters Most`}
                </span>
            ),
            titles: ['One Move, Full Control'.toUpperCase()]
        }
    }

    const getStats = () => {
        return {
            description: `Most people don’t expect a crisis. But when one hits — a medical emergency, an arrest, an accident, or worse — seconds matter. And your phone, laptop, cloud, and passwords are still wide open. Emergency Trigger gives you a single point of control — even if everything else is spinning out.`,
            items: [{
                description: '87% of smartphone owners have no device-level emergency plan.',
                source: 'Pew Research, 2023'
            },{
                description: 'Identity theft rises up to 92% in the 72 hours after a person is incapacitated.',
                source: `AARP Cybersecurity Report, 2024`
            },{
                description: '63% of U.S. adults store sensitive personal data on devices they carry daily.',
                source: 'Norton Consumer Risk Survey, 2023'
            },{
                description: `1 in 4 Americans say they’d want instant deletion tools during a security threat.`,
                source: 'Life After Tech Poll, 2025'
            }],
            subTitle: (
                <>
                <span style={{
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>
                    {`When Emergencies Strike, ${system.window.isMobile ? '\n' : ''}Every `}
                </span>
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600,
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>{'Second'}</span>
                {` Counts`}
                </>
            ),
            titles: ['Why Speed Matters'.toUpperCase()]
        }
    }

    return (
        <ServiceContainer
        academics={getAcademics()}
        cta={getCTA()}
        description={getDescription()}
        onDemoVideoClick={onDemoVideoClick}
        hero={getHeroSection()}
        overview={getOverview()}
        stats={getStats()}
        utils={utils}
        videoPoster={'images/services.emergency_trigger.png'}
        videoSrc={'images/services.emergency_trigger.mp4'} />
    )
}

export const ServicesInstructions = ({ utils }) => {

    const { system } = useDataStore();
    
    const getAcademics = () => {
        return {
            description: `As your digital self becomes ever more intertwined with your life — and death — the frameworks governing it are struggling to keep up. From remote‐wiping devices to ensuring one’s personal legacy isn’t commandeered, the legal and technical scholarship is converging on one reality: you must proactively define how your data behaves when you can’t`,
            items: [{
                description: 'The cost of the hardware... due to lost or stolen devices is generally less than the direct and indirect cost resulting from the leakage of the information.',
                source: 'M. Di Leom, (2016)',
                url: 'https://mdleom.com/files/publications/Remote-wiping-and-secure-deletion-on-mobile-devices-a-review_postprint.pdf'
            },{
                description: 'The current framework lacks clear guidelines, leading to privacy concerns, emotional distress for families, and legal ambiguities.',
                source: `N. Rao, (Brazilian Journal of Law, Tech & Innovation, 2024)`,
                url: 'https://arxiv.org/abs/2104.07807'
            },{
                description: 'When a person leaves digital assets intestate, courts should destroy those assets unless a potential beneficiary can demonstrate the deceased’s intent.',
                source: 'J. Manjares, 2022',
                url: 'https://libpros.com/wp-content/uploads/2022/01/5ff81-flp-dissertation-writing-contest-entry-manjares-.pdf'
            },{
                description: `These include... safeguard posthumous privacy and data protections, sustain citizen autonomy and decision making (advanced directive, and data trustee)`,
                source: 'M. Cunneen, (2025)',
                url: 'https://link.springer.com/article/10.1007/s00146-025-02514-4'
            }],
            scrollDescription: (
                <>
                {`Digital autonomy must be planned in `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'advance'}</span>
                {`, and legal systems are increasingly recognizing the need for explicit user directives. `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'Instructions'}</span>
                {` give users enforceable control in life, incapacitation, or death — before `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'ambiguity'}</span>
                {` becomes a vulnerability.`}
                </>
            ),
            subTitle: (
                <>
                {'Proactively '}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Define'}</span>
                {` How Your Data Behaves`}
                </>
            )
        };
    }

    const getCTA = () => {
        return {
            description: `Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Your Wishes, `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Protected'}</span>
                    {`. ${system.window.isMobile ? '\n' : ''}No Matter What Happens.`}
                </span>
            ),
            titles: ['Future-Proof Your Privacy'.toUpperCase()]
        };
    }

    const getDescription = () => {
        return (
            <>
            {`Whether the threat is physical, digital, or posthumous, life `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700,
            }}>{'rarely'}</span>
            {` gives a warning. Phones vanish. Accounts get breached. One `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'crisis'}</span>
            {` is all it takes for your digital footprint to become a `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'liability'}</span>
            {`.`}
            </>
        )
    }

    const getHeroSection = () => {
        return {
            backgroundColor: 'rgba(255,255,255,0.5)',
            description: `Instructions are a set of preloaded commands that detonate only under the conditions you define. Instant. Automated. Uncompromising.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Activated `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Exactly'}</span>
                    {` How ${system.window.isMobile ? '\n' : ''}You Designed It`}
                </span>
            ),
            titles: ['Instructions'.toUpperCase(), 'Directives'.toUpperCase(), 'Commands'.toUpperCase(), 'Instructions'.toUpperCase()]
        }
    }

    const getOverview = () => {
        return {
            description: `Instructions don’t just protect you — they execute exactly what you want, when you can’t intervene. Each step is engineered to prevent misuse while enabling immediate, irreversible action when a real emergency hits. Below is how the system works from setup to completion, whether you’re in danger, incapacitated, or confirmed deceased.`,
            items: [{
                description: 'You choose the exact situations that should activate an instruction. This might be inactivity for a certain number of days, an Emergency Trigger, a verified SMS phrase, or a request from a Trusted Contact.',
                icon: 'images/services_instructions_step1.png',
                id: 1,
                title: 'Set the Conditions'
            },{
                description: 'You assign the digital actions you want carried out. You can customize instructions to handle different scenarios — or stack actions for a total fail-safe.',
                icon: 'images/services_instructions_step2.png',
                id: 2,
                title: `Choose ${system.window.isMobile ? '\n' : ''}What Happens`
            },{
                description: 'Before anything executes, our system performs behind-the-scenes verification. That might include confirming Trusted Contact wishes, or matching authentication data if enabled. No action moves forward unless your conditions are met.',
                icon: 'images/services_instructions_step3.png',
                id: 3,
                title: `Layer In ${system.window.isMobile ? '\n' : ''}Security Checks`
            },{
                description: 'Once verified, LifeWipe carries out your instructions across all registered devices and accounts. Everything happens quickly and without the need for your input.',
                icon: 'images/services_instructions_step4.png',
                id: 4,
                title: 'Trigger Is Activated'
            }],
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {'Precision On '}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Standby'}</span>
                    {`. ${system.window.isMobile ? '\n' : ''}Your Data Obeys Only You.`}
                </span>
            ),
            titles: ['When Trust Meets Technology'.toUpperCase()]
        }
    }

    const getStats = () => {
        return {
            description: `Even as digital lives grow more complex, most people still haven’t created a clear plan for what happens to their data in a crisis. From device disposal to death, users are consistently unprepared — or unaware — of the tools available to protect their digital footprint. The numbers show a growing desire for control, but a persistent gap in follow-through.`,
            items: [{
                description: '80% of Americans agree it’s important to plan for what happens to their digital life after death — but only 57% have done anything about it.',
                source: 'OnePoll Survey for OneDay, 2024'
            },{
                description: '61% of global consumers worry that a deceased person’s identity is especially vulnerable to theft — yet few take action to secure accounts.',
                source: `Kaspersky "Digital Afterlife" Report, 2021`
            },{
                description: '29% of adults don’t know how to wipe personal information from an old device before discarding or recycling it.',
                source: 'Information Commissioner’s Office (ICO), UK, 2024'
            },{
                description: `76% of Americans say they have little to no knowledge of how to handle digital assets during estate planning.`,
                source: 'Bryn Mawr Trust "Digital Legacy Survey", 2023'
            }],
            subTitle: (
                <span style={{
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>
                    {'Most People Don’t '}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Plan'}</span>
                    {` To ${system.window.isMobile ? '\n' : ''}Lose Control Of Their Data`}
                </span>
            ),
            titles: ['Why People Don’t Plan'.toUpperCase()]
        }
    }

    return (
        <ServiceContainer
        academics={getAcademics()}
        cta={getCTA()}
        description={getDescription()}
        hero={getHeroSection()}
        overview={getOverview()}
        stats={getStats()}
        utils={utils}
        videoPoster={'images/services.instructions.png'}
        videoSrc={'images/services.instructions.mp4'} 
        videoStyle={{
            objectFit: system.window.isMobile ? 'none' : 'cover',
            objectPosition: system.window.isMobile ? '40% 80%' : null, 
        }} />
    )
}

export const ServicesLifeChecks = ({ utils }) => {

    const { system } = useDataStore();
    
    const onDemoVideoClick = () => {
        if(Utils.isMobile() === true) {
            window.open('https://www.youtube.com/watch?v=hFrgLg0X_Yk');
            return;
        }
        utils.layer.open({
            Component: VideoPlayer.bind(this, { url: 'https://www.youtube.com/embed/hFrgLg0X_Yk?si=smRy8ZajBfP-Ri3A' }),
            id: 'video_player'
        });
    }

    const getAcademics = () => {
        return {
            description: `Researchers from top universities — from privacy specialists to digital sociologists — are raising serious concerns about what happens to our digital lives after we die. What they’ve found? Most people know they should plan ahead… and most still don’t. This gap between intention and action is what LifeCheck is designed to close.`,
            items: [{
                description: 'We discuss a post-mortem privacy paradox — users recognize the value of digital legacy planning, but avoid doing it.',
                source: 'Holt et al., 2021, WWW ‘21 Proceedings',
                url: 'https://arxiv.org/abs/2104.07807'
            },{
                description: 'Technology users’ planning actions are underdeveloped compared to their concerns about the future of their digital assets.',
                source: `Holt et al., 2021`,
                url: 'https://arxiv.org/abs/2104.07807'
            },{
                description: 'Digital legacy spans a lifecycle: encoding → accessing → dispossessing — but most users don’t move beyond step one.',
                source: 'Doyle & Brubaker, 2023, Univ. of Colorado Boulder',
                url: 'https://cmci.colorado.edu/idlab/assets/bibliography/pdf/Doyle2023.pdf'
            },{
                description: `Current estate laws are inadequate for managing digital accounts and online assets… leaving executors unprepared.`,
                source: 'Perrone, Catholic University Legal Studies Journal, 2021',
                url: 'https://scholarship.law.edu/commlaw/vol21/iss1/7/'
            }],
            scrollDescription: (
                <>
                {`Behind every `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'quote'}</span>
                {` is a peer-reviewed paper, a legal study, or a behavioral analysis — all pointing to the same truth: We are digitally `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'unprepared'}</span>
                {` for death. LifeCheck isn’t just a feature. It’s a response to a documented `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'crisis'}</span>
                {` in personal privacy, family burden, and legal inaccessibility.`}
                </>
            ),
            subTitle: (
                <>
                {'You Don’t Have To Take '}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Our'}</span>
                {` Word For It..`}
                </>
            )
        };
    }

    const getCTA = () => {
        return {
            description: `Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`,
            subTitle: (
                <>
                {`Death Is `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Unpredictable'}</span>
                {`, Your Data Isn't.`}
                </>
            ),
            titles: ['Secure Your Digital Legacy'.toUpperCase()]
        };
    }

    const getDescription = () => {
        return (
            <>
            {`LifeChecks verify you're okay using `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700,
            }}>{'proof-of-life'}</span>
            {` signals. When you're `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'unable to respond'}</span>
            {`—whether due to an emergency, disappearance, or death—LifeWipe `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'automatically'}</span>
            {` enacts your predetermined digital legacy plans.`}
            </>
        )
    }

    const getHeroSection = () => {
        return {
            backgroundColor: 'rgba(255,255,255,0.5)',
            description: `Your quiet guardian. LifeChecks send periodic pings to confirm you're safe, alive, and in control. \nMiss a check-in—and your preset instructions take over automatically.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Automated Check-Ins That ${system.window.isMobile ? '\n' : ''}`}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif', 
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Protect'}</span>
                    {` Your Digital Life`}
                </span>
            ),
            titles: ['LifeChecks'.toUpperCase(), 'Automated Check-Ins'.toUpperCase(), 'Digital Well-Being'.toUpperCase(), 'LifeChecks'.toUpperCase()]
        }
    }

    const getOverview = () => {
        return {
            description: `When life is unpredictable, systems save us. We're checking in when you’re unavailable, escalating responsibly when you’re silent, and executing your wishes if you can’t come back. You choose how often it pings you. You define what happens if you don’t respond. You stay in control — even if life says otherwise.`,
            items: [{
                description: 'Choose how often LifeCheck “pings” you — every 7, 14, 30, or 90 days. Missed check-ins don’t trigger anything at first. We’ll nudge gently to confirm you’re okay.',
                icon: 'images/services_lifechecks_step1.png',
                id: 1,
                title: 'Periodic Check-Ins'
            },{
                description: 'When a check-in goes unanswered, LifeCheck moves to gentle escalation mode. That means we follow up with your Trusted Contacts to determine next steps.',
                icon: 'images/services_lifechecks_step2.png',
                id: 2,
                title: `You Miss a Check-In`
            },{
                description: 'Still no response? LifeCheck reaches out to the person (or people) you’ve designated. It doesn’t say “something’s wrong.” It simply checks if they’ve heard from you recently. No data is shared. No assumptions made.',
                icon: 'images/services_lifechecks_step3.png',
                id: 3,
                title: 'Escalation'
            },{
                description: 'After multiple missed check-ins and no verification from your contacts, LifeCheck initiates your posthumous protocol. You decide what happens — and in what order. Everything is pre-set, encrypted, and triggered only when your silence says it’s time.',
                icon: 'images/services_lifechecks_step4.png',
                id: 4,
                title: 'Instructions Begin'
            }],
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {'LifeCheck Is Your '}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Invisible'}</span>
                    {` ${system.window.isMobile ? '\n' : ''}Digital Watchdog.`}
                </span>
            ),
            titles: ['Your Digital Life Monitor'.toUpperCase()]
        }
    }

    const getStats = () => {
        return {
            description: `Every day, millions of people live fully online lives — storing passwords, messages, assets, and memories in digital vaults. But when they die or disappear… their digital lives stay behind. Active. Vulnerable. Unmonitored. This isn’t hypothetical. It’s already happening — in every inbox, photo album, crypto wallet, and private account left untouched after someone’s last login.`,
            items: [{
                description: '83% of Americans say estate planning is important — but only 31% have actually written a will.',
                source: 'Trust & Will Estate Planning Report, 2025'
            },{
                description: 'Over 30 million Facebook accounts belong to deceased users. That number could grow to 4.9 billion by 2100.',
                source: `Trend Micro Digital Legacy Report, 2024`
            },{
                description: 'Just 4% of users designate a “digital executor” — leaving most families to guess passwords or lose access entirely.',
                source: 'Rocket Lawyer Study, 2022'
            },{
                description: `The percentage of U.S. adults with a will dropped from 42% in 2017 to 32% in 2024 — despite growing digital lives.`,
                source: 'Caring.com Estate Survey, 2024'
            }],
            subTitle: (
                <span style={{
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>
                    {'Digital Lives Are '}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Long'}</span>
                    {` ${system.window.isMobile ? '\n' : ''}But Human Ones Aren't. ${system.window.isMobile ? '\n' : ''}The `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Numbers'}</span>
                    {` Speak For Themselves.`}
                </span>
            ),
            titles: ['Unplanned. Unprotected'.toUpperCase()]
        }
    }

    return (
        <ServiceContainer
        academics={getAcademics()}
        cta={getCTA()}
        description={getDescription()}
        onDemoVideoClick={onDemoVideoClick}
        hero={getHeroSection()}
        overview={getOverview()}
        stats={getStats()}
        utils={utils}
        videoPoster={'images/services.life_checks.png'}
        videoSrc={'images/services.life_checks.mp4'} 
        videoStyle={{
            objectFit: system.window.isMobile ? 'none' : 'cover',
            objectPosition: system.window.isMobile ? '50% 10%' : null, 
        }} />
    )
}

export const ServicesLinkedAccounts = ({ utils }) => {

    const { system } = useDataStore();
    
    const onDemoVideoClick = () => {
        if(Utils.isMobile() === true) {
            window.open('https://www.youtube.com/watch?v=VM7kd3ENOl4');
            return;
        }
        utils.layer.open({
            Component: VideoPlayer.bind(this, { url: 'https://www.youtube.com/embed/VM7kd3ENOl4?si=smRy8ZajBfP-Ri3A' }),
            id: 'video_player'
        });
    }

    const getAcademics = () => {
        return {
            description: `Scholars and legal experts are sounding alarms: digital assets are now more complex than physical ones — and far less protected. Without structured access or deletion, your online accounts become security risks, legal hurdles, and emotional burdens. Most users die with dozens (or hundreds) of unmanaged accounts, leaving behind liability, data exposure, and legal confusion.`,
            items: [{
                description: 'The typical web user has 25 online accounts … families, companies and legislators are just starting to sort out who owns and has access after someone dies.',
                source: 'Pew Research Center. (2013)',
                url: 'https://www.pewresearch.org/short-reads/2013/12/02/what-happens-to-your-digital-life-after-death/'
            },{
                description: `Rather than accounts lingering untouched, causing confusion and security risk, proactive digital-legacy planning allows the bereaved to manage and preserve what’s meaningful.`,
                source: `George, A. (2024)`,
                url: 'https://www.researchgate.net/publication/377776089_Digital_Afterlife_Preserving_Online_Legacies_Beyond_Death'
            },{
                description: 'Executors are often locked out of key accounts unless the user gave explicit digital consent before death. Platform terms and privacy laws frequently override estate access.',
                source: 'Cardozo Law Review. (2022)',
                url: 'https://cardozolawreview.com/beyond-the-grave-a-fiduciarys-access-to-a-decedents-digital-assets/'
            },{
                description: `There is a significant gap between digital literacy and digital estate planning… Many individuals are unaware of the volume of accounts they’ve created, let alone how to pass them on or delete them.`,
                source: 'Park, J. (2024)',
                url: 'https://journals.sagepub.com/doi/full/10.1177/20563051241274676'
            }],
            scrollDescription: (
                <>
                {`LifeWipe exists because the systems `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'designed'}</span>
                {` to protect your privacy in life actively `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'block'}</span>
                {` your loved ones in death. Your bank accounts, cloud drives, and emails won’t `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'close'}</span>
                {` themselves.`}
                </>
            ),
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`What the `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Research'}</span>
                    {` ${system.window.isMobile ? '\n' : ''}Says About Digital Chaos`}
                </span>
            )
        };
    }

    const getCTA = () => {
        return {
            description: `Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`,
            subTitle: (
                <>
                {`Your Accounts `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Shouldn’t'}</span>
                {` Become Someone Else’s Problem.`}
                </>
            ),
            titles: ['Clean Up Your Digital Life'.toUpperCase()]
        };
    }

    const getDescription = () => {
        return (
            <>
            {`The internet doesn’t `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700,
            }}>{'forget'}</span>
            {` — and neither do the accounts you’ll leave behind. When you pass, your `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'accounts'}</span>
            {` often become inaccessible, unmanaged, or misused. They'll become a `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'liability'}</span>
            {`, not a legacy.`}
            </>
        )
    }

    const getHeroSection = () => {
        return {
            description: `Linked Accounts gives you one place to connect, control, and clean up your online presence — automatically, and on your terms.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`The Digital You Lives `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Everywhere'}</span>
                    {`. ${system.window.isMobile ? '\n' : ''}We Handle That.`}
                </span>
            ),
            titles: ['Linked Accounts'.toUpperCase(), 'Online Presence'.toUpperCase(), 'Digital Assets'.toUpperCase(), 'Linked Accounts'.toUpperCase()]
        }
    }

    const getOverview = () => {
        return {
            description: `How many accounts have you created over the years? Email. Banks. Cloud drives. Streaming services. Loyalty cards. Old photo apps. Imagine your family trying to find, access, or close all of them — without your logins. Linked Accounts makes sure that never happens. You link what matters while you're alive. We help control, transfer, or erase it when you can’t.`,
            items: [{
                description: `Connect the accounts that hold value — financial, emotional, or legal. This includes everything from your email inbox to cloud storage, crypto wallets, banks, and old social platforms you may not even use anymore. Each login is secured inside your encrypted profile, protected with two factor authentication.`,
                icon: 'images/services_linked_accounts_step1.png',
                id: 1,
                title: `The Accounts ${system.window.isMobile ? '\n' : ''}That Matter`
            },{
                description: `You choose what should happen to it in the event of your death or disappearance. Want your email archived and sent to a loved one? Done. Prefer to wipe your old Facebook account and close down a subscription app? That’s in your hands too. It's flexible, and it respects your privacy and intent.`,
                icon: 'images/services_linked_accounts_step2.png',
                id: 2,
                title: `Create Your ${system.window.isMobile ? '\n' : ''}Instructions`
            },{
                description: `Your instructions won’t run unless something activates them — and that part is up to you. You decide what counts as an emergency. It could be a failed LifeCheck, where you stop responding after a certain number of days. It could be a manual Emergency Trigger you launch during a crisis. Whatever your scenario, LifeWipe waits quietly until it’s time to act — and never before.`,
                icon: 'images/services_linked_accounts_step3.png',
                id: 3,
                title: `Set Your ${system.window.isMobile ? '\n' : ''}Triggers`
            },{
                description: `As you grow, so do your accounts. That’s why your Linked Account setup is easy to update. You can log in anytime to add new services, change what happens to each account, or reassign who gets access. Your setup doesn’t just preserve your data — it evolves with your life.`,
                icon: 'images/services_linked_accounts_step4.png',
                id: 4,
                title: `Review ${system.window.isMobile ? '\n' : ''}and Evolve`
            }],
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Take Control of `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Every'}</span>
                    {` Account ${system.window.isMobile ? '\n' : ''}You’ve Ever Created`}
                </span>
            ),
            titles: ['Connect and Protect'.toUpperCase()]
        }
    }

    const getStats = () => {
        return {
            description: `The average user has over 100 online accounts — across subscriptions, shopping sites, storage, social media, financial tools, and more. Without a plan, all of them stay active long after you're gone — creating risk, confusion, and stress for the people you leave behind.`,
            items: [{
                description: 'Only 12% of people include digital accounts in their estate plan.',
                source: 'AARP Digital Estate Report, 2022'
            },{
                description: '76% of users couldn’t access a loved one’s account after death.',
                source: `Statista Survey, 2023`
            },{
                description: 'Over 40% of online accounts remain active for years after the user dies.',
                source: 'Digital Legacy Coalition, 2024'
            },{
                description: `Less than 10% of services have clear posthumous account policies.`,
                source: 'Consumer Reports, 2023'
            }],
            subTitle: (
                <span style={{
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>
                    {`We Counted. You Have ${system.window.isMobile ? '\n' : ''}More `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600,
                        maxWidth: '100%',
                        whiteSpace: 'pre'
                    }}>{'Accounts'}</span>
                    {` Than You Realize.`}
                </span>
                
            ),
            titles: [`The Digital Age`.toUpperCase()]
        }
    }

    return (
        <ServiceContainer
        academics={getAcademics()}
        cta={getCTA()}
        description={getDescription()}
        onDemoVideoClick={onDemoVideoClick}
        hero={getHeroSection()}
        overview={getOverview()}
        stats={getStats()}
        utils={utils}
        videoPoster={'images/services.linked_accounts.png'}
        videoSrc={'images/services.linked_accounts.mp4'} 
        videoStyle={{
            objectFit: system.window.isMobile ? 'none' : 'cover',
            objectPosition: system.window.isMobile ? '25% 25%' : null
        }} />
    )
}

export const ServicesRequiem = ({ utils }) => {

    const { system } = useDataStore();
    
    const getAcademics = () => {
        return {
            description: `Scholars in tech ethics, cognitive psychology, and estate law are now aligned: intelligent assistance isn't just helpful — it’s essential. Without support, people delay planning, forget key items, and often pass without a clear or complete handoff. Requiem exists to change that. It gives users a guide that listens, adapts, and honors intent — even when they’re no longer here to explain it.`,
            items: [{
                description: 'Participants viewed AI-based afterlife agents as helpful in ensuring their intentions were remembered and respected… especially when human executors might forget, disagree, or not fully understand digital instructions.',
                source: 'Lei et al., 2025',
                url: 'https://arxiv.org/abs/2502.10924'
            },{
                description: `Persistent digital agents can extend the boundaries of personal identity and legacy, requiring ethical frameworks that prioritize consent, access control, and respectful handling of memory.`,
                source: `Kocarev & Koteska, 2020`,
                url: 'https://arxiv.org/abs/2012.14325'
            },{
                description: 'AI now curates, interprets, and extends personal memory — turning individual data into living systems of recall and reconstruction. This reshapes how identity persists beyond biological life.',
                source: 'Andrew Hoskins, 2024',
                url: 'https://www.cambridge.org/core/journals/memory-mind-and-media/article/ai-and-memory/BB2E4B113B826133E1B6C8DB6BACD192'
            },{
                description: `Digital continuity systems must prioritize autonomy, revocability, and informed consent. Without these, posthumous technology can risk violating the very legacy it claims to protect.`,
                source: 'Puzio, 2023',
                url: 'https://www.ivysci.com/en/articles/2531453__When_the_Digital_Continues_After_Death_Ethical_Perspectives_on_Death_Tech_and_the_Digital_Afterlife'
            }],
            scrollDescription: (
                <>
                {`Digital death is no longer a `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'fringe'}</span>
                {` conversation — it’s a mainstream problem with legal, emotional, and `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'ethical'}</span>
                {`  weight. Traditional estate tools fall short in `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 700,
                }}>{'helping'}</span>
                {` people manage their digital presence, intentions, and transitions.`}
                </>
            ),
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Assisted `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Digital '}</span>
                    {` Legacy ${system.window.isMobile ? '\n' : ''}Is the Future`}
                </span>
            )
        };
    }

    const getCTA = () => {
        return {
            description: `Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`,
            subTitle: (
                <>
                {`Design a Legacy That `}
                <span style={{
                    color: Appearance.colors.primary(),
                    fontFamily: 'Instrument Serif',
                    fontStyle: 'italic',
                    fontWeight: 600
                }}>{'Thinks'}</span>
                {` for You.`}
                </>
            ),
            titles: ['Start Building With Requiem'.toUpperCase()]
        };
    }

    const getDescription = () => {
        return (
            <>
            {`Requiem is your AI legacy guide — `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700,
            }}>{'built'}</span>
            {`  to help you organize, decide, and  `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'deliver'}</span>
            {` your digital afterlife plan. It turns overwhelm into `}
            <span style={{
                color: Appearance.colors.primary(),
                fontFamily: 'Instrument Serif',
                fontStyle: 'italic',
                fontWeight: 700
            }}>{'structure'}</span>
            {`, one question at a time.`}
            </>
        )
    }

    const getHeroSection = () => {
        return {
            description: `Requiem is your personal AI assistant, designed to help you build, refine, and finalize your digital legacy — with empathy, structure, and clarity.`,
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`Not Just Smart. `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Trained'}</span>
                    {` to ${system.window.isMobile ? '\n' : ''}Help You Grow.`}
                </span>
            ),
            titles: ['Requiem AI'.toUpperCase(), 'Legacy Guide'.toUpperCase(), 'Estate Architect'.toUpperCase(), 'Requiem AI'.toUpperCase()]
        }
    }

    const getOverview = () => {
        return {
            description: `Requiem is trained on every facet of LifeWipe. Whether you need a guide to help organize your digital life or a way to preserve your presence for those you leave behind, Requiem works in service of your intent — with precision, empathy, and full control.`,
            items: [{
                description: `Requiem asks intelligent questions about your values, files, beneficiaries, and digital footprint. It builds a structured legacy plan as you answer — so by the end, you’ve created something real without feeling overwhelmed.`,
                icon: 'images/services_requiem_step1.png',
                id: 1,
                title: `Requiem Listens ${system.window.isMobile ? '\n' : ''}and Builds`
            },{
                description: `As you add items to your Data Vault, connect accounts, or define emergency triggers, Requiem quietly observes your preferences (with your permission). The more you use LifeWipe, the more accurate and helpful it becomes.`,
                icon: 'images/services_requiem_step2.png',
                id: 2,
                title: `Learns From ${system.window.isMobile ? '\n' : ''}Your Activity`
            },{
                description: `You can allow Requiem to learn from your stored messages, letters, or reflections — or keep it limited to technical setup only. You choose what it sees, how much memory it carries, and whether it ever speaks to someone else on your behalf.`,
                icon: 'images/services_requiem_step3.png',
                id: 3,
                title: 'You Decide How \nMuch It Knows'
            },{
                description: `When enabled, Requiem can become a posthumous voice — delivering messages, reminders, or responses. Loved ones or legal contacts may interact with it to retrieve insights, hear memorial messages, or receive guidance.`,
                icon: 'images/services_requiem_step4.png',
                id: 4,
                title: `Your Legacy ${system.window.isMobile ? '\n' : ''}Interface`
            }],
            subTitle: (
                <span style={{
                    whiteSpace: 'pre'
                }}>
                    {`From Setup `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Assistant'}</span>
                    {` to ${system.window.isMobile ? '\n' : ''}Legacy Companion`}
                </span>
            ),
            titles: ['Guided, Not Automated'.toUpperCase()]
        }
    }

    const getStats = () => {
        return {
            description: `Building your digital legacy can feel overwhelming. What should you include? Who should receive what? Which accounts matter? Research shows that without guided support, most people avoid planning altogether — or only think about it once it's too late.`,
            items: [{
                description: `72% of adults say they feel “uncertain or anxious” when starting end-of-life digital planning — citing confusion, emotional resistance, and lack of guidance.`,
                source: 'AARP / Stanford Longevity Center, 2023'
            },{
                description: `3 in 4 people report not knowing how to properly organize or pass on digital assets like passwords, cloud files, or social media accounts to their heirs.`,
                source: 'Pew Research Digital Estate Brief, 2022'
            },{
                description: `Most estate attorneys say their clients “underestimate the complexity” of digital transitions — especially when no tools or structured guidance are in place.`,
                source: 'American Bar Association Tech Trends, 2023'
            },{
                description: `81% of users say they would use AI assistance, templates, or guided prompts to help manage their digital afterlife — if such tools were built with privacy in mind.`,
                source: 'Future of DeathTech Report, 2024'
            }],
            subTitle: (
                <span style={{
                    maxWidth: '100%',
                    whiteSpace: 'pre'
                }}>
                    {`Digital `}
                    <span style={{
                        color: Appearance.colors.primary(),
                        fontFamily: 'Instrument Serif',
                        fontStyle: 'italic',
                        fontWeight: 600
                    }}>{'Planning'}</span>
                    {` Is Too Hard Alone`}
                </span>
                
            ),
            titles: [`Clarity Requires a Guide`.toUpperCase()]
        }
    }

    return (
        <ServiceContainer
        academics={getAcademics()}
        cta={getCTA()}
        description={getDescription()}
        hero={getHeroSection()}
        overview={getOverview()}
        stats={getStats()}
        utils={utils}
        videoPoster={'images/services.requiem.png'}
        videoSrc={'images/services.requiem.mp4'} />
    )
}

const SlideInAnimated = ({ children, className, direction = 'left', style }) => {

    const ref = useRef(null)
    const { scrollYProgress } = useScroll({ 
        target: ref,
        offset: ['start end', 'center center']
    });

    const opacity = useTransform(scrollYProgress, [0, 0.65], [0, 1]);
    const x = useTransform(scrollYProgress, [0, 0.65], [direction === 'left' ? -250 : 250, 0]);
    const rotateX = useTransform(scrollYProgress, [0, 0.65], [25, 0]);
    const rotateY = useTransform(scrollYProgress, [0, 0.65], direction === 'left' ? [-45, 0] : [45, 0]);
    
    return (
        <motion.div
        className={className}
        ref={ref}
        style={{
            ...style,
            opacity,
            rotateX,
            rotateY,
            transformStyle: 'preserve-3d',
            x
        }}>
            {children}
        </motion.div>
    )
}

const SlideUpAnimated = ({ children, className, position = 'center', style }) => {

    const ref = useRef(null)
    const { scrollYProgress } = useScroll({ 
        target: ref,
        offset: ['start end', 'center center']
    });
    
    const opacity = useTransform(scrollYProgress, [0, 0.85], [0, 1]);
    const x = useTransform(scrollYProgress, [0, 0.85], [position === 'left' ? -125 : 125, 0]);
    const y = useTransform(scrollYProgress, [0, 0.85], [500, 0]);
    const rotateX = useTransform(scrollYProgress, [0, 0.85], [45, 0]);
    const rotateY = useTransform(scrollYProgress, [0, 0.85], position === 'left' ? [-45, 0] : [45, 0]);
    
    return (
        <motion.div
        className={className}
        ref={ref}
        style={{
            ...style,
            opacity,
            y,
            transformStyle: 'preserve-3d',
            ...position !== 'center' && {
                rotateX,
                rotateY,
                translateY: y,
                x
            }
        }}>
            {children}
        </motion.div>
    )
}

export const StartConversation = ({ style, typed = false, utils }) => {

    const { system } = useDataStore();
    
    return (
        <AnimatedElement
        {...utils.animations.get('panel')}
        animateWithPage={true}
        style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            ...style
        }}>
            <div 
            className={'text-left text-xl-center'}
            style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.panelBackground(),
                borderRadius: system.window.isMobile ? 0 : 10,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                marginBottom: Appearance.styles.panelSpacing(),
                maxWidth: 1250,
                padding: Appearance.styles.panelPadding(),
                width: '100%'
            }}>
                {typed === true && (
                    <ReactTyped 
                    backSpeed={100} 
                    loop={false} 
                    strings={[`Ready to Secure Your Digital Legacy?`.toUpperCase()]} 
                    style={{
                        ...Appearance.textStyles.panelHeaderText(),
                        marginBottom: 16
                    }}
                    typeSpeed={150} />
                )}
                {typed === false && (
                    <span style={{
                    ...Appearance.textStyles.panelHeaderText(),
                    marginBottom: 16
                }}>{`Ready to Secure Your Digital Legacy?`.toUpperCase()}</span>
                )}
                <span 
                className={'mb-4'}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    maxWidth: 1000
                }}>{`Our comprehensive digital legacy management system ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`}</span>

                <div 
                className={'row m-0'}
                style={{
                    maxWidth: Utils.isMobile() ? null : '50%',
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-6 px-0 pt-0 pb-2 px-xl-2'}>
                        <Button
                        color={Appearance.colors.primary()}
                        containerStyle={{
                            width: '100%'
                        }}
                        label={'Get Started'}
                        onClick={utils.navigation.set.bind(this, { view: 'onboarding' })}
                        type={'large'} /> 
                    </div>
                    <div className={'col-12 col-xl-6 p-0 px-xl-2'}>
                        <Button
                        color={Appearance.colors.secondary()}
                        containerStyle={{
                            width: '100%'
                        }}
                        label={'Read FAQ'}
                        onClick={utils.navigation.set.bind(this, { view: 'faq' })}
                        type={'large'} /> 
                    </div>
                </div>
            </div>
        </AnimatedElement>
    )
}

export function useIconTransform({ x, y, scale, planeX, planeY, xOffset, yOffset, deviceWidth, deviceHeight, icon }) {

    // keep track of our calculated x and y scales - we'll
    // set scale to the smallest of the two
    const xScale = useRef(1);
    const yScale = useRef(1);

    const createScreenRange = (axis, deviceSize, icon) => [
        -60,
        80,
        deviceSize - (icon.size + icon.margin) / 2 - 80,
        deviceSize - (icon.size + icon.margin) / 2 + 60
    ];

    const createTransformMappers = (deviceWidth, deviceHeight, icon) => {

        let scaleRange = [0, 1, 1, 0];
        let xRange = createScreenRange("width", deviceWidth, icon);
        let yRange = createScreenRange("height", deviceHeight, icon);
        
        return {
            mapScreenToXOffset: transform(xRange, [50, 0, 0, -50]),
            mapScreenToYOffset: transform(yRange, [50, 0, 0, -50]),
            mapScreenXToScale: transform(xRange, scaleRange),
            mapScreenYToScale: transform(yRange, scaleRange)
        };
    };
    
    // create transform mappers based on device dimensions
    const mappers = useMemo(() => 
        createTransformMappers(deviceWidth, deviceHeight, icon),
        [deviceWidth, deviceHeight, icon]
    );

    // function to update transforms based on planeX
    const updateX = (v) => {
        const screenOffset = v + xOffset + 20;
        xScale.current = mappers.mapScreenXToScale(screenOffset);
        const newScale = Math.min(xScale.current, yScale.current);
        scale.set(newScale);
        x.set(mappers.mapScreenToXOffset(screenOffset));
    };

    // function to update transforms based on planeY
    const updateY = (v) => {
        let screenOffset = v + yOffset + 20;
        yScale.current = mappers.mapScreenYToScale(screenOffset);
        let newScale = Math.min(xScale.current, yScale.current);
        scale.set(newScale);
        y.set(mappers.mapScreenToYOffset(screenOffset));
    };

    // initialize on mount with current values
    useEffect(() => {
        updateX(planeX.get());
        updateY(planeY.get());
    }, []);

    // listen to plane changes
    useMotionValueEvent(planeX, "change", updateX);
    useMotionValueEvent(planeY, "change", updateY);
}

export const LeadLanding = ({ utils }) => {

    const { advertising_metadata, request, system, setRequiem } = useDataStore();
    const [edits, setEdits] = useState({});
    const [loading, setLoading] = useState(false);

    const onBookConsultationClick = () => {
        utils.layer.open({
            Component: BookConsultation,
            id: 'book_consultation'
        });
    }

    const onPlanSelectionClick = tier => {
        onSelectPlan(tier);
    }

    const onSelectPlan = tier => {
        utils.layer.open({
            Component: NewSubscription.bind(this, { 
                freeTrial: request.focus !== 'third_party_referral',
                selection: tier,
                showStarterPlan: true 
            }),
            id: 'new_subscription'
        });
    }

    const onShowReferralPartnerPromotion = async code => {
        try {

            // submit request to server to validate promo code
            setLoading('start_now');
            let { promotion } = await Request.post(utils, '/subscriptions/', {
                code: code,
                type: 'validate_promotional_code'
            });

            // show alert to customer with details around promotion
            setLoading(false);
            utils.alert.show({
                title: promotion.title,
                message: `Sign up for coverage today to take advantage of the ${promotion.title} (${Subscription.promotions.overview(promotion)}). Discounts are automatically applied at checkout.`,
                buttons: [{
                    key: 'view_plans',
                    title: 'View Plans',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Close',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'view_plans') {
                        onSelectPlan();
                        return;
                    }
                }
            });
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops',
                message: `There was an issue retrieving the details for your offer. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onStartNowClick = origin => {

        // log event with firebase
        utils.analytics.log('cta_click', {
            origin,
            view_name: 'lead_landing'
        });

        // determine if a referral partner pipeline is active
        if(request.focus === 'third_party_referral') {
            let code = getCodeForReferralProvider();
            if(code) {
                onShowReferralPartnerPromotion(code);
            }
            return;
        }

        // request tier selection
        onSelectPlan();
    };

    const onAskRequiemClick = key => {
        setRequiem(requiem => {
            requiem.request = {
                loading: true,
                preset: key
            };
            return requiem;
        });
    }

    const getCodeForReferralProvider = () => {
        switch(request.provider) {
            case 'ebg':
            return 'jKR64hiO5QJ298l97ko110DfH'

            default:
            return null;
        }
    }

    const getQuotedContentContainer = key => {

        let message = null;
        switch(key) {
            case "beyond_passwords_security":
            message = `What makes a comprehensive digital security plan different from just using strong passwords? What other protections should I consider?`;
            break;
            
            case "consultation_benefits":
            message = `What are the benefits of a LifeWipe consultation, and how can a short call help me get started with my digital protection plan?`;
            break;
            
            case "emergency_activation_examples":
            message = `How does LifeWipe actually help during an emergency? Can you give me examples of how the protection plan activates when something unexpected happens?`;
            break;
            
            case "free_plan_vs_paid_features":
            message = `What's included in the free starter plan, and how do I know when it's time to upgrade? \nWhat additional features do I get with a paid subscription?`;
            break;
            
            case "non_technical_setup_explainer":
            message = `I'm not very technical. Can you walk me through the basic steps of setting up a digital protection plan and explain what information I need to gather?`;
            break;
            
            case "personalized_help_next_steps":
            message = `I have more questions about my specific situation. \nCan you help me understand which features would work best for my family's needs and how to get started?`;
            break;

            case "risk_of_no_digital_protection":
            message = `What are the real risks of not having a digital protection plan, and what could happen to my accounts and files if I face a device emergency or sudden absence?`;
            break;

            default:
            return null;
        }

        return (
            <div 
            className={`text-button`}
            onClick={onAskRequiemClick.bind(this, key)}
            style={{
                alignItems: 'center',
                backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.05),
                border: `2px dashed ${Appearance.colors.primary()}`,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                padding: 16,
                textAlign: 'left',
                whiteSpace: 'pre-wrap'
            }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.15),
                    borderRadius: '50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    flex: 'none',
                    height: 50,
                    marginRight: 12,
                    padding: 12,
                    width: 50
                }}>
                    <img
                    src={'images/ask-requiem-green.png'}
                    style={{
                        height: '100%',
                        width: '100%'
                    }} />
                </div>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    color: Appearance.colors.primary(),
                    fontWeight: 600
                }}>{`Ask Requiem: "${message}"`}</span>
            </div>
        )
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getCtaLabel = () => {
        let label = system.window.isMobile ? 'Start Free — No Credit Card' : 'Start Free — No Credit Card Required';
        if(request.focus === 'third_party_referral') {
            label = 'View Your Exclusive Offer';
        }
        return label;
    }

    const getLeadGenFields = () => {
        return [{
            collapsed: null,
            id: 'details',
            items: [{
                component: 'textfield',
                id: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                id: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                id: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                id: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                required: false,
                title: 'Phone Number (Optional)',
                value: edits.phone_number
            },{
                component: 'textview',
                id: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'What Do You Need Help With?',
                value: edits.description
            }]
        }];
    }

    const getCardTitleStyles = () => ({
        ...Appearance.textStyles.panelSubHeaderText(),
        display: 'block',
        fontSize: 16
    });

    const renderHero = () => {

        let items = [{
            image: 'images/lead-landing-instructions-green.png',
            title: 'Take Control Of What Matters',
            body: 'Your digital assets, your rules. Decide exactly what happens to each account and file.'
        },{
            image: 'images/lead-landing-fingerprint-secondary-green.png',
            title: 'Security-First Approach',
            body: 'Bank-level security with multiple verification checkpoints protecting your sensitive information.'
        },{
            image: 'images/lead-landing-emergencies-black.png',
            title: 'Built for Real Emergencies',
            body: 'Automated protocols activate when you need them most, following your exact instructions.'
        }];
        
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{
                    ...Appearance.styles.unstyledResponsivePanel(),
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-6 p-4 p-xl-5'}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            maxWidth: 750
                        }}>{'STOP LIVING YOUR DIGITAL LIFE ON AUTOPILOT'}</span>
                        <span 
                        className={'mb-4 mb-xl-5'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block'
                        }}>{`LifeWipe gives you one secure place to decide what happens to your accounts, files, and messages—before an emergency, not after. Whether it's a lost device, unexpected silence, or verified death, your plan activates automatically, following your exact instructions.`}</span>
                        <div className={'row mx-0 mt-0 mb-3 mb-xl-4'}>
                            {items.map((item, idx) => (
                                <div 
                                className={'col-12 p-0 pb-3'}
                                key={idx}
                                style={{
                                    textAlign: 'left'
                                }}>
                                    <div 
                                    className={'p-3 p-xl-3 d-flex flex-column flex-xl-row align-items-start align-items-xl-center'}
                                    style={{ 
                                        ...Appearance.styles.unstyledPanel(),
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <img 
                                        className={'mb-3 mb-xl-0 mr-2 mr-xl-3'}
                                        src={item.image}
                                        style={{
                                            height: 50,
                                            width: 50
                                        }} />
                                        <div style={{ 
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <span 
                                            className={'mb-1'}
                                            style={{
                                                ...getCardTitleStyles(),
                                                marginBottom: 2
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText()
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button
                        color={'primary'}
                        label={getCtaLabel()}
                        loading={loading === 'start_now'}
                        onClick={onStartNowClick.bind(this, 'hero_cta')}
                        type={'large'} />
                        <div className={'d-flex d-xl-none pt-2'}>
                            <Button
                            color={'dark'}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={'Book Your Free Consultation'}
                            onClick={onBookConsultationClick}
                            type={'large'} />
                        </div>
                    </div>
                    <div 
                    className={'col-12 col-xl-6 p-0 d-none d-xl-flex'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            minHeight: 400,
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            {getLeadGenerationForm()}
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const renderProblem = () => {

        let items = [{
            title: 'SCATTERED ACCOUNTS',
            body: 'Banking, social, crypto, subscriptions—hard to track and even harder to secure or hand off in a crisis.'
        },{
            title: 'NO CLEAR HANDOFF',
            body: `Loved ones aren't sure what to do, who to call, or how to access what matters without risking accounts or privacy.`
        },{
            title: 'SECURITY WORRIES',
            body: 'You want things executed safely without exposing private info, breaking anything, or relying on an unverified note or shared password.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <div 
                    className={'col-12 col-xl-5 p-0'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(0% 0, 92% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }}>
                                <source src="images/lead-capture-video.mp4" type="video/mp4" />
                            </video>
                            <div style={{
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)',
                                height: '100%',
                                left: 0,
                                position: 'absolute',
                                top: 0,
                                width: '100%'
                            }} />
                        </div>
                    </div>
                    <div 
                    className={'col-12 col-xl-7 p-4 p-xl-5'} 
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        position: 'relative'
                    }}>
                        <AltBadge 
                        className={'mb-3 mb-xl-4'}
                        content={{ 
                            color: Appearance.colors.secondary(), 
                            text: 'THE PROBLEM' 
                        }} 
                        style={{ 
                            marginLeft: 0,
                            width: 125
                        }} />
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            display: 'block'
                        }}>{'Right now, your digital life is scattered and vulnerable'.toUpperCase()}</span>
                        <span 
                        className={'mb-4 mb-xl-5'}
                        style={{
                            ...Appearance.textStyles.defaultText()
                        }}>{'Most people have no clear plan for their accounts, files, or messages if something happens. Loved ones and teammates are left guessing—or locked out entirely.'}</span>
                        
                        <div 
                        className={'row mx-0 mb-3 mb-xl-5'} 
                        style={{ 
                            justifyContent: 'center' 
                        }}>
                            {items.map((item, idx) => (
                                <div 
                                key={idx} 
                                className={`col-12 col-md-4 p-0 pb-2 pb-xl-0 pl-xl-${idx === 0 ? 0 : 2} pr-xl-${idx === 2 ? 0 : 2}`}>
                                    <div 
                                    className={'p-3 p-xl-4'}
                                    style={{
                                        backgroundColor: Appearance.colors.panelBackground(),
                                        border: `2px solid ${Appearance.colors.divider()}`,
                                        borderRadius: 12,
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <span 
                                        className={'mb-1 mb-xl-2'}
                                        style={{
                                            ...getCardTitleStyles()
                                        }}>{item.title}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText()
                                        }}>{item.body}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {getQuotedContentContainer('risk_of_no_digital_protection')}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const renderHowItWorks = () => {

        let items = [{
            image: 'images/lead-landing-edit-green.png',
            title: 'TELL US WHAT MATTERS',
            body: `Pick the accounts, files, and people that matter most. You're guided through simple questions instead of technical forms. No jargon, no confusion—just clear prompts that help you think through what you need to protect and share.`
        },{
            image: 'images/lead-landing-user-secondary-green.png',
            title: 'ADD YOUR CONTENT',
            body: `Link key accounts, upload important documents to the Secure Data Vault, and add trusted contacts who should be involved. Everything is encrypted at rest and in transit, with multiple verification checkpoints protecting your sensitive information.`
        },{
            image: 'images/lead-landing-instructions-black.png',
            title: 'SET YOUR RULES',
            body: `Configure LifeChecks and Emergency Triggers so that if you go silent or hit the panic button, your instructions run—backups, wipes, and legacy deliveries included. Your plan becomes your safety net, active and ready without requiring constant attention.`
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 850
                    }}>{'FROM SCATTERED TO SECURE IN THREE GUIDED STEPS'}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        maxWidth: 1250
                    }}>{`Setting up comprehensive digital protection might sound complex, but we've broken it down into a simple, guided process. Most people complete their initial setup in under an hour—and you can always adjust your plan as life changes. You don't need to be technical. LifeWipe walks you through every step with plain language and clear guidance.`}</span>
                    
                    <div 
                    className={'row mx-0 mb-3 mb-xl-5'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                    {getQuotedContentContainer('non_technical_setup_explainer')}
                </div>
            </AnimatedElement>
        )
    };

    const getCardElement = (item, index) => {
        return (
            <div 
            key={index} 
            className={'col-12 col-md-4 p-0 px-xl-2 mb-3'}>
                <div 
                className={'p-3 p-xl-4 d-flex flex-column align-items-start'}
                style={{ 
                    ...Appearance.styles.unstyledPanel(),
                    height: '100%',
                    textAlign: 'left'
                }}>
                    {typeof(item.image) === 'string' && (
                        <img 
                        className={'mb-3'}
                        src={item.image}
                        style={{
                            height: 50,
                            width: 50
                        }} />
                    )}
                    <span
                    className={'mb-2'}
                    style={{
                        ...getCardTitleStyles()
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{item.body}</span>
                </div>
            </div>
        )
    }

    const renderTestimonials = () => {
    
        let items = [{
            avatar: 'images/testimonial-avatar-01.jpg',
            quote: `I have three kids, and if something happened to me, I didn’t want them left guessing. LifeWipe let me put my passwords, photos, and important things in one place, so they’re taken care of.`,
            name: 'Anna R.',
            role: 'School Bus Driver'
        },{
            avatar: 'images/testimonial-avatar-02.jpg',
            quote: `I’ve got bank stuff, photos, and accounts spread everywhere. LifeWipe gave me one place to put it all, so my kids and my brother wouldn’t have to deal with a mess.`,
            name: 'Tyler H.',
            role: 'Contractor / Construction'
        },{
            avatar: 'images/testimonial-avatar-03.jpg',
            quote: `My daughters live in another state, and I didn’t want them trying to piece things together from afar. LifeWipe helps me keep everything organized so they’re not left scrambling.`,
            name: 'Ashley B.',
            role: 'Retail Security'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{
                    ...Appearance.styles.unstyledResponsivePanel(),
                    width: '100%'
                }}>
                    <div 
                    className={'col-12 p-4 p-xl-5 text-left text-xl-center'}
                    style={{ 
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                    }}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            lineHeight: 1.25,
                            maxWidth: 850
                        }}>{'HEAR FROM REAL PEOPLE WHO TRUST LIFEWIPE'}</span>
                        <span 
                        className={'mb-4 mb-xl-5'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            maxWidth: 1250
                        }}>{`The decision to protect your digital legacy is deeply personal. It's not just about features or technology—it's about peace of mind, family security, and knowing you've done the right thing for the people who matter most. People from all walks of life are already using LifeWipe to gain control over their digital assets and ensure their loved ones won't face confusion or chaos during difficult times. Here's what some of them have to say about their experience.`}</span>
                        <div 
                        className={'row mx-0 mb-3 mb-xl-4'} 
                        style={{ 
                            justifyContent: 'center'
                        }}>
                            {items.map((item, idx) => (
                                <div 
                                key={idx} 
                                className={'col-12 col-md-4 p-0 px-xl-2 mb-3'}>
                                    <div 
                                    className={'p-3 p-xl-4'}
                                    style={{ 
                                        backgroundColor: Appearance.colors.panelBackground(),
                                        border: `2px solid ${Appearance.colors.divider()}`,
                                        borderRadius: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            flexGrow: 1,
                                            fontStyle: 'italic',
                                            marginBottom: 16
                                        }}>{`"${item.quote}"`}</span>
                                        <div style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            flexDirection: 'row'
                                        }}>
                                            <img 
                                            src={item.avatar}
                                            style={{
                                                borderRadius: '50%',
                                                height: 48,
                                                marginRight: 12,
                                                objectFit: 'cover',
                                                width: 48
                                            }} />
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <span style={{
                                                    ...Appearance.textStyles.defaultText(),
                                                    display: 'block',
                                                    fontWeight: 600,
                                                    marginBottom: 2
                                                }}>{item.name}</span>
                                                <span style={{
                                                    ...Appearance.textStyles.supporting(),
                                                    fontSize: 13
                                                }}>{item.role}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {getQuotedContentContainer('emergency_activation_examples')}
                    </div>
                </div>
            </AnimatedElement>
        )
    };


    const renderTrustSignals = () => {

        let items = [{
            image: 'images/lead-landing-security-green.png',
            title: 'SECURITY-FIRST APPROACH',
            body: 'Strong encryption, layered authentication protocols, and real-time wipe capabilities ensure your digital assets remain secure under all circumstances.'
        },{
            image: 'images/lead-landing-document-secondary-green.png',
            title: 'CLEAR, AUDITABLE PROTOCOLS',
            body: 'Every action taken on your behalf is logged, verified, and transparent. You maintain complete visibility and control over all operations.'
        },{
            image: 'images/lead-landing-fingerprint-black.png',
            title: 'BUILT BY SECURITY EXPERTS',
            body: 'Our platform was designed by former intelligence officers, cybersecurity engineers, digital forensics specialists, and privacy advocates who know how to protect what matters most.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 850
                    }}>{'TRUSTED BY PRIVACY-CONSCIOUS INDIVIDUALS AND TEAMS WORLDWIDE'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        maxWidth: 1250
                    }}>{`LifeWipe isn't just another password manager or cloud backup service. We're a comprehensive digital life management platform built by experts who understand what's at stake when security and legacy planning intersect.`}</span>
                    <div 
                    className={'row mx-0 mb-3 mb-xl-4'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                    {getQuotedContentContainer('beyond_passwords_security')}
                </div>
            </AnimatedElement>
        )
    };


    const renderPricing = () => (
        <AnimatedElement 
        {...utils.animations.get('panel')} 
        animateWithPage={false}
        useContentDivider={true}
        visible={true}>
            <div 
            className={'p-4 p-xl-5 text-left text-xl-center'}
            style={{ 
                ...Appearance.styles.unstyledResponsivePanel(),
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <span 
                className={'mb-3 mb-xl-4'}
                style={{
                    ...Appearance.textStyles.leadLandingTitle(),
                    maxWidth: 850
                }}>{'START FREE. UPGRADE ONLY IF IT EARNS YOUR TRUST.'}</span>
                <span
                className={'mb-4 mb-xl-5'}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    maxWidth: 1250
                }}>{`We believe in earning your business through value, not pressure. That's why you can start with LifeWipe completely free—no credit card required, no time limits, no surprise charges. Build your complete digital protection plan, explore every feature, connect your accounts, and see how it all works before deciding if a paid upgrade makes sense for you.`}</span>
                <div 
                className={'mb-4'}
                style={{ 
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <SubscriptionOptions
                    embedded={true}
                    minimal={true}
                    onSelectionClick={onPlanSelectionClick}
                    utils={utils} />        
                </div>
                {getQuotedContentContainer('free_plan_vs_paid_features')}
            </div>
        </AnimatedElement>
    );



    const renderCTA = () => (
        <AnimatedElement 
        {...utils.animations.get('panel')} 
        animateWithPage={false}
        useContentDivider={true}
        visible={true}>
            <div 
            className={'row mx-0'} 
            style={{ 
                ...Appearance.styles.unstyledResponsivePanel(),
                width: '100%'
            }}>
                <div className={'col-12 col-xl-6 p-4 p-xl-5'}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        lineHeight: 1.25,
                        maxWidth: 850
                    }}>{'TALK TO A HUMAN FOR 15 MINUTES AND LEAVE WITH A CLEAR PLAN'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        maxWidth: 1250
                    }}>{`Not ready to dive in alone? Book a short consultation with our team. We'll help you map out your digital life, understand your protection options, and build a draft plan together—with zero pressure to commit.`}</span>
                    <div 
                    className={'p-3 p-xl-4 mb-4'}
                    style={{ 
                        ...Appearance.styles.unstyledPanel(),
                        textAlign: 'left'
                    }}>
                        <span 
                        className={'mb-3'}
                        style={{
                            ...getCardTitleStyles()
                        }}>{'WHAT YOU GET IN 15 MINUTES:'}</span>
                        <ul style={{
                            ...Appearance.textStyles.defaultText(),
                            lineHeight: 1.6,
                            marginBottom: 0,
                            paddingLeft: 18
                        }}>
                            <li style={{
                                marginBottom: 8
                            }}>{'Map your key accounts, vault items, and trusted contacts into a draft LifeWipe plan'}</li>
                            <li style={{
                                marginBottom: 8
                            }}>{'Walk through LifeChecks and Emergency Triggers so you understand exactly how they protect you'}</li>
                            <li>{'Get rollout suggestions for family or team—no pressure, no obligation'}</li>
                        </ul>
                    </div>
                    {getQuotedContentContainer('consultation_benefits')}
                    <div className={'d-flex d-xl-none mt-3'}>
                        <Button
                        color={'dark'}
                        containerStyle={{
                            width: '100%'
                        }}
                        label={'Book Your Free Consultation'}
                        onClick={onBookConsultationClick}
                        type={'large'} />
                    </div>
                </div>
                <div className={'col-12 col-xl-6 p-0 d-none d-xl-flex'}>
                    {getLeadGenerationForm()}
                </div>
            </div>
        </AnimatedElement>
    );

    const onSubmitClick = async() => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getLeadGenFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/utils/', {
                ...edits,
                metadata: { advertising_metadata },
                origin: 'lead_landing',
                type: 'new_website_contact_form'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: 'We have received your contact request and will reach out to you shortly.',
                onClick: setEdits.bind(this, {})
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your contact request. ${e.message || 'An unknown error occurred'}`,
                origin: 'contact.submit_click',
                title: 'Oops!'
            });
        }
    }

    const getLeadGenerationForm = () => (
        <div 
        className={'p-5'}
        style={{
            backgroundColor: Appearance.colors.divider(),
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            ...system.window.isMobile === false && {
                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
            }
        }}>
            <div style={{
                alignSelf: 'flex-end',
                width: '80%'
            }}>
                <AltFieldMapper
                fields={getLeadGenFields()}
                utils={utils} />
                <Button
                color={'dark'}
                containerStyle={{
                    marginBottom: 8,
                    marginTop: 16
                }}
                label={'Talk to a Human — 15-Min Consultation'}
                loading={loading === 'submit'}
                onClick={onSubmitClick}
                type={'large'} />
                <span style={{
                    ...Appearance.textStyles.supporting(),
                    display: 'block',
                    fontSize: 12,
                    lineHeight: 1.4,
                    marginTop: 16
                }}>{`We respond within one business day. No surprise commitments—just 100% human follow-up from someone who genuinely wants to help.`}</span>
            </div>
        </div>
    )

    const renderFAQ = () => {

        const faqs = [{
            q: 'Is my data safe?',
            a: 'Yes. We protect your info and require checks before anything important happens.'
        },{
            q: 'Do I need to be technical?',
            a: 'No. We guide you in plain language. You can also ask us for help anytime.'
        },{
            q: 'Can this work for family and small teams?',
            a: 'Yes. Add the people you trust and give them clear instructions.'
        },{
            q: 'How quickly do you reply?',
            a: 'We respond within one business day. If something urgent happens, your plan already spells out what to do.'
        },{
            q: 'Can someone help me set it up?',
            a: 'Yes. You can start on your own or book a short consult. No pressure, no commitment.'
        },{
            q: 'Do I need a credit card to start?',
            a: 'No. You can start with LifeWipe for free and decide later if you want to upgrade.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        display: 'block',
                        maxWidth: 850,
                        width: '100%'
                    }}>{'COMMON QUESTIONS'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        maxWidth: 1250
                    }}>{`Below are answers to some of the most common questions we hear, covering how LifeWipe works, how your information is protected, and what control you maintain throughout the process. Whether you’re just exploring or actively considering next steps, these answers are here to help you understand your options clearly.`}</span>
                    <div 
                    className={'row mx-0 mb-3 mb-xl-4'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {faqs.map((faq, index) => (
                            <div 
                            key={index} 
                            className={'col-12 col-xl-6 p-0 px-xl-2 mb-3'}>
                                <div 
                                className={'p-3 p-xl-4'}
                                style={{
                                    ...Appearance.styles.unstyledPanel(),
                                    height: '100%',
                                    textAlign: 'left'
                                }}>
                                    <span 
                                    className={'mb-2'}
                                    style={{
                                        ...getCardTitleStyles()
                                    }}>{faq.q.toUpperCase()}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText()
                                    }}>{faq.a}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {getQuotedContentContainer('personalized_help_next_steps')}
                </div>
            </AnimatedElement>
        );
    };

    const renderTakeControl = () => {

        let items = [{
            image: 'images/lead-landing-free-gradient.png',
            title: 'START FREE',
            body: 'No credit card required. Begin protecting your most important accounts and files right now with our comprehensive Starter plan.'
        },{
            image: 'images/lead-landing-user-secondary-green.png',
            title: 'TALK TO OUR TEAM',
            body: 'Book a 15-minute consultation to map out your specific needs and get personalized guidance from our experts.'
        },{
            image: 'images/lead-landing-ready-black.png',
            title: 'UPGRADE WHEN READY',
            body: 'Explore LifeWipe at your own pace. Upgrade to Pro only when you\'re ready for advanced features and priority support.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        display: 'block'
                    }}>{'TAKE CONTROL OF YOUR DIGITAL LIFE TODAY'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        maxWidth: 1250
                    }}>{`Your digital life holds your memories, your finances, your connections, and your legacy. Taking a few steps now gives you—and the people who rely on you—clarity, security, and control. Without a plan, accounts, files, and digital assets are left exposed and uncertain. There’s no need to leave loved ones or teammates guessing when protection and clarity are within reach.`}</span>
                    
                    <div 
                    className={'row mx-0 mb-4 mb-xl-5'} 
                    style={{ 
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        {items.map(getCardElement)}
                    </div>

                    <div 
                    className={'row mx-0 mb-4 mb-xl-5'}
                    style={{
                        maxWidth: Utils.isMobile() ? null : 850,
                        width: '100%'
                    }}>
                        <div className={'col-12 col-xl-6 px-0 pt-0 pb-2 px-xl-2'}>
                            <Button
                            color={Appearance.colors.primary()}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={getCtaLabel()}
                            loading={loading === 'start_now'}
                            onClick={onStartNowClick.bind(this, 'take_control_cta')}
                            type={'large'} /> 
                        </div>
                        <div className={'col-12 col-xl-6 p-0 px-xl-2'}>
                            <Button
                            color={'dark'}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={'Book Your Free Consultation'}
                            onClick={onBookConsultationClick}
                            type={'large'} /> 
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            <div style={{
                maxWidth: 1500,
                width: '100%'
            }}>
                {renderHero()}
                {renderProblem()}
                {renderHowItWorks()}
                {renderTrustSignals()}
                {renderTestimonials()}
                {renderPricing()}
                {renderCTA()}
                {renderFAQ()}
                {renderTakeControl()}
            </div>
        </div>
    );
};

export const LeadLandingUrgency = ({ utils }) => {

    const { advertising_metadata, promo_code, request, user, show_free_trial = true, system, setRequiem } = useDataStore();
    const [edits, setEdits] = useState({});
    const [loading, setLoading] = useState(false);

    const onBookConsultationClick = () => {
        utils.layer.open({
            Component: BookConsultation,
            id: 'book_consultation'
        });
    }

    const onSelectPlan = () => {
        console.log(show_free_trial)
        utils.layer.open({
            Component: NewSubscription.bind(this, { 
                freeTrial: show_free_trial,
                promoCode: promo_code
            }),
            id: 'new_subscription'
        });
    }

    const onShowStarterPlan = async () => {
        try {


            // fetch list of available tiers and find the requested plan
            setLoading(true);
            let { tiers } = await Request.get(utils, '/subscriptions/', { 
                include_starter_plan: true,
                type: 'available_tiers' 
            });

            // throw an error if we couldn't find the plan
            let tier = tiers.find(tier => tier.metadata.is_starter_plan === true);
            if(!tier) {
                throw new Error('The Starter Plan is not currently available. Please contact support for assistance.');
            }

            // open layer to configure and purchase subscription
            setLoading(false);
            utils.layer.open({
                Component: NewSubscription.bind(this, { 
                    freeTrial: true, 
                    selection: tier 
                }),
                id: 'new_subscription'
            });
            
        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue preparing the Starter Plan. ${e.message || 'An unknown error occurred'}`,
                origin: 'lead_landing_urgency.show_starter_plan'
            });
        }
    }

    const onStartNowClick = origin => {

        // log event with firebase
        utils.analytics.log('cta_click', {
            origin,
            view_name: 'lead_landing_paid'
        });

        // determine if we need to show plan selection
        if(request.auto_starter_plan !== true) {
            onSelectPlan();
            return;
        }

        // determine if a user is already signed in
        if(user) {
            return onShowStarterPlan();
        }
        
        // show user signup flow for default mode 
        utils.user.request.signup({ onComplete: onShowStarterPlan });
    };

    const onAskRequiemClick = key => {
        setRequiem(requiem => {
            requiem.request = {
                loading: true,
                preset: key
            };
            return requiem;
        });
    }

    const getQuotedContentContainer = key => {

        let message = null;
        switch(key) {
            case "beyond_passwords_security":
            message = `What makes a comprehensive digital security plan different from just using strong passwords? What other protections should I consider?`;
            break;
            
            case "consultation_benefits":
            message = `What are the benefits of a LifeWipe consultation, and how can a short call help me get started with my digital protection plan?`;
            break;
            
            case "emergency_activation_examples":
            message = `How does LifeWipe actually help during an emergency? Can you give me examples of how the protection plan activates when something unexpected happens?`;
            break;
            
            case "free_plan_vs_paid_features":
            message = `What's included in the free starter plan, and how do I know when it's time to upgrade? \nWhat additional features do I get with a paid subscription?`;
            break;
            
            case "non_technical_setup_explainer":
            message = `I'm not very technical. Can you walk me through the basic steps of setting up a digital protection plan and explain what information I need to gather?`;
            break;
            
            case "personalized_help_next_steps":
            message = `I have more questions about my specific situation. \nCan you help me understand which features would work best for my family's needs and how to get started?`;
            break;

            case "risk_of_no_digital_protection":
            message = `What are the real risks of not having a digital protection plan, and what could happen to my accounts and files if I face a device emergency or sudden absence?`;
            break;

            default:
            return null;
        }

        return (
            <div 
            className={`text-button`}
            onClick={onAskRequiemClick.bind(this, key)}
            style={{
                alignItems: 'center',
                backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.05),
                border: `2px dashed ${Appearance.colors.primary()}`,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                padding: 16,
                textAlign: 'left',
                whiteSpace: 'pre-wrap'
            }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.15),
                    borderRadius: '50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    flex: 'none',
                    height: 50,
                    marginRight: 12,
                    padding: 12,
                    width: 50
                }}>
                    <img
                    src={'images/ask-requiem-green.png'}
                    style={{
                        height: '100%',
                        width: '100%'
                    }} />
                </div>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    color: Appearance.colors.primary(),
                    fontWeight: 600
                }}>{`Ask Requiem: "${message}"`}</span>
            </div>
        )
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getLeadGenFields = () => {
        return [{
            collapsed: null,
            id: 'details',
            items: [{
                component: 'textfield',
                id: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                id: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                id: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                id: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                required: false,
                title: 'Phone Number (Optional)',
                value: edits.phone_number
            },{
                component: 'textview',
                id: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'What Do You Need Help With?',
                value: edits.description
            }]
        }];
    }

    const getStartFreeButton = style => {
        let label = system.window.isMobile ? 'Start Free — No Credit Card' : 'Start Free — No Credit Card Required';
        if(request.focus === 'third_party_referral') {
            label = 'View Your Exclusive Offer';
        }
        return (
            <Button
            color={'primary'}
            containerStyle={{
                maxWidth: 540,
                width: '100%',
                ...style
            }}
            label={label}
            onClick={onStartNowClick.bind(this, 'pricing_cta')}
            type={'large'} />
        )
    }

    const getCardTitleStyles = () => ({
        ...Appearance.textStyles.panelSubHeaderText(),
        display: 'block',
        fontSize: 16
    });

    const renderHero = () => {

        let items = [{
            image: 'images/lead-landing-emergencies-green.png',
            title: `Don't Wait for a Crisis`,
            body: `Most people don't create a digital plan until they're forced to.`
        },{
            image: 'images/lead-landing-easy-secondary-green.png',
            title: 'Simple to Start',
            body: `You don't need to finish everything today — just start.`
        },{
            image: 'images/lead-landing-users-black.png',
            title: 'Quick to Implement',
            body: 'Most people can create their first protection plan in under 5 minutes.'
        }];
        
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{
                    ...Appearance.styles.unstyledResponsivePanel(),
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-6 p-4 p-xl-5'}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            maxWidth: 750
                        }}>{'If something happened to you tomorrow, would anyone know what to do?'.toUpperCase()}</span>
                        <span 
                        className={'mb-4'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            fontSize: 16,
                            display: 'block'
                        }}>{`LifeWipe helps you decide what happens to your digital life—so your accounts, files, and memories are handled exactly the way you intend, before a crisis forces rushed decisions.`}</span>
                        <div className={'row mx-0 mt-0 mb-3 mb-xl-4'}>
                            {items.map((item, idx) => (
                                <div 
                                className={'col-12 p-0 pb-3'}
                                key={idx}
                                style={{
                                    textAlign: 'left'
                                }}>
                                    <div 
                                    className={'p-3 p-xl-3 d-flex flex-column flex-xl-row align-items-start align-items-xl-center'}
                                    style={{ 
                                        ...Appearance.styles.unstyledPanel(),
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <img 
                                        className={'d-none d-xl-flex mb-3 mb-xl-0 mr-2 mr-xl-3'}
                                        src={item.image}
                                        style={{
                                            height: 50,
                                            width: 50
                                        }} />
                                        <div style={{ 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                            width: '100%'
                                        }}>
                                            <span 
                                            className={'mb-1'}
                                            style={{
                                                ...getCardTitleStyles(),
                                                marginBottom: 2
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText()
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {getStartFreeButton({ maxWidth: null })}
                    </div>
                    <div 
                    className={'col-12 col-xl-6 p-0 d-none d-xl-flex'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            minHeight: 400,
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            <video
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            playsInline={true}
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }}>
                                <source 
                                src={'images/lead-landing.urgency.mp4'} 
                                type={'video/mp4'} />
                            </video>
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const renderDecisionAnchor = () => (
        <AnimatedElement 
        {...utils.animations.get('panel')} 
        animateWithPage={false}
        useContentDivider={true}
        visible={true}>
            <div 
            className={'p-4 p-xl-5 text-left text-xl-center'}
            style={{ 
                ...Appearance.styles.unstyledResponsivePanel(),
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                position: 'relative',
                width: '100%'
            }}>
                <span 
                className={'mb-3 mb-xl-4'}
                style={{
                    ...Appearance.textStyles.leadLandingTitle(),
                    maxWidth: 950,
                    whiteSpace: 'pre-wrap'
                }}>{'Starting now means your plan exists — even if you come back later'.toUpperCase()}</span>
                <span
                className={'mb-4 mb-xl-5'}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    fontSize: 16,
                    maxWidth: 1250
                }}>{`You can start in minutes — and finish at your own pace. Families across the country are already using LifeWipe to avoid uncertainty.`}</span>
                <Button
                color={'secondary'}
                containerStyle={{
                    maxWidth: 500,
                    width: '100%'
                }}
                label={`Start Free — Takes 5 Minutes`}
                onClick={onStartNowClick.bind(this, 'decision_anchor_cta')}
                type={'large'} />
            </div>
        </AnimatedElement>
    );

    const renderWhoThisIsFor = () => {

        let forItems = [{
            text: 'You\'ve thought, "If something happened to me, my family wouldn\'t know where anything is"'
        },{
            text: 'Your accounts, files, and passwords are scattered across devices'
        },{
            text: 'You want control without sharing passwords'
        }];

        let notForItems = [{
            text: 'You believe you\'ll "figure it out later"'
        },{
            text: 'You only want a basic password manager'
        },{
            text: 'You\'re not willing to spend 5 minutes setting up a plan'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 850,
                        whiteSpace: 'pre-wrap'
                    }}>{`WHO THIS IS FOR \n(AND WHO IT'S NOT FOR)`}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>
                        {`LifeWipe isn’t for everyone. It’s for people who know protecting what matters `}
                        <b>{`can’t wait`}</b>
                        {`—and are ready to start, even with a simple first step.`}
                    </span>
                    
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        <div className={'col-12 col-md-6 p-0 px-xl-2 mb-3 mb-xl-0'}>
                            <div 
                            className={'p-3 p-xl-4'}
                            style={{ 
                                backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.05),
                                border: `2px solid ${Appearance.colors.primary()}`,
                                borderRadius: 12,
                                height: '100%',
                                textAlign: 'left'
                            }}>
                                <span
                                className={'mb-3'}
                                style={{
                                    ...getCardTitleStyles(),
                                    color: Appearance.colors.primary()
                                }}>{'THIS IS FOR YOU IF:'}</span>
                                <ul style={{
                                    ...Appearance.textStyles.defaultText(),
                                    lineHeight: 1.6,
                                    marginBottom: 0,
                                    paddingLeft: 18
                                }}>
                                    {forItems.map((item, idx, items) => (
                                        <li 
                                        key={idx} 
                                        style={{ 
                                            marginBottom: idx === items.length - 1 ? 0 : 12 
                                        }}>{item.text}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className={'col-12 col-md-6 p-0 px-xl-2 mb-3 mb-xl-0'}>
                            <div 
                            className={'p-3 p-xl-4'}
                            style={{ 
                                backgroundColor: Appearance.colors.panelBackground(),
                                border: `2px solid ${Appearance.colors.divider()}`,
                                borderRadius: 12,
                                height: '100%',
                                textAlign: 'left'
                            }}>
                                <span
                                className={'mb-3'}
                                style={{
                                    ...getCardTitleStyles()
                                }}>{'THIS ISN\'T FOR YOU IF:'}</span>
                                <ul style={{
                                    ...Appearance.textStyles.defaultText(),
                                    lineHeight: 1.6,
                                    marginBottom: 0,
                                    paddingLeft: 18
                                }}>
                                    {notForItems.map((item, idx, items) => (
                                        <li 
                                        key={idx} 
                                        style={{ 
                                            marginBottom: idx === items.length - 1 ? 0 : 12 
                                        }}>{item.text}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const renderProblem = () => {

        let items = [{
            title: 'SCATTERED ACCOUNTS',
            body: 'Banking, social, crypto, subscriptions—hard to track and even harder to secure or hand off in a crisis.'
        },{
            title: 'NO CLEAR HANDOFF',
            body: `Loved ones aren't sure what to do, who to call, or how to access what matters without risking accounts or privacy.`
        },{
            title: 'SECURITY WORRIES',
            body: 'You want things executed safely without exposing private info, breaking anything, or relying on an unverified note or shared password.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <div 
                    className={'col-12 col-xl-5 p-0'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(0% 0, 92% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            <video
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            playsInline={true}
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }}>
                                <source 
                                src={'images/lead-capture-video.mp4'} 
                                type={'video/mp4'} />
                            </video>
                            <div style={{
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)',
                                height: '100%',
                                left: 0,
                                position: 'absolute',
                                top: 0,
                                width: '100%'
                            }} />
                        </div>
                    </div>
                    <div 
                    className={'col-12 col-xl-7 p-4 p-xl-5'} 
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        position: 'relative'
                    }}>
                        <AltBadge 
                        className={'mb-3 mb-xl-4'}
                        content={{ 
                            color: Appearance.colors.secondary(), 
                            text: 'THE PROBLEM' 
                        }} 
                        style={{ 
                            marginLeft: 0,
                            width: 125
                        }} />
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            display: 'block'
                        }}>{'Right now, your digital life is scattered and vulnerable'.toUpperCase()}</span>
                        <span 
                        className={'mb-4 mb-xl-5'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            fontSize: 16,
                        }}>{'Most people have no clear plan for their accounts, files, or messages if something happens. Loved ones and teammates are left guessing—or locked out entirely.'}</span>
                        
                        <div 
                        className={'row mx-0 mb-3 mb-xl-5'} 
                        style={{ 
                            justifyContent: 'center' 
                        }}>
                            {items.map((item, idx) => (
                                <div 
                                key={idx} 
                                className={`col-12 col-md-4 p-0 pb-2 pb-xl-0 pl-xl-${idx === 0 ? 0 : 2} pr-xl-${idx === 2 ? 0 : 2}`}>
                                    <div 
                                    className={'p-3 p-xl-4'}
                                    style={{
                                        backgroundColor: Appearance.colors.panelBackground(),
                                        border: `2px solid ${Appearance.colors.divider()}`,
                                        borderRadius: 12,
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <span 
                                        className={'mb-1 mb-xl-2'}
                                        style={{
                                            ...getCardTitleStyles()
                                        }}>{item.title}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText()
                                        }}>{item.body}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const renderHowItWorks = () => {

        let items = [{
            image: 'images/lead-landing-edit-green.png',
            title: 'TELL US WHAT MATTERS',
            body: `Pick the accounts, files, and people that matter most. You're guided through simple questions instead of technical forms. No jargon, no confusion—just clear prompts that help you think through what you need to protect and share.`
        },{
            image: 'images/lead-landing-user-secondary-green.png',
            title: 'ADD YOUR CONTENT',
            body: `Link key accounts, upload important documents to the Secure Data Vault, and add trusted contacts who should be involved. Everything is encrypted at rest and in transit, with multiple verification checkpoints protecting your sensitive information.`
        },{
            image: 'images/lead-landing-instructions-black.png',
            title: 'SET YOUR RULES',
            body: `Configure LifeChecks and Emergency Triggers so that if you go silent or hit the panic button, your instructions run—backups, wipes, and legacy deliveries included. Your plan becomes your safety net, active and ready without requiring constant attention.`
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 850
                    }}>{'FROM SCATTERED TO SECURE IN THREE GUIDED STEPS'}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`Setting up comprehensive digital protection might sound complex, but we've broken it down into a simple, guided process. Get started in minutes—and you can always adjust your plan as life changes. You don't need to be technical. LifeWipe walks you through every step with plain language and clear guidance.`}</span>
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        )
    };

    const getCardElement = (item, index) => {
        return (
            <div 
            key={index} 
            className={'col-12 col-md-4 p-0 px-xl-2 mb-3'}>
                <div 
                className={'p-4 p-xl-5 d-flex flex-column text-left text-xl-center align-items-start align-items-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledPanel(),
                    height: '100%'
                }}>
                    {typeof(item.image) === 'string' && (
                        <img 
                        className={'mb-3'}
                        src={item.image}
                        style={{
                            height: system.window.isMobile ? 50 : 75,
                            width: system.window.isMobile ? 50 : 75
                        }} />
                    )}
                    <span
                    className={'mb-2'}
                    style={{
                        ...getCardTitleStyles()
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{item.body}</span>
                </div>
            </div>
        )
    }

    const renderTestimonials = () => {
    
        let items = [{
            avatar: 'images/testimonial-avatar-01.jpg',
            quote: `I have three kids, and if something happened to me, I didn’t want them left guessing. LifeWipe let me put my passwords, photos, and important things in one place, so they’re taken care of.`,
            name: 'Anna R.',
            role: 'School Bus Driver'
        },{
            avatar: 'images/testimonial-avatar-02.jpg',
            quote: `I’ve got bank stuff, photos, and accounts spread everywhere. LifeWipe gave me one place to put it all, so my kids and my brother wouldn’t have to deal with a mess.`,
            name: 'Tyler H.',
            role: 'Contractor / Construction'
        },{
            avatar: 'images/testimonial-avatar-03.jpg',
            quote: `My daughters live in another state, and I didn’t want them trying to piece things together from afar. LifeWipe helps me keep everything organized so they’re not left scrambling.`,
            name: 'Ashley B.',
            role: 'Retail Security'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{
                    ...Appearance.styles.unstyledResponsivePanel(),
                    width: '100%'
                }}>
                    <div 
                    className={'col-12 p-4 p-xl-5 text-left text-xl-center'}
                    style={{ 
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                    }}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            lineHeight: 1.25,
                            maxWidth: 850
                        }}>{'HEAR FROM REAL PEOPLE WHO TRUST LIFEWIPE'}</span>
                        <span 
                        className={'mb-4 mb-xl-5'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            fontSize: 16,
                            maxWidth: 1250
                        }}>{`The decision to protect your digital legacy is deeply personal. It's not just about features or technology—it's about peace of mind, family security, and knowing you've done the right thing for the people who matter most. People from all walks of life are already using LifeWipe to gain control over their digital assets and ensure their loved ones won't face confusion or chaos during difficult times. Here's what some of them have to say about their experience.`}</span>
                        <div 
                        className={'row mx-0 mb-3 mb-xl-4'} 
                        style={{ 
                            justifyContent: 'center'
                        }}>
                            {items.map((item, idx) => (
                                <div 
                                key={idx} 
                                className={'col-12 col-md-4 p-0 px-xl-2 mb-3'}>
                                    <div 
                                    className={'p-3 p-xl-4'}
                                    style={{ 
                                        backgroundColor: Appearance.colors.panelBackground(),
                                        border: `2px solid ${Appearance.colors.divider()}`,
                                        borderRadius: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            flexGrow: 1,
                                            fontStyle: 'italic',
                                            marginBottom: 16
                                        }}>{`"${item.quote}"`}</span>
                                        <div style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            flexDirection: 'row'
                                        }}>
                                            <img 
                                            src={item.avatar}
                                            style={{
                                                borderRadius: '50%',
                                                height: 48,
                                                marginRight: 12,
                                                objectFit: 'cover',
                                                width: 48
                                            }} />
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <span style={{
                                                    ...Appearance.textStyles.defaultText(),
                                                    display: 'block',
                                                    fontWeight: 600,
                                                    marginBottom: 2
                                                }}>{item.name}</span>
                                                <span style={{
                                                    ...Appearance.textStyles.supporting(),
                                                    fontSize: 13
                                                }}>{item.role}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    };

    const renderTrustSignals = () => {

        let items = [{
            image: 'images/lead-landing-security-green.png',
            title: 'SECURITY-FIRST APPROACH',
            body: 'Strong encryption, layered authentication protocols, and real-time wipe capabilities ensure your digital assets remain secure under all circumstances.'
        },{
            image: 'images/lead-landing-document-secondary-green.png',
            title: 'CLEAR, AUDITABLE PROTOCOLS',
            body: 'Every action taken on your behalf is logged, verified, and transparent. You maintain complete visibility and control over all operations.'
        },{
            image: 'images/lead-landing-fingerprint-black.png',
            title: 'BUILT BY SECURITY EXPERTS',
            body: 'Our platform was designed by former intelligence officers, cybersecurity engineers, digital forensics specialists, and privacy advocates who know how to protect what matters most.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 850
                    }}>{'TRUSTED BY PRIVACY-CONSCIOUS INDIVIDUALS AND TEAMS WORLDWIDE'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`LifeWipe isn't just another password manager or cloud backup service. We're a comprehensive digital life management platform built by experts who understand what's at stake when security and legacy planning intersect.`}</span>
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        )
    };


    const renderPricing = () => (
        <AnimatedElement 
        {...utils.animations.get('panel')} 
        animateWithPage={false}
        useContentDivider={true}
        visible={true}>
            <div 
            className={'p-4 p-xl-5 text-left text-xl-center'}
            style={{ 
                ...Appearance.styles.unstyledResponsivePanel(),
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <span 
                className={'mb-3 mb-xl-4'}
                style={{
                    ...Appearance.textStyles.leadLandingTitle(),
                    maxWidth: 850
                }}>{'START FREE. UPGRADE ONLY IF IT EARNS YOUR TRUST.'}</span>
                <span
                className={'mb-4 mb-xl-5'}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    fontSize: 16,
                    maxWidth: 1250
                }}>{`We believe in earning your business through value, not pressure. That's why you can start with LifeWipe completely free—no credit card required, no time limits, no surprise charges. Build your complete digital protection plan, explore every feature, connect your accounts, and see how it all works before deciding if a paid upgrade makes sense for you.`}</span>
                {getStartFreeButton()}
            </div>
        </AnimatedElement>
    );



    const onSubmitClick = async() => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getLeadGenFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/utils/', {
                ...edits,
                metadata: { advertising_metadata },
                origin: 'lead_landing_paid',
                type: 'new_website_contact_form'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: 'We have received your contact request and will reach out to you shortly.',
                onClick: setEdits.bind(this, {})
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your contact request. ${e.message || 'An unknown error occurred'}`,
                origin: 'contact.submit_click',
                title: 'Oops!'
            });
        }
    }

    const getLeadGenerationForm = () => (
        <div 
        className={'p-5'}
        style={{
            backgroundColor: Appearance.colors.divider(),
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            ...system.window.isMobile === false && {
                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
            }
        }}>
            <div style={{
                alignSelf: 'flex-end',
                width: '80%'
            }}>
                <AltFieldMapper
                fields={getLeadGenFields()}
                utils={utils} />
                <Button
                color={'dark'}
                containerStyle={{
                    marginBottom: 8,
                    marginTop: 16
                }}
                label={'Talk to a Human — 15-Min Consultation'}
                loading={loading === 'submit'}
                onClick={onSubmitClick}
                type={'large'} />
                <span style={{
                    ...Appearance.textStyles.supporting(),
                    display: 'block',
                    fontSize: 12,
                    lineHeight: 1.4,
                    marginTop: 16
                }}>{`We respond within one business day. No surprise commitments—just 100% human follow-up from someone who genuinely wants to help.`}</span>
            </div>
        </div>
    )

    const renderFAQ = () => {

        const faqs = [{
            q: 'Is my data safe?',
            a: 'Yes. We protect your info and require checks before anything important happens.'
        },{
            q: 'Do I need to be technical?',
            a: 'No. We guide you in plain language. You can also ask us for help anytime.'
        },{
            q: 'Can this work for family and small teams?',
            a: 'Yes. Add the people you trust and give them clear instructions.'
        },{
            q: 'How quickly do you reply?',
            a: 'We respond within one business day. If something urgent happens, your plan already spells out what to do.'
        },{
            q: 'Can someone help me set it up?',
            a: 'Yes. You can start on your own or book a short consult. No pressure, no commitment.'
        },{
            q: 'Do I need a credit card to start?',
            a: 'No. You can start with LifeWipe for free and decide later if you want to upgrade.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        display: 'block',
                        maxWidth: 850,
                        width: '100%'
                    }}>{'COMMON QUESTIONS'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        maxWidth: 1250
                    }}>{`Below are answers to some of the most common questions we hear, covering how LifeWipe works, how your information is protected, and what control you maintain throughout the process. Whether you're just exploring or actively considering next steps, these answers are here to help you understand your options clearly.`}</span>
                    <div 
                    className={'row mx-0 mb-3 mb-xl-4'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {faqs.map((faq, index) => (
                            <div 
                            key={index} 
                            className={'col-12 col-xl-6 p-0 px-xl-2 mb-3'}>
                                <div 
                                className={'p-3 p-xl-4'}
                                style={{
                                    ...Appearance.styles.unstyledPanel(),
                                    height: '100%',
                                    textAlign: 'left'
                                }}>
                                    <span 
                                    className={'mb-2'}
                                    style={{
                                        ...getCardTitleStyles()
                                    }}>{faq.q.toUpperCase()}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText()
                                    }}>{faq.a}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {getQuotedContentContainer('personalized_help_next_steps')}
                </div>
            </AnimatedElement>
        );
    };

    const renderTakeControl = () => {

        let items = [{
            image: 'images/lead-landing-free-gradient.png',
            title: 'START FREE',
            body: 'No credit card required. Begin protecting your most important accounts and files right now with our comprehensive Starter plan.'
        },{
            image: 'images/lead-landing-user-secondary-green.png',
            title: 'TALK TO OUR TEAM',
            body: 'Book a 15-minute consultation to map out your specific needs and get personalized guidance from our experts.'
        },{
            image: 'images/lead-landing-ready-black.png',
            title: 'UPGRADE WHEN READY',
            body: 'Explore LifeWipe at your own pace. Upgrade to Pro only when you\'re ready for advanced features and priority support.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        display: 'block'
                    }}>{'TAKE CONTROL OF YOUR DIGITAL LIFE TODAY'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`Your digital life holds your memories, your finances, your connections, and your legacy. Taking a few steps now gives you—and the people who rely on you—clarity, security, and control. Without a plan, accounts, files, and digital assets are left exposed and uncertain. There's no need to leave loved ones or teammates guessing when protection and clarity are within reach.`}</span>
                    
                    <div 
                    className={'row mx-0 mb-4 mb-xl-5'} 
                    style={{ 
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                    <div 
                    className={'row m-0'}
                    style={{
                        maxWidth: 850,
                        width: '100%'
                    }}>
                        <div className={'col-12 col-xl-6 p-0 pb-2 pb-xl-0 pr-xl-2'}>
                            {getStartFreeButton()}
                        </div>
                        <div className={'col-12 col-xl-6 p-0 pl-xl-2'}>
                            <Button
                            color={'dark'}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={'Book Your Free Consultation'}
                            onClick={onBookConsultationClick}
                            type={'large'} />
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            <div style={{
                maxWidth: 1500,
                width: '100%'
            }}>
                {renderHero()}
                {renderDecisionAnchor()}
                {renderWhoThisIsFor()}
                {renderProblem()}
                {renderHowItWorks()}
                {renderTrustSignals()}
                {renderTestimonials()}
                {renderPricing()}
                {renderTakeControl()}
            </div>
        </div>
    );
};

export const SalesOpsLanding = ({ utils }) => {

    const { request, user, system } = useDataStore();

    const onExploreCoverageClick = () => {
        utils.layer.open({
            Component: NewSubscription.bind(this, { 
                freeTrial: false
            }),
            id: 'new_subscription'
        });
    }

    const onPlanSelectionClick = (tier, schedule) => {
        utils.layer.open({
            Component: NewSubscription.bind(this, { 
                defaultSchedule: schedule,
                freeTrial: false,
                selection: tier
            }),
            id: 'new_subscription'
        });
    }

    const onSalesOrderFormClick = () => {
        utils.layer.open({
            Component: SalesOpsOrderForm.bind(this, {}),
            id: 'sales_ops_order_form'
        });
    }

    const getCardTitleStyles = () => ({
        ...Appearance.textStyles.panelSubHeaderText(),
        display: 'block',
        fontSize: 16
    });

    const getCardElement = (item, index) => {
        return (
            <div 
            key={index} 
            className={'col-12 col-md-4 p-0 px-xl-2 mb-3'}>
                <div 
                className={'p-4 p-xl-5 d-flex flex-column text-left text-xl-center align-items-start align-items-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledPanel(),
                    height: '100%'
                }}>
                    {typeof(item.image) === 'string' && (
                        <img 
                        className={'mb-3'}
                        src={item.image}
                        style={{
                            height: system.window.isMobile ? 50 : 75,
                            width: system.window.isMobile ? 50 : 75
                        }} />
                    )}
                    <span
                    className={'mb-2'}
                    style={{
                        ...getCardTitleStyles()
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{item.body}</span>
                </div>
            </div>
        )
    }

    const getExploreCoverageButton = () => {
        return (
            <Button
            color={'primary'}
            containerStyle={{
                marginTop: 24,
                maxWidth: 540,
                width: '100%'
            }}
            label={'Explore Coverage'}
            onClick={onExploreCoverageClick}
            type={'large'} />
        )
    }

    // Section 1: Opening Frame
    const renderOpeningFrame = () => {

        let items = [{
            image: 'images/sales-ops-landing-easy-green.png',
            title: 'No Pressure, Just Clarity',
            body: `This conversation is about understanding the customer's needs—not pushing products or forcing decisions.`
        },{
            image: 'images/sales-ops-landing-user-secondary-green.png',
            title: 'Personalized Approach',
            body: `Show the customer how other privacy-focused people in similar situations protect what matters most to them.`
        },{
            image: 'images/lead-landing-instructions-black.png',
            title: 'Guided Decision-Making',
            body: `Help the customer evaluate options and decide if this level of protection makes sense for their situation.`
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{
                    ...Appearance.styles.unstyledResponsivePanel(),
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-6 p-4 p-xl-5'}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            maxWidth: 750
                        }}>{'THIS ISN\'T A SALES PITCH — IT\'S A CONVERSATION ABOUT THE CUSTOMER\'S CONTROL'}</span>
                        <span 
                        className={'mb-4 mb-xl-5'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            fontSize: 16,
                            display: 'block'
                        }}>{`Your customer is here because someone they trust suggested they talk to us, or because they've been thinking about digital security for a while and finally decided to look into it. Either way, they're in the right place. Today is about understanding what they actually need and showing them how people in similar situations are protecting what matters most.`}</span>
                        <div className={'row mx-0 mt-0 mb-3 mb-xl-4'}>
                            {items.map((item, idx) => (
                                <div 
                                className={'col-12 p-0 pb-3'}
                                key={idx}
                                style={{
                                    textAlign: 'left'
                                }}>
                                    <div 
                                    className={'p-3 p-xl-3 d-flex flex-column flex-xl-row align-items-start align-items-xl-center'}
                                    style={{ 
                                        ...Appearance.styles.unstyledPanel(),
                                        height: '100%',
                                        textAlign: 'left'
                                    }}>
                                        <img 
                                        className={'d-none d-xl-flex mb-3 mb-xl-0 mr-2 mr-xl-3'}
                                        src={item.image}
                                        style={{
                                            height: 50,
                                            width: 50
                                        }} />
                                        <div style={{ 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                            width: '100%'
                                        }}>
                                            <span 
                                            className={'mb-1'}
                                            style={{
                                                ...getCardTitleStyles(),
                                                marginBottom: 2
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText()
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button
                        color={'primary'}
                        label={'New Sales Order Form'}
                        onClick={onSalesOrderFormClick}
                        type={'large'} />
                    </div>
                    <div 
                    className={'col-12 col-xl-6 p-0 d-none d-xl-flex'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            minHeight: 400,
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            <video
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            playsInline={true}
                            poster={'images/sales-ops-landing-poster.png'}
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }}>
                                <source 
                                src={'images/sales-ops-landing.mp4'} 
                                type={'video/mp4'} />
                            </video>
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    // Section 2: Guided Discovery Mirror
    const renderGuidedDiscovery = () => {

        let items = [{
            image: 'images/lead-landing-security-green.png',
            title: 'SCATTERED ACCESS POINTS',
            body: `Customers typically have accounts across banks, social media, cloud storage, subscriptions, and crypto platforms. Most don't realize how many they have until you walk through it together.`
        },{
            image: 'images/lead-landing-document-secondary-green.png',
            title: 'UNCLEAR HANDOFF PLANS',
            body: `If something happened tomorrow, would the customer's spouse, business partner, or executor know what to do? Most people say "probably not" when asked this question.`
        },{
            image: 'images/lead-landing-fingerprint-black.png',
            title: 'SECURITY VS. ACCESS TENSION',
            body: `Customers want things secure, but they also need someone to be able to act if they can't. Balancing those two needs is harder than it sounds—and that's exactly what LifeWipe helps with.`
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 850
                    }}>{'THESE ARE THE BLIND SPOTS CUSTOMERS FACE MOST OFTEN'}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`When you sit down with a customer for the first time, don't lecture them about what they should have done. Instead, ask a few simple questions. Those questions usually reveal patterns—things that feel normal day-to-day but become serious problems during a crisis or transition. These blind spots come up in almost every conversation and help identify what protection the customer actually needs.`}</span>
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    // Section 3: Emotional Translation
    const renderEmotionalTranslation = () => {
        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <div 
                    className={'col-12 col-xl-5 p-0'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(0% 0, 92% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            <video
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            playsInline={true}
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }}>
                                <source 
                                src={'images/lead-capture-video.mp4'} 
                                type={'video/mp4'} />
                            </video>
                            <div style={{
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)',
                                height: '100%',
                                left: 0,
                                position: 'absolute',
                                top: 0,
                                width: '100%'
                            }} />
                        </div>
                    </div>
                    <div 
                    className={'col-12 col-xl-7 p-4 p-xl-5'} 
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        position: 'relative'
                    }}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            display: 'block'
                        }}>{'CUSTOMERS DON\'T WANT TO THINK ABOUT THIS — BUT THAT DOESN\'T MAKE IT GO AWAY'}</span>
                        <span 
                        className={'mb-4'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            fontSize: 16,
                            fontStyle: 'italic'
                        }}>{`This is usually where customers stop avoiding the topic.`}</span>
                        <span 
                        className={'mb-4'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            fontSize: 16,
                        }}>{`People avoid digital planning for the same reason they avoid estate planning: it feels uncomfortable. Nobody likes imagining scenarios where they're incapacitated, absent, or gone. But the families and businesses who end up scrambling wish someone had taken an afternoon to get things organized. The goal of this conversation is to help the customer see what's possible, understand how other people are handling this, and decide whether taking a few steps now makes sense for their situation.`}</span>
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    // Section 4: Control Without Exposure
    const renderControlWithoutExposure = () => {

        let items = [{
            image: 'images/lead-landing-edit-green.png',
            title: 'ENCRYPTED STORAGE',
            body: `Customer data is protected with strong encryption, the same approaches used by high profile organizations. Even we can't see their passwords or private files without their explicit authorization.`
        },{
            image: 'images/lead-landing-user-secondary-green.png',
            title: 'CONDITIONAL ACCESS',
            body: `The customer decides who gets access to what, and under what conditions. No one sees anything unless the specific triggers they've defined are met—and even then, only the information they've authorized.`
        },{
            image: 'images/lead-landing-instructions-black.png',
            title: 'VERIFIED EXECUTION',
            body: `When the customer's plan activates, multiple verification steps ensure that instructions are carried out exactly as they specified. Nothing happens without proper authorization and documented confirmation.`
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 1250
                    }}>{'CUSTOMER INFORMATION STAYS ENCRYPTED UNTIL THE MOMENT IT\'S ACTUALLY NEEDED'}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`The biggest concern customers have is, "How do I make sure someone can act on my behalf without giving them access to everything right now?" That's a smart question. The answer is conditional encryption and verified triggers. Customer data stays locked until specific conditions they define are met. Then, and only then, does the system release the information they've designated—and only to the people they've authorized.`}</span>
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    // Section 5: Protection Levels Framing
    const renderProtectionLevels = () => {

        let layers = [{
            title: 'IMMEDIATE RESPONSE',
            body: 'Panic button triggers, emergency wipes, and immediate notification protocols that execute within minutes of activation.',
            timeframe: 'Real-time'
        },{
            title: 'SHORT-TERM PROTECTION',
            body: 'Automated check-ins and deadman switches that detect absence and begin executing pre-defined backup and notification sequences.',
            timeframe: 'Days to weeks'
        },{
            title: 'MEDIUM-TERM PLANNING',
            body: 'Executor handoff procedures, business continuity protocols, and family access plans that ensure smooth transitions during extended absence.',
            timeframe: 'Weeks to months'
        },{
            title: 'LONG-TERM LEGACY',
            body: 'Full estate integration, memorial account handling, and comprehensive digital asset transfer procedures for permanent transitions.',
            timeframe: 'Months to years'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 950,
                        whiteSpace: 'pre-wrap'
                    }}>{'CUSTOMER PROTECTION ISN\'T ONE-SIZE-FITS-ALL — IT\'S LAYERED ACROSS TIME'}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`When you design the customer's protection plan together, you're not just thinking about one scenario. You're thinking about four distinct time horizons, each with its own needs and triggers. Some situations require immediate action. Others unfold over weeks or months. The customer's plan needs to account for all of them.`}</span>
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        {layers.map((layer, idx) => (
                            <div 
                            key={idx} 
                            className={'col-12 col-md-6 p-0 px-xl-2 mb-3'}>
                                <div 
                                className={'p-3 p-xl-4'}
                                style={{ 
                                    ...Appearance.styles.unstyledPanel(),
                                    height: '100%',
                                    textAlign: 'left'
                                }}>
                                    <AltBadge 
                                    className={'mb-4'}
                                    content={{ 
                                        color: Appearance.colors.secondary(), 
                                        text: layer.timeframe.toUpperCase()
                                    }} 
                                    style={{ 
                                        display: 'inline-flex',
                                        marginLeft: 0,
                                        width: 'auto'
                                    }} />
                                    <span
                                    className={'mb-2'}
                                    style={{
                                        ...getCardTitleStyles()
                                    }}>{`${idx + 1}. ${layer.title}`}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText()
                                    }}>{layer.body}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    // Section 6: Multi-Year Commitment Selector
    const renderMultiYearCommitment = () => {
        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 1250
                    }}>{'HOW LONG DOES THE CUSTOMER WANT THIS PROTECTION IN PLACE?'}</span>
                    <span
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`Digital protection isn't something customers set up for a few months. It's infrastructure—something that should be in place for as long as they have accounts, files, and people who depend on them. That's why we offer multi-year plans with significant savings. Customers lock in their rate, secure their protection, and never worry about renewal reminders or price increases.`}</span>
                    <div 
                    className={'row m-0 mb-4'} 
                    style={{ 
                        justifyContent: 'center',
                        maxWidth: 1200
                    }}>
                        <SubscriptionOptions
                        embedded={true}
                        minimal={true}
                        onSelectionClick={onPlanSelectionClick}
                        utils={utils} /> 
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    // Section 7: What Happens After Today
    const renderWhatHappensNext = () => (
        <AnimatedElement 
        {...utils.animations.get('panel')} 
        animateWithPage={false}
        useContentDivider={true}
        visible={true}>
            <div 
            className={'p-4 p-xl-5 text-left text-xl-center'}
            style={{ 
                ...Appearance.styles.unstyledResponsivePanel(),
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                position: 'relative',
                width: '100%'
            }}>
                <span 
                className={'mb-3 mb-xl-4'}
                style={{
                    ...Appearance.textStyles.leadLandingTitle(),
                    maxWidth: 950,
                    whiteSpace: 'pre-wrap'
                }}>{'WE\'LL GUIDE THE CUSTOMER THROUGH SETUP — AND THEY CAN ADJUST THEIR PLAN ANYTIME'}</span>
                <span
                className={'mb-4'}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    fontSize: 16,
                    maxWidth: 1250
                }}>{`Once the customer decides to move forward, they'll get access to their account and we'll schedule an onboarding call. We'll walk through their situation, help them map out their most important accounts and contacts, and configure their first protection rules. Most customers have a working plan within a week.`}</span>
                <span
                className={'mb-4'}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    fontSize: 16,
                    maxWidth: 1250
                }}>{`After that, the customer can refine things at their own pace. They can add new accounts, update contacts, adjust triggers, and test their emergency protocols whenever they want. Their plan evolves with them. Nothing is locked once they set it—everything stays flexible.`}</span>
            </div>
        </AnimatedElement>
    );

    // Section 8: Operative-Led CTA
    const renderOperativeCTA = () => {

        let items = [{
            image: 'images/lead-landing-edit-green.png',
            title: 'REVIEW PLANS TOGETHER',
            body: `Take a look at the plan options and talk through which features matter most for the customer's situation.`
        },{
            image: 'images/lead-landing-user-secondary-green.png',
            title: 'CHOOSE THEIR TERM',
            body: `Help the customer pick the commitment length that makes sense—most people go with 5 or 7 years for the savings and long-term security.`
        },{
            image: 'images/lead-landing-ready-black.png',
            title: 'ACTIVATE THEIR ACCOUNT',
            body: `Set up the customer's account, schedule their onboarding, and get them started with their first protection rules within the week.`
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        display: 'block'
                    }}>{'WALK THROUGH THE NEXT STEPS WITH THE CUSTOMER'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        fontSize: 16,
                        maxWidth: 1250
                    }}>{`If this makes sense to the customer and they're ready to get started, you can take care of everything during this call. There's no need for them to think it over for weeks or schedule another meeting. Pick a plan, set up their account, and schedule their onboarding session—all in the next few minutes.`}</span>
                    
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            <div style={{
                maxWidth: 1500,
                width: '100%'
            }}>
                {renderOpeningFrame()}
                {renderGuidedDiscovery()}
                {renderOperativeCTA()}
                {renderMultiYearCommitment()}
            </div>
        </div>
    );
};

const BookConsultation = ({ index, options, utils}) => {

    const layerID = 'book_consultation';
    const { advertising_metadata } = useDataStore();
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
 
    const onCloseClick = () => {
        utils.alert.show({
            title: 'You’re Almost There',
            message: 'In just 15 minutes, we’ll help you begin organizing and protecting your digital life—securely and on your terms. Most people leave feeling clearer, not overwhelmed.',
            buttons: [{
                key: 'continue',
                title: 'Yes, I’ll Continue',
                style: 'default'
            },{
                key: 'decline',
                title: 'No, Cancel',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'decline') {
                    setLayerState('close');
                    return;
                }
            }
        });
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const onSubmitClick = async() => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/utils/', {
                ...edits,
                metadata: { advertising_metadata },
                origin: 'lead_landing',
                type: 'new_website_contact_form'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Request Submitted',
                message: 'We have received your consultation request and will reach out to you shortly.',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                error: e,
                message: `There was an issue submitting your contact request. ${e.message || 'An unknown error occurred'}`,
                origin: 'contact.submit_click',
                title: 'Oops!'
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: null,
            description: 'These details allow us to schedule your consultation and tailor the conversation to your needs. Your information is handled securely and never shared.',
            id: 'details',
            title: 'Contact Information',
            items: [{
                component: 'textfield',
                id: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                id: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                id: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                id: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                required: false,
                title: 'Phone Number (Optional)',
                value: edits.phone_number
            },{
                component: 'textview',
                id: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'What Do You Need Help With?',
                value: edits.description
            }]
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium'
        }}
        utils={utils}>
            <LayerItem
            collapsed={false}
            title={`Let’s Start Together`}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block'
                    }}>{`In this short consultation, we’ll help you explore how to organize, protect, and plan for your digital life. Most people use this time to ask questions, understand what’s possible, and decide what feels right for them. It’s a straightforward, 15-minute conversation designed to make things feel clearer, not more complicated.`}</span>
                </div>
            </LayerItem>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const PlanOverview = ({ utils }) => {

    const { user, system } = useDataStore();

    const getCardTitleStyles = () => ({
        ...Appearance.textStyles.panelSubHeaderText(),
        display: 'block',
        fontSize: 16
    });

    const getCardElement = (item, index) => {
        return (
            <div 
            key={index} 
            className={'col-12 col-md-4 p-0 px-xl-2 mb-3'}>
                <div 
                className={'p-4 p-xl-5 d-flex flex-column text-left text-xl-center align-items-start align-items-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledPanel(),
                    height: '100%'
                }}>
                    {typeof(item.image) === 'string' && (
                        <img 
                        className={'mb-3'}
                        src={item.image}
                        style={{
                            height: system.window.isMobile ? 50 : 75,
                            width: system.window.isMobile ? 50 : 75
                        }} />
                    )}
                    <span
                    className={'mb-2'}
                    style={{
                        ...getCardTitleStyles()
                    }}>{item.title.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{item.body}</span>
                </div>
            </div>
        )
    }

    const onActivateClick = () => {

        // log event with firebase
        utils.analytics.log('plan_overview_cta_click');

        // show user the activation options if they have not activated their plan
        let activated = user?.subscription?.is_activated || false;
        if(activated === false) {
            utils.layer.open({
                Component: NewSubscription.bind(this, { 
                    freeTrial: false,
                    isActivationMode: true
                }),
                id: 'new_subscription'
            });
            return;
        }

        // show an alert for users who have already activated their plan
        utils.alert.show({
            message: 'Your plan is already active. No further action is needed at this time.',
            title: 'Plan Already Active'
        });
    }

    const renderHero = () => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'row m-0'}
                style={{
                    ...Appearance.styles.unstyledResponsivePanel(),
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-6 p-4 p-xl-5'}>
                        <span 
                        className={'mb-3 mb-xl-4'}
                        style={{
                            ...Appearance.textStyles.leadLandingTitle(),
                            maxWidth: 750
                        }}>{'What protection looks like in LifeWipe'.toUpperCase()}</span>
                        <span
                        className={'mb-4'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            fontSize: 16,
                            display: 'block'
                        }}>{`Your plan exists in one of two states: draft or active. Understanding the difference helps you decide when you're ready to turn protection on.`}</span>
                        <div className={'row mx-0 mt-0 mb-3 mb-xl-4'}>
                            {renderTwoStates()}
                        </div>
                    </div>
                    <div 
                    className={'col-12 col-xl-6 p-0 d-none d-xl-flex'} 
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            height: '100%',
                            minHeight: 400,
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...system.window.isMobile === false && {
                                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            <video
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            playsInline={true}
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                width: '100%'
                            }}>
                                <source 
                                src={'images/draft-outreach-plan-overview.mp4'} 
                                type={'video/mp4'} />
                            </video>
                        </div>
                    </div>
                </div>
            </AnimatedElement>
        );
    }

    const renderTwoStates = () => {

        let draftItems = [
            'Add Locket Passwords, Data Vault items, trusted contacts, and configure instructions',
            'Emergency triggers do not run automatically',
            'LifeChecks are not monitored',
            'Your plan remains in preparation mode — you control when to turn it on'
        ];

        let activeItems = [
            'All draft capabilities remain available',
            'Emergency triggers are monitored and will execute based on your configured rules',
            'LifeChecks run on schedule and trigger instructions if you go silent',
            'Your protection system is live and ready to respond'
        ];

        return (
            <div 
            className={'row m-0'} 
            style={{ 
                justifyContent: 'center',
                width: '100%'
            }}>
                <div className={'col-12 p-0 mb-3'}>
                    <div 
                    className={'p-3 p-xl-4'}
                    style={{ 
                        backgroundColor: Appearance.colors.panelBackground(),
                        border: `2px solid ${Appearance.colors.divider()}`,
                        borderRadius: 12,
                        height: '100%',
                        textAlign: 'left'
                    }}>
                        <span
                        className={'mb-3'}
                        style={{
                            ...getCardTitleStyles()
                        }}>{'DRAFT PLAN'}</span>
                        <ul style={{
                            ...Appearance.textStyles.defaultText(),
                            lineHeight: 1.6,
                            marginBottom: 0,
                            paddingLeft: 18
                        }}>
                            {draftItems.map((item, idx, items) => (
                                <li 
                                key={idx} 
                                style={{ 
                                    marginBottom: idx === items.length - 1 ? 0 : 12 
                                }}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className={'col-12 p-0'}>
                    <div 
                    className={'p-3 p-xl-4'}
                    style={{ 
                        backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.05),
                        border: `2px solid ${Appearance.colors.primary()}`,
                        borderRadius: 12,
                        height: '100%',
                        textAlign: 'left'
                    }}>
                        <span
                        className={'mb-3'}
                        style={{
                            ...getCardTitleStyles(),
                            color: Appearance.colors.primary()
                        }}>{'ACTIVE PLAN'}</span>
                        <ul style={{
                            ...Appearance.textStyles.defaultText(),
                            lineHeight: 1.6,
                            marginBottom: 0,
                            paddingLeft: 18
                        }}>
                            {activeItems.map((item, idx, items) => (
                                <li 
                                key={idx} 
                                style={{ 
                                    marginBottom: idx === items.length - 1 ? 0 : 12 
                                }}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    const renderActivationClarification = () => {

        let items = [{
            image: 'images/draft-outreach-user-green.png',
            title: 'Waiting For Your Signal',
            body: `Your plan remains in a draft state until activated. In draft mode, your plan exists as preparation — information is stored and instructions are defined, but nothing runs automatically.`
        },{
            image: 'images/draft-outreach-shield-secondary-green.png',
            title: 'Monitoring Begins',
            body: `Activation tells LifeWipe to start monitoring your emergency triggers and LifeChecks. Before activation, you can configure everything but the system won't act on its own.`
        },{
            image: 'images/draft-outreach-triggers-black.png',
            title: 'Triggers Become Live',
            body: 'Once activated, your emergency triggers become live. If you set up a LifeCheck schedule, the system will track your responses and execute instructions if you go silent.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 950
                    }}>{'WHAT ACTIVATION ACTUALLY CHANGES'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        fontSize: 16,
                        maxWidth: 1250
                    }}>
                        {`Activation is the point where preparation becomes operational. Up to this point, you’ve been defining what should happen and who should be involved. Activation doesn’t change any of that — it determines whether LifeWipe is allowed to monitor for the conditions you’ve defined and carry out the instructions you’ve already set. In other words, activation doesn’t add features or require new decisions. It simply puts the plan you’ve built into motion.`}
                    </span>
                    <div 
                    className={'row m-0'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        );
    }

    const renderObjectionRemoval = () => {

        let items = [{
            image: 'images/draft-outreach-finalize-green.png',
            title: 'Doesn’t Finalize Your Plan',
            body: 'Activating your plan does not mean everything is finished. You can continue adding accounts, updating instructions, and changing trusted contacts at any time.'
        },{
            image: 'images/draft-outreach-lock-secondary-green.png',
            title: `Doesn't Lock You In`,
            body: 'Activation does not prevent you from making changes later. You can pause, disable, or adjust LifeChecks and emergency triggers whenever you need to.'
        },{
            image: 'images/draft-outreach-complex-black.png',
            title: 'Doesn’t Add Complexity',
            body: 'Activation doesn’t add new steps or require additional setup. It enables the rules and instructions you’ve already defined. Your plan simply moves from prepared to active.'
        }];

        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={true}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 950
                    }}>{'WHAT ACTIVATION DOES NOT DO'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        fontSize: 16,
                        maxWidth: 1250
                    }}>
                        {`Activation is a meaningful step, and it’s reasonable to pause before taking it. Most hesitation at this stage doesn’t come from uncertainty about the product — it comes from uncertainty about what activation actually changes. Activation does not finalize your plan, remove control, or add new complexity. It simply determines whether the plan you’ve been building is allowed to run.`}
                    </span>
                    <div 
                    className={'row m-0 mb-4'} 
                    style={{ 
                        justifyContent: 'center'
                    }}>
                        {items.map(getCardElement)}
                    </div>
                </div>
            </AnimatedElement>
        );
    }

    const renderCTA = () => {
        return (
            <AnimatedElement 
            {...utils.animations.get('panel')} 
            animateWithPage={false}
            useContentDivider={false}
            visible={true}>
                <div 
                className={'p-4 p-xl-5 text-left text-xl-center'}
                style={{ 
                    ...Appearance.styles.unstyledResponsivePanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <span 
                    className={'mb-3 mb-xl-4'}
                    style={{
                        ...Appearance.textStyles.leadLandingTitle(),
                        maxWidth: 950
                    }}>{'PUTTING YOUR PLAN INTO ACTION'}</span>
                    <span 
                    className={'mb-4 mb-xl-5'}
                    style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        fontSize: 16,
                        maxWidth: 1250
                    }}>
                        {`You’ve seen the difference between preparing a plan and having protection that actually runs. If you want the plan you’ve built to monitor for conditions and carry out your instructions automatically, the next step is activation. Activation doesn’t change what you’ve set up — it allows it to operate.`}
                    </span>
                    <Button
                    color={'primary'}
                    containerStyle={{
                        maxWidth: 540,
                        width: '100%'
                    }}
                    label={'Activate Your Plan'}
                    onClick={onActivateClick}
                    type={'large'} />
                    {!user?.subscription?.is_activated && (
                        <span 
                        className={'mt-3'}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.grey(),
                            fontSize: 14
                        }}>{`You can continue preparing and refining your plan without activating.`}</span>
                    )}
                </div>
            </AnimatedElement>
        );
    }

    return (
         <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            <div style={{
                maxWidth: 1500,
                width: '100%'
            }}>
                {renderHero()}
                {renderActivationClarification()}
                {renderObjectionRemoval()}
                {renderCTA()}
            </div>
        </div>
    );
}

export const VideoPlayer = ({ url }, { index, options, utils }) => {

    const layerID = 'video_player';
    const [layerState, setLayerState] = useState(null);
    
    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'large'
        }}
        utils={utils}>
            <div style={{
                borderRadius: 8,
                height: 364,
                opacity: 0.99,
                overflow: 'hidden',
                width: '100%'
            }}>
                <iframe width="100%" height="364" src={url} title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </div>
        </Layer>
    )
}